/**
 * Minimal translation layer (docs: context.md §130).
 *
 * UI copy never appears hardcoded in components. pt-BR is the initial locale; en-US and es slot in
 * by adding dictionaries. A full i18n library is introduced when pluralisation and formatting
 * demand it — the call sites stay the same.
 */

export const LOCALES = ['pt-BR', 'en-US', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

type Dictionary = Record<string, string>;

const ptBR: Dictionary = {
  'app.name': 'Safe Cortex CRM',
  'app.tagline': 'Plataforma comercial multi-tenant',
  'chat.members': 'Membros',
  'chat.addMember': 'Adicionar pessoa',
  'chat.pickPerson': 'Escolha alguém',
  'chat.add': 'Adicionar',
  'chat.admin': 'admin',
  'chat.promote': 'Tornar admin',
  'chat.demote': 'Tirar admin',
  'chat.remove': 'Remover',
  'chat.adminsOnly': 'Só administradores da sala adicionam, removem e promovem pessoas.',
  'error.chat.lastAdmin': 'A sala precisa de pelo menos um administrador.',
  'error.chat.removeSelf': 'Para sair da sala, use "Sair".',
  'error.ai.noProvider': 'Nenhum provedor de IA está configurado nesta instalação.',
  'error.inbox.nothingToSummarise': 'Ainda não há nada nesta conversa para resumir.',
  'error.inbox.summaryEmpty': 'O modelo não devolveu um resumo para esta conversa.',
  'error.branding.badColor': 'A cor precisa ser um hexadecimal, como #1a73e8.',
  'error.branding.cssRemote':
    'Esse CSS busca um arquivo em outro site. Use url(data:...) ou um arquivo do próprio sistema.',
  'error.branding.cssScript': 'Esse CSS tenta executar script. Remova esse trecho.',
  'error.branding.cssTooLarge': 'Esse CSS é grande demais para carregar em toda página.',
  'status.readAll': 'Marcar tudo como lido',
  'status.nothingNew': 'Nenhuma novidade por enquanto.',
  'status.latency': 'Resposta em {ms} ms',
  'status.resolved': 'resolvido',
  'status.kind.RELEASE': 'Versão',
  'status.kind.NOTICE': 'Aviso',
  'status.kind.INCIDENT': 'Incidente',
  'status.kind.MAINTENANCE': 'Manutenção',

  // The way back (Phase 129): where the dependency check used to be.
  'report.title': 'Reportar um problema ou sugerir melhoria',
  'report.kind.PROBLEM': 'Algo está errado',
  'report.kind.IDEA': 'Tenho uma ideia',
  'report.problemHint': 'O que aconteceu? Se puder, conte o que você esperava que acontecesse.',
  'report.ideaHint': 'O que ficaria melhor? Conte com suas palavras.',
  'report.sendsPage': 'Enviamos junto a tela em que você está ({page}).',
  'report.send': 'Enviar',
  'report.thanks': 'Recebemos, obrigado.',
  'report.thanksBody':
    'Uma pessoa da Safe Cortex vai ler. Se precisarmos de detalhes, falamos com você.',
  'report.another': 'Enviar outro',
  'status.version': 'Versão {version}',
  'admin.announce.new': 'Novo aviso para todos os tenants',
  'admin.announce.kind': 'Tipo',
  'admin.announce.version': 'Versão (opcional)',
  'admin.announce.title': 'Título',
  'admin.announce.body': 'Texto',
  'admin.announce.saveDraft': 'Salvar rascunho',
  'admin.announce.publish': 'Publicar',
  'admin.announce.resolve': 'Marcar como resolvido',
  'admin.announce.list': 'Avisos',
  'admin.announce.none': 'Nenhum aviso ainda.',
  'admin.announce.published': 'publicado em',
  'admin.announce.draft': 'rascunho',
  'confirm.remove': 'Remover',
  'crm.deal.OPEN': 'Em aberto',
  'crm.deal.WON': 'Ganho',
  'crm.deal.LOST': 'Perdido',
  'status.OPERATIONAL': 'Todos os sistemas operacionais',
  'status.DEGRADED': 'Desempenho degradado',
  'status.INCIDENT': 'Incidente em andamento',
  'status.MAINTENANCE': 'Manutenção em andamento',
  'status.bellTitle': 'Status e novidades',
  'admin.tab.announcements': 'Avisos',
  'activity.WHATSAPP': 'WhatsApp',
  'activity.SMS': 'SMS',
  'activity.OWNER_CHANGED': 'Responsável alterado',
  'activity.SYSTEM': 'Sistema',
  'activity.VISIT': 'Visita',
  'audit.actor.ANONYMOUS': 'Anônimo',
  'chat.type.GROUP': 'Grupo',
  'email.reason.INVALID': 'Endereço inválido',
  'inbox.handling.HUMAN': 'Com uma pessoa',
  'work.status.COMPLETED': 'Concluído',
  'work.status.ARCHIVED': 'Arquivado',
  'billing.status.EXPIRED': 'Expirada',
  'catalogues.fieldType.USER': 'Usuário',
  'catalogues.fieldType.TEAM': 'Equipe',
  'catalogues.fieldType.RELATION': 'Relação',
  'record.field.closed_at': 'Fechado em',
  'record.field.legal_name': 'Razão social',
  'record.field.lifecycle_stage': 'Estágio no ciclo',
  'record.field.loss_reason_id': 'Motivo da perda',
  'record.field.id': 'ID',
  'record.field.owner_id': 'Responsável',
  'record.field.qualified_at': 'Qualificado em',
  'record.field.source_id': 'Origem',
  'record.field.stage_changed_at': 'Etapa alterada em',
  'record.field.status_id': 'Status',
  'record.field.team_id': 'Equipe',
  'record.field.utm_campaign': 'Campanha (UTM)',
  'record.field.utm_source': 'Fonte (UTM)',
  'record.field.temperature': 'Temperatura',
  'record.field.priority': 'Prioridade',
  'record.field.next_step': 'Próximo passo',
  'limit.ai_agents': 'Agentes de IA',
  'limit.ai_tokens_monthly': 'Tokens de IA por mês',
  'limit.whatsapp_numbers': 'Números de WhatsApp',
  'limit.sms_monthly': 'SMS por mês',
  'audit.unknownAction': 'Ação registrada',
  'audit.action.access.invitation_accepted': 'Convite aceito',
  'audit.action.access.member_invited': 'Pessoa convidada',
  'audit.action.access.member_removed': 'Acesso removido',
  'audit.action.inbox.conversation_started': 'Conversa iniciada pela equipe',
  'audit.action.whatsapp_web.contacts_imported': 'Contatos importados do WhatsApp',
  'audit.action.privacy.subject_exported': 'Dados do titular exportados',
  'audit.action.privacy.subject_erased': 'Dados do titular apagados',
  'audit.action.scim.token_created': 'Token de provisionamento criado',
  'audit.action.scim.token_revoked': 'Token de provisionamento revogado',
  'audit.action.scim.user_provisioned': 'Pessoa provisionada pelo provedor',
  'audit.action.scim.user_activated': 'Acesso reativado pelo provedor',
  'audit.action.scim.user_deactivated': 'Acesso desativado pelo provedor',
  'audit.action.sso.connection_created': 'Login único configurado',
  'audit.action.sso.connection_updated': 'Login único atualizado',
  'audit.action.sso.connection_deleted': 'Login único removido',
  'audit.action.sso.identity_linked': 'Conta vinculada ao provedor',
  'audit.action.sso.user_provisioned': 'Pessoa criada pelo login único',
  'audit.action.account.managed': 'Conta passou a ser acompanhada',
  'audit.action.account.unmanaged': 'Conta deixou de ser acompanhada',
  'audit.action.account.updated': 'Conta de sucesso atualizada',
  'audit.action.activity.recorded': 'Interação registrada',
  'audit.action.ads.account_connected': 'Conta de anúncios conectada',
  'audit.action.ads.account_disconnected': 'Conta de anúncios desconectada',
  'audit.action.ads.campaign_utm_set': 'UTM da campanha definida',
  'audit.action.ai.agent_archived': 'Agente arquivado',
  'audit.action.ai.agent_created': 'Agente criado',
  'audit.action.ai.agent_paused': 'Agente pausado',
  'audit.action.ai.agent_run': 'Agente executado',
  'audit.action.ai.version_published': 'Versão do agente publicada',
  'audit.action.ai_batch.canceled': 'Execução em lote cancelada',
  'audit.action.ai_batch.started': 'Execução em lote iniciada',
  'audit.action.automation.activated': 'Automação ativada',
  'audit.action.automation.created': 'Automação criada',
  'audit.action.automation.deleted': 'Automação excluída',
  'audit.action.automation.updated': 'Automação atualizada',
  'audit.action.availability.override_set': 'Exceção de agenda definida',
  'audit.action.availability.updated': 'Disponibilidade atualizada',
  'audit.action.billing.invoice_voided': 'Fatura cancelada',
  'audit.action.billing.payment_confirmed': 'Pagamento confirmado',
  'audit.action.booking.canceled': 'Agendamento cancelado',
  'audit.action.booking.created': 'Agendamento criado',
  'audit.action.booking_link.created': 'Link de agendamento criado',
  'audit.action.catalogue.custom_field_created': 'Campo personalizado criado',
  'audit.action.catalogue.custom_field_updated': 'Campo personalizado atualizado',
  'audit.action.catalogue.loss_reason_created': 'Motivo de perda criado',
  'audit.action.catalogue.loss_reason_deleted': 'Motivo de perda excluído',
  'audit.action.catalogue.loss_reason_updated': 'Motivo de perda atualizado',
  'audit.action.catalogue.score_factor_created': 'Fator de pontuação criado',
  'audit.action.catalogue.score_factor_deleted': 'Fator de pontuação excluído',
  'audit.action.catalogue.score_factor_updated': 'Fator de pontuação atualizado',
  'audit.action.catalogue.score_rule_created': 'Regra de pontuação criada',
  'audit.action.catalogue.score_rule_deleted': 'Regra de pontuação excluída',
  'audit.action.catalogue.score_rule_updated': 'Regra de pontuação atualizada',
  'audit.action.catalogue.source_created': 'Origem criada',
  'audit.action.catalogue.source_deleted': 'Origem excluída',
  'audit.action.catalogue.source_updated': 'Origem atualizada',
  'audit.action.catalogue.status_created': 'Status criado',
  'audit.action.catalogue.status_deleted': 'Status excluído',
  'audit.action.catalogue.status_updated': 'Status atualizado',
  'audit.action.catalogue.tag_created': 'Etiqueta criada',
  'audit.action.catalogue.tag_deleted': 'Etiqueta excluída',
  'audit.action.catalogue.tag_updated': 'Etiqueta atualizada',
  'audit.action.company.created': 'Empresa criada',
  'audit.action.company.deleted': 'Empresa excluída',
  'audit.action.company.lifecycle_changed': 'Estágio da empresa alterado',
  'audit.action.company.updated': 'Empresa atualizada',
  'audit.action.contact.created': 'Contato criado',
  'audit.action.contact.deleted': 'Contato excluído',
  'audit.action.contact.lifecycle_changed': 'Estágio do contato alterado',
  'audit.action.contact.updated': 'Contato atualizado',
  'audit.action.contract.ended': 'Contrato encerrado',
  'audit.action.conversation.created': 'Conversa criada',
  'audit.action.conversation.member_removed': 'Pessoa removida da conversa',
  'audit.action.conversation.members_added': 'Pessoas adicionadas à conversa',
  'audit.action.conversation.role_changed': 'Papel na conversa alterado',
  'audit.action.dashboard.layout_saved': 'Painel reorganizado',
  'audit.action.domain.claimed': 'Domínio reivindicado',
  'audit.action.domain.primary_set': 'Domínio principal definido',
  'audit.action.domain.released': 'Domínio liberado',
  'audit.action.email.suppressed': 'Endereço bloqueado',
  'audit.action.email.template_reset': 'Modelo de e-mail restaurado',
  'audit.action.email.template_saved': 'Modelo de e-mail salvo',
  'audit.action.email.unsuppressed': 'Endereço desbloqueado',
  'audit.action.event.created': 'Evento criado',
  'audit.action.event.deleted': 'Evento excluído',
  'audit.action.event.updated': 'Evento atualizado',
  'audit.action.event.occurrence_changed': 'Uma repetição do evento foi alterada',
  'audit.action.event.occurrence_canceled': 'Uma repetição do evento foi cancelada',
  'audit.action.event.series_split': 'A série foi dividida a partir de uma repetição',
  'audit.action.event.series_trimmed': 'A série foi encerrada a partir de uma repetição',
  'audit.action.event.responded': 'Convite respondido',
  'audit.action.event.guests_invited': 'Convidados adicionados',
  'audit.action.calendar.created': 'Agenda criada',
  'audit.action.calendar.updated': 'Agenda atualizada',
  'audit.action.calendar.deleted': 'Agenda removida',
  'audit.action.calendar.shared': 'Agenda compartilhada',
  'audit.action.calendar.unshared': 'Compartilhamento de agenda removido',
  'audit.action.calendar.connection_created': 'Agenda externa conectada',
  'audit.action.calendar.connection_authorized': 'Agenda externa autorizada',
  'audit.action.calendar.connection_updated': 'Sincronização da agenda alterada',
  'audit.action.calendar.connection_removed': 'Agenda externa desconectada',
  'audit.action.crm.exported': 'Registros exportados',
  'audit.action.file.deleted': 'Arquivo excluído',
  'audit.action.file.rejected': 'Arquivo recusado',
  'audit.action.file.updated': 'Arquivo atualizado',
  'audit.action.file.uploaded': 'Arquivo enviado',
  'audit.action.form.created': 'Formulário criado',
  'audit.action.form.deleted': 'Formulário excluído',
  'audit.action.form.updated': 'Formulário atualizado',
  'audit.action.import.started': 'Importação iniciada',
  'audit.action.inbox.assigned': 'Conversa atribuída',
  'audit.action.inbox.message_retried': 'Mensagem reenviada após falha',
  'audit.action.inbox.message_deleted': 'Mensagem apagada para o cliente',
  'audit.action.inbox.conversation_resolved_by_bot': 'Atendimento encerrado pelo agente de IA',
  'audit.action.inbox.message_scheduled': 'Mensagem agendada',
  'audit.action.inbox.conversation_summarised': 'Conversa resumida por IA',
  'audit.action.crm.order_created': 'Pedido criado',
  'audit.action.crm.order_moved': 'Situação do pedido alterada',
  'audit.action.crm.order_items_changed': 'Itens do pedido alterados',
  'audit.action.crm.order_removed': 'Pedido excluído',
  'audit.action.crm.cadence_created': 'Cadência criada',
  'audit.action.crm.cadence_updated': 'Cadência alterada',
  'audit.action.crm.cadence_removed': 'Cadência excluída',
  'audit.action.crm.cadence_enrolled': 'Registro entrou em uma cadência',
  'audit.action.crm.cadence_stopped': 'Registro saiu de uma cadência',
  'audit.action.crm.key_date_added': 'Data-chave cadastrada',
  'audit.action.crm.key_date_removed': 'Data-chave removida',
  'audit.action.tenant.branding_changed': 'Identidade visual alterada',
  'audit.action.whatsapp.campaign_created': 'Campanha de WhatsApp criada',
  'audit.action.whatsapp.campaign_scheduled': 'Campanha de WhatsApp disparada',
  'audit.action.whatsapp.campaign_cancelled': 'Campanha de WhatsApp cancelada',
  'audit.action.inbox.scheduled_message_cancelled': 'Agendamento cancelado',
  'audit.action.inbox.quick_reply_created': 'Resposta rápida criada',
  'audit.action.inbox.quick_reply_updated': 'Resposta rápida alterada',
  'audit.action.inbox.quick_reply_deleted': 'Resposta rápida excluída',
  'audit.action.inbox.channel_bot_changed': 'Agente do canal alterado',
  'audit.action.inbox.channel_connected': 'Canal conectado',
  'audit.action.inbox.channel_updated': 'Canal alterado',
  'audit.action.inbox.channel_disconnected': 'Canal desconectado',
  'audit.action.inbox.channel_status': 'Situação do canal alterada',
  'audit.action.inbox.delivery_failed': 'Falha na entrega da mensagem',
  'audit.action.inbox.handling_changed': 'Atendimento alterado',
  'audit.action.inbox.handoff': 'Conversa passada para uma pessoa',
  'audit.action.inbox.note_added': 'Nota interna adicionada',
  'audit.action.inbox.queue_created': 'Fila criada',
  'audit.action.inbox.queue_members_set': 'Membros da fila definidos',
  'audit.action.inbox.replied': 'Resposta enviada',
  'audit.action.integrations.api_key_created': 'Chave de API criada',
  'audit.action.integrations.api_key_revoked': 'Chave de API revogada',
  'audit.action.integrations.webhook_created': 'Webhook criado',
  'audit.action.integrations.webhook_deleted': 'Webhook excluído',
  'audit.action.integrations.webhook_delivery_retried': 'Entrega de webhook reenviada',
  'audit.action.integrations.webhook_updated': 'Webhook atualizado',
  'audit.action.knowledge.added': 'Conteúdo adicionado à base',
  'audit.action.knowledge.edited': 'Conteúdo da base editado',
  'audit.action.knowledge.removed': 'Conteúdo removido da base',
  'audit.action.lead.converted': 'Lead convertido',
  'audit.action.lead.created': 'Lead criado',
  'audit.action.lead.deleted': 'Lead excluído',
  'audit.action.lead.updated': 'Lead atualizado',
  'audit.action.marketing.campaign_canceled': 'Campanha de e-mail cancelada',
  'audit.action.marketing.campaign_created': 'Campanha de e-mail criada',
  'audit.action.marketing.campaign_dispatched': 'Campanha de e-mail disparada',
  'audit.action.marketing.campaign_scheduled': 'Campanha de e-mail agendada',
  'audit.action.marketing.campaign_window_set': 'Janela de envio definida',
  'audit.action.marketing.consent_recorded': 'Consentimento registrado',
  'audit.action.marketing.segment_created': 'Segmento criado',
  'audit.action.marketing.segment_deleted': 'Segmento excluído',
  'audit.action.marketing.segment_updated': 'Segmento atualizado',
  'audit.action.marketing.unsubscribed': 'Descadastro registrado',
  'audit.action.onboarding_plan.started': 'Onboarding iniciado',
  'audit.action.onboarding_template.created': 'Modelo de onboarding criado',
  'audit.action.onboarding_template.updated': 'Modelo de onboarding atualizado',
  'audit.action.opportunity.created': 'Negócio criado',
  'audit.action.opportunity.deleted': 'Negócio excluído',
  'audit.action.opportunity.stage_changed': 'Etapa do negócio alterada',
  'audit.action.opportunity.updated': 'Negócio atualizado',
  'audit.action.override.limit_changed': 'Limite ajustado',
  'audit.action.override.revoked': 'Exceção revogada',
  'audit.action.page.created': 'Página criada',
  'audit.action.page.deleted': 'Página excluída',
  'audit.action.page.experiment_set': 'Teste A/B configurado',
  'audit.action.page.published': 'Página publicada',
  'audit.action.page.unpublished': 'Página despublicada',
  'audit.action.page.updated': 'Página atualizada',
  'audit.action.pipeline.created': 'Funil criado',
  'audit.action.pipeline.stages_replaced': 'Etapas do funil substituídas',
  'audit.action.pipeline.updated': 'Funil atualizado',
  'audit.action.plan.updated': 'Plano atualizado',
  'audit.action.platform.announcement_created': 'Aviso criado',
  'audit.action.platform.announcement_deleted': 'Aviso excluído',
  'audit.action.platform.announcement_updated': 'Aviso atualizado',
  'audit.action.inbox.channel_hours_updated': 'Horário de atendimento do canal alterado',
  'audit.action.lead.qualified_from_contact': 'Contato qualificado como lead',
  'audit.action.files.share_created': 'Link de compartilhamento criado',
  'audit.action.files.share_revoked': 'Link de compartilhamento cancelado',
  'audit.action.platform.feedback_sent': 'Relato enviado à Safe Cortex',
  'audit.action.platform.feedback_triaged': 'Relato triado',
  'audit.action.project.created': 'Projeto criado',
  'audit.action.project.deleted': 'Projeto excluído',
  'audit.action.project.updated': 'Projeto atualizado',
  'audit.action.proposal.accepted': 'Proposta aceita',
  'audit.action.proposal.declined': 'Proposta recusada',
  'audit.action.proposal.drafted': 'Proposta criada',
  'audit.action.proposal.sent': 'Proposta enviada',
  'audit.action.proposal.withdrawn': 'Proposta retirada',
  'audit.action.session.revoked': 'Sessão encerrada',
  'audit.action.sms.campaign_canceled': 'Campanha de SMS cancelada',
  'audit.action.sms.campaign_created': 'Campanha de SMS criada',
  'audit.action.sms.campaign_dispatched': 'Campanha de SMS disparada',
  'audit.action.sms.campaign_scheduled': 'Campanha de SMS agendada',
  'audit.action.sms.credit_added': 'Créditos adicionados',
  'audit.action.sms.queued': 'SMS na fila de envio',
  'audit.action.sms.topup_paid': 'Recarga paga',
  'audit.action.sms.topup_started': 'Recarga iniciada',
  'audit.action.social.account_connected': 'Perfil social conectado',
  'audit.action.social.account_disconnected': 'Perfil social desconectado',
  'audit.action.social.account_updated': 'Perfil social atualizado',
  'audit.action.social.post_approved': 'Publicação aprovada',
  'audit.action.social.post_canceled': 'Publicação cancelada',
  'audit.action.social.post_composed': 'Publicação criada',
  'audit.action.social.post_deleted': 'Publicação excluída',
  'audit.action.subscription.plan_changed': 'Plano alterado',
  'audit.action.subscription.plan_change_canceled': 'Mudança de plano cancelada',
  'audit.action.subscription.status_changed': 'Situação da assinatura alterada',
  'audit.action.subscription.trial_extended': 'Teste estendido',
  'audit.action.support.session_ended': 'Acesso de suporte encerrado',
  'audit.action.support.session_started': 'Acesso de suporte iniciado',
  'audit.action.tag.added': 'Etiqueta aplicada',
  'audit.action.tag.removed': 'Etiqueta retirada',
  'audit.action.task.commented': 'Comentário na tarefa',
  'audit.action.task.created': 'Tarefa criada',
  'audit.action.task.deleted': 'Tarefa excluída',
  'audit.action.task.dependency_added': 'Dependência adicionada',
  'audit.action.task.status_changed': 'Situação da tarefa alterada',
  'audit.action.task.time_logged': 'Tempo registrado na tarefa',
  'audit.action.tenant.settings_changed': 'Configurações da empresa alteradas',
  'audit.action.support.portal_link_issued': 'Link do portal gerado para um cliente',
  'audit.action.support.portal_link_revoked': 'Link do portal revogado',
  'audit.action.task.updated': 'Tarefa atualizada',
  'audit.action.team.created': 'Equipe criada',
  'audit.action.team.member_added': 'Pessoa adicionada à equipe',
  'audit.action.team.member_removed': 'Pessoa removida da equipe',
  'audit.action.tenant.created': 'Conta criada',
  'audit.action.tenant.status_changed': 'Situação da conta alterada',
  'audit.action.ticket.created': 'Chamado criado',
  'audit.action.ticket.deleted': 'Chamado excluído',
  'audit.action.ticket.updated': 'Chamado atualizado',
  'audit.action.ticket_category.created': 'Categoria de chamado criada',
  'audit.action.ticket_category.updated': 'Categoria de chamado atualizada',
  'audit.action.user.avatar_changed': 'Foto de perfil alterada',
  'audit.action.user.avatar_removed': 'Foto de perfil removida',
  'audit.action.user.email_changed': 'E-mail alterado',
  'audit.action.user.email_verified': 'E-mail confirmado',
  'audit.action.user.logged_in': 'Entrou no sistema',
  'audit.action.user.logged_out': 'Saiu do sistema',
  'audit.action.user.password_changed': 'Senha alterada',
  'audit.action.user.password_reset': 'Senha redefinida',
  'audit.action.user.registered': 'Cadastro criado',
  'audit.action.user.role_changed': 'Perfil alterado',
  'audit.action.user.role_granted': 'Perfil concedido',
  'audit.action.user.role_revoked': 'Perfil revogado',
  'audit.action.user.settings_changed': 'Preferências alteradas',
  'audit.action.whatsapp.template_created': 'Modelo de WhatsApp criado',
  'audit.action.whatsapp.template_deleted': 'Modelo de WhatsApp excluído',
  'audit.action.whatsapp.template_sent': 'Modelo de WhatsApp enviado',
  'audit.action.whatsapp.interactive_sent': 'Opções de resposta enviadas no WhatsApp',
  'audit.action.whatsapp.template_status_changed': 'Situação do modelo alterada',
  'audit.action.whatsapp.template_updated': 'Modelo de WhatsApp atualizado',
  'audit.action.whatsapp_web.disconnected': 'WhatsApp Web desconectado',
  'audit.action.whatsapp_web.pairing_started': 'Pareamento do WhatsApp Web iniciado',
  'audit.action.sprint.created': 'Sprint criada',
  'audit.action.sprint.updated': 'Sprint atualizada',
  'audit.action.sprint.started': 'Sprint iniciada',
  'audit.action.sprint.completed': 'Sprint encerrada',
  'audit.action.sprint.deleted': 'Sprint excluída',
  'audit.action.sprint.item_moved': 'Item movido de sprint',
  'status.title': 'Status da plataforma',
  'status.checking': 'Verificando serviços...',
  'status.ready': 'Todos os serviços operacionais',
  'status.notReady': 'Serviços indisponíveis',
  'status.unreachable': 'API indisponível',
  'status.dependency.up': 'operacional',
  'status.dependency.down': 'indisponível',
  'status.retry': 'Verificar novamente',
  'status.requestId': 'ID da requisição',
  'phase.current': 'Fase atual',
  'phase.foundation': 'Fase 1 — Fundação',
  'phase.description':
    'Ambiente reproduzível, contexto de requisição, isolamento por tenant e verificações de saúde.',

  'field.email': 'E-mail',
  'field.password': 'Senha',
  'field.name': 'Seu nome',
  'field.company': 'Nome da empresa',

  'login.title': 'Entrar',
  'login.submit': 'Entrar',
  'login.submitting': 'Entrando...',
  'login.invalidCredentials': 'E-mail ou senha incorretos.',
  'login.signupLink': 'Criar uma conta',
  'login.subtitle': 'Acesse o painel da sua operação',
  'login.showPassword': 'Mostrar senha',
  'login.hidePassword': 'Ocultar senha',
  'login.noAccount': 'Ainda não tem conta?',
  'login.methodLabel': 'Forma de entrar',
  'login.withPassword': 'Com senha',
  'login.withLink': 'Com link',
  'login.forgotPassword': 'Esqueci minha senha',

  // Entrar por link enviado ao e-mail. A confirmação é sempre condicional: dizer "enviado" para um
  // endereço e outra coisa para outro transformaria a tela num jeito de descobrir quem tem conta.
  'magic.subtitle': 'Informe seu e-mail e enviamos um link de acesso — sem senha.',
  'magic.hint': 'O link vale por 15 minutos e só pode ser usado uma vez.',
  'magic.submit': 'Enviar link de acesso',
  'magic.submitting': 'Enviando...',
  'magic.sentTitle': 'Verifique seu e-mail',
  'magic.sentBody': 'Se {email} tiver uma conta, o link de acesso chega em instantes.',
  'magic.sentFineprint':
    'O link vale por 15 minutos e só pode ser usado uma vez. Não encontrou? Confira a caixa de spam.',
  'magic.useAnother': 'Usar outro e-mail',
  'magic.checkingTitle': 'Entrando',
  'magic.checking': 'Validando seu link de acesso...',
  'magic.failedTitle': 'Não foi possível entrar',
  'magic.invalid': 'Este link não é válido.',
  'magic.used': 'Este link já foi usado. Peça um novo.',
  'magic.expired': 'Este link expirou. Peça um novo.',
  'magic.mismatch': 'Este link foi emitido para outro endereço de e-mail.',
  'magic.disabled': 'Esta conta não está ativa. Fale com o suporte.',
  'magic.requestAnother': 'Pedir um novo link',

  'forgot.title': 'Redefinir senha',
  'forgot.subtitle': 'Informe seu e-mail e enviamos um link para criar uma nova senha.',
  'forgot.submit': 'Enviar link',
  'forgot.submitting': 'Enviando...',
  'forgot.sentTitle': 'Verifique seu e-mail',
  'forgot.sentBody': 'Se {email} tiver uma conta, o link de redefinição chega em instantes.',
  'forgot.sentFineprint':
    'O link vale por 60 minutos. Enquanto você não criar uma nova senha, a atual continua valendo.',
  'forgot.backToLogin': 'Voltar para o login',

  /* Onde o e-mail de confirmação cai (Phase 128). */
  'verifyEmail.title': 'Confirmação de e-mail',
  'verifyEmail.working': 'Confirmando seu endereço…',
  'verifyEmail.doneTitle': 'E-mail confirmado',
  'verifyEmail.doneBody': 'Sua conta está ativa. Pode entrar.',
  'verifyEmail.alreadyDone': 'Este e-mail já estava confirmado.',
  'verifyEmail.invalid': 'Este link não é válido. Peça outro entrando na sua conta.',
  'verifyEmail.expired': 'Este link expirou. Entre na sua conta para receber outro.',
  'verifyEmail.missingToken':
    'O endereço veio sem o código de confirmação. Abra o link direto do e-mail.',
  'verifyEmail.goToLogin': 'Ir para o login',
  'reset.title': 'Criar nova senha',
  'reset.subtitle': 'Escolha uma senha que você não use em nenhum outro lugar.',
  'reset.newPassword': 'Nova senha',
  'reset.confirmPassword': 'Confirme a nova senha',
  'reset.mismatch': 'As senhas não conferem.',
  'reset.submit': 'Salvar nova senha',
  'reset.submitting': 'Salvando...',
  'reset.doneTitle': 'Senha alterada',
  'reset.doneBody': 'Suas outras sessões foram encerradas. Entre com a nova senha.',
  'reset.goToLogin': 'Ir para o login',
  'reset.invalid': 'Este link não é válido.',
  'reset.used': 'Este link já foi usado. Peça um novo.',
  'reset.expired': 'Este link expirou. Peça um novo.',
  'reset.missingToken': 'O link está incompleto. Peça um novo e-mail de redefinição.',
  'reset.requestAnother': 'Pedir um novo link',

  // The panel beside the form. Copy is ours; nothing here describes another product.
  'auth.hero.line': 'CRM e atendimento',
  'auth.hero.highlight': 'em uma só plataforma.',
  'auth.hero.body':
    'Leads, conversas, propostas e cobrança no mesmo lugar — com automações que seguem trabalhando enquanto sua equipe atende.',
  'auth.hero.point1': 'Caixa de entrada única: WhatsApp, e-mail e SMS',
  'auth.hero.point2': 'CRM completo: contatos, oportunidades e tarefas',
  'auth.hero.point3': 'Automações, formulários e páginas de captura',
  'auth.hero.rights': 'Todos os direitos reservados',

  'signup.title': 'Criar conta',
  'signup.subtitle': 'Comece o teste gratuito de 7 dias, sem cartão de crédito.',
  'signup.submit': 'Criar conta',
  'signup.submitting': 'Criando...',
  'signup.passwordHint': 'Use pelo menos 10 caracteres. Uma frase longa funciona melhor.',
  'signup.weakPassword': 'Escolha uma senha mais forte:',
  'signup.emailTaken': 'Este e-mail já está cadastrado.',
  'signup.haveAccount': 'Já tem uma conta?',
  'signup.checkInbox': 'Confirme seu e-mail',
  'signup.sentTo': 'Enviamos um link de confirmação para {email}.',
  'signup.restrictedMode':
    'Você já pode entrar: até confirmar o e-mail, alguns recursos ficam indisponíveis.',

  'password.reason.too_short': 'A senha é curta demais.',
  'password.reason.too_long': 'A senha é longa demais.',
  'password.reason.too_common': 'Esta senha é muito comum.',
  'password.reason.repeated_character': 'Evite repetir o mesmo caractere.',
  'password.reason.sequential': 'Evite sequências como 123456789.',
  'password.reason.contains_email': 'A senha não pode conter seu e-mail.',
  'password.reason.contains_name': 'A senha não pode conter seu nome.',

  'home.welcome': 'Olá, {name}',
  'home.tenant': 'Tenant ativo',
  'home.role': 'Perfil',
  'home.unverified':
    'Confirme seu e-mail para liberar campanhas, integrações, convites e exportações.',
  'home.logout': 'Sair',

  'error.generic': 'Não foi possível concluir a operação. Tente novamente.',

  'nav.companies': 'Empresas',
  'nav.pipeline': 'Funil',
  'nav.home': 'Início',
  'nav.crm': 'CRM',
  'nav.leads': 'Leads',
  'nav.contacts': 'Contatos',
  'nav.opportunities': 'Oportunidades',
  'nav.inbox': 'Atendimento',
  'nav.conversations': 'Conversas',
  'nav.channels': 'Canais',
  'nav.tickets': 'Chamados',
  // ----- Escolher uma pessoa -----
  'work.systemAuthor': 'Sistema',
  'people.unassigned': 'Sem responsável',
  'people.filter': 'Filtrar pessoas',
  'people.noMatches': 'Ninguém com esse nome.',

  'nav.agentList': 'Agentes',
  'nav.knowledge': 'Conhecimento',
  'nav.batches': 'Execuções em lote',
  'nav.proposals': 'Propostas',
  'nav.contracts': 'Contratos',
  'nav.success': 'Sucesso do cliente',
  'nav.accounts': 'Contas',
  'nav.playbooks': 'Playbooks',
  'playbooks.description':
    'Pontos de partida para o que a equipe faz sempre. Usar um cria uma automação comum, que você pode abrir e mudar.',
  'playbooks.when': 'Quando',
  'playbooks.then': 'Faz',
  'playbooks.use': 'Usar este',
  'playbooks.inactiveNote':
    'Nada dispara sozinho: a automação é criada desativada e só passa a valer quando você ativar.',

  'playbook.account_quiet_check_in': 'Conta quieta: escrever para o cliente',
  'playbook.account_quiet_check_in.hint':
    'Quando a conta fica quieta, envia um e-mail de acompanhamento usando o modelo "Contato de acompanhamento" — que você pode reescrever em Marketing › E-mail › Modelos — e cria uma tarefa para ver se responderam.',
  'playbook.account_critical': 'Conta em risco crítico',
  'playbook.account_critical.hint':
    'A saúde caiu para crítica. Abre uma tarefa para o CSM e marca a renovação como em risco.',
  'playbook.account_at_risk': 'Conta em atenção',
  'playbook.account_at_risk.hint':
    'A saúde caiu para atenção. Abre uma tarefa para revisar o histórico antes de falar com o cliente.',
  'playbook.account_recovered': 'Conta recuperada',
  'playbook.account_recovered.hint':
    'A conta voltou a ficar saudável. Tira a marca de risco da renovação.',
  'playbook.renewal_60': 'Renovação em 60 dias',
  'playbook.renewal_60.hint':
    'Ainda dá tempo de negociar preço ou expansão. Abre uma tarefa para preparar a conversa.',
  'playbook.renewal_30': 'Renovação em 30 dias',
  'playbook.renewal_30.hint': 'Hora da proposta. Abre uma tarefa para enviá-la.',
  'playbook.renewal_7': 'Renovação em 7 dias',
  'playbook.renewal_7.hint':
    'Sem confirmação a uma semana do prazo. Abre uma tarefa e marca a renovação como em risco.',
  'playbook.account_welcome': 'Boas-vindas a uma conta nova',
  'playbook.account_welcome.hint':
    'Uma conta passou a ser gerenciada. Abre a tarefa da reunião inicial.',

  'nav.onboarding': 'Onboarding',
  'nav.queues': 'Filas',
  'nav.templates': 'Templates',
  'nav.marketing': 'Marketing',
  'nav.forms': 'Formulários',
  'nav.pages': 'Páginas',
  'nav.domains': 'Domínios',
  'nav.email': 'E-mail',
  'nav.sms': 'SMS',
  'nav.forecast': 'Previsão',
  'nav.keyDates': 'Datas-chave',
  'nav.cadences': 'Cadências',
  'nav.orders': 'Pedidos',
  'nav.whatsappCampaigns': 'Campanhas WhatsApp',
  'nav.campaigns': 'Campanhas',
  'nav.agents': 'Agentes de IA',
  'nav.catalogues': 'Cadastros',
  // SMS (context.md sections 72-74)
  'sms.description': 'Envie SMS com créditos pré-pagos, e veja exatamente o que cada envio custa.',
  'sms.comingSoon':
    'O módulo de SMS está em breve. As telas já funcionam, mas o envio ainda não está liberado — estamos fechando o contrato com a operadora e o preço por mensagem.',
  'sms.balance': 'Saldo',
  'sms.heldAmount': '{amount} reservado',
  'sms.lowBalance': 'Saldo baixo',
  'sms.tab.send': 'Enviar',
  'sms.tab.campaigns': 'Campanhas',
  'sms.tab.messages': 'Mensagens',
  'sms.tab.wallet': 'Créditos',
  'sms.compose': 'Nova mensagem',
  'sms.message': 'Mensagem',
  'sms.messagePlaceholder': 'Escreva a mensagem que será enviada',
  'sms.characters': '{n} caracteres',
  'sms.recipients': 'Destinatários',
  'sms.recipientCount': '{n} número(s)',
  'sms.estimate': 'Calcular custo',
  'sms.send': 'Enviar',
  'sms.sending': 'Enviando…',
  'sms.priced': 'Números com preço',
  'sms.segments': 'Segmentos',
  'sms.cost': 'Custo',
  'sms.balanceAfter': 'Saldo depois',
  'sms.shortfall': 'Faltam {amount} de crédito para este envio.',
  'sms.unpriced': 'Sem tarifa para: {numbers}',
  'sms.queuedResult': '{n} mensagem(ns) na fila, {amount} reservado.',
  'sms.usage': 'Consumo (30 dias)',
  'sms.sentCount': 'Enviadas',
  'sms.deliveredCount': 'Entregues',
  'sms.failedCount': 'Não entregues',
  'sms.spent': 'Gasto',
  'sms.noUsage': 'Nenhum SMS ainda',
  'sms.noUsageBody': 'O consumo aparece aqui depois do primeiro envio.',
  'sms.recipient': 'Destinatário',
  'sms.queuedAt': 'Enviada em',
  'sms.noMessages': 'Nenhuma mensagem',
  'sms.noMessagesBody': 'As mensagens enviadas aparecem aqui com o status de entrega.',
  'sms.newCampaign': 'Nova campanha',
  'sms.campaignName': 'Campanha',
  'sms.createCampaign': 'Criar campanha',
  'sms.campaignResult': 'Resultado',
  'sms.campaignQueued': '{queued} de {total} · {amount}',
  'sms.campaignEstimated': '{n} destinatário(s) · {amount}',
  'sms.sendNow': 'Enviar agora',
  'sms.cancel': 'Cancelar',
  'sms.noCampaigns': 'Nenhuma campanha',
  'sms.noCampaignsBody': 'Crie uma campanha para enviar a mesma mensagem a uma lista.',
  'sms.movement': 'Movimento',
  'sms.amount': 'Valor',
  'sms.held': 'Reservado',
  'sms.when': 'Quando',
  'sms.ledgerTitle': 'Extrato de créditos',
  'sms.ledgerBody': 'Todo movimento de crédito aparece aqui: o saldo é a soma deste extrato.',
  'sms.noLedger': 'Sem movimentos',
  'sms.noLedgerBody': 'Créditos comprados e mensagens enviadas aparecem aqui.',

  'sms.state.QUEUED': 'Na fila',
  'sms.state.RESERVED': 'Reservada',
  'sms.state.SENDING': 'Enviando',
  'sms.state.SENT': 'Enviada',
  'sms.state.DELIVERED': 'Entregue',
  'sms.state.UNDELIVERED': 'Não entregue',
  'sms.state.FAILED': 'Falhou',
  'sms.state.CANCELED': 'Cancelada',

  'sms.campaign.DRAFT': 'Rascunho',
  'sms.campaign.SCHEDULED': 'Agendada',
  'sms.campaign.SENDING': 'Enviando',
  'sms.campaign.SENT': 'Enviada',
  'sms.campaign.CANCELED': 'Cancelada',
  'sms.campaign.FAILED': 'Falhou',

  'sms.ledger.CREDIT': 'Crédito',
  'sms.ledger.DEBIT': 'Débito',
  'sms.ledger.RESERVATION': 'Reserva',
  'sms.ledger.RELEASE': 'Devolução',
  'sms.ledger.REFUND': 'Estorno',
  'sms.ledger.ADJUSTMENT': 'Ajuste',

  'sms.refusal.insufficient_credit': 'Crédito insuficiente',
  'sms.refusal.no_price': 'Sem tarifa para este país',
  'sms.refusal.invalid_number': 'Número inválido',
  'sms.refusal.already_reserved': 'Já reservada',
  'sms.refusal.nothing_queued': 'Nada foi enviado',
  // E-mail marketing (context.md sections 45, 46, 80)
  'campaigns.description':
    'Envie uma mensagem para uma lista, com consentimento, descadastro e métricas.',
  'campaigns.tab.campaigns': 'Campanhas',
  'campaigns.tab.segments': 'Segmentos',
  'campaigns.newCampaign': 'Nova campanha',
  'campaigns.campaign': 'Campanha',
  'campaigns.subject': 'Assunto',
  'campaigns.body': 'Mensagem',
  'campaigns.bodyHint':
    'O link de descadastro é acrescentado automaticamente — é obrigatório e não pode ser removido.',
  'campaigns.audience': 'Público',
  'campaigns.segment': 'Segmento',
  'campaigns.list': 'Lista',
  'campaigns.useList': 'Usar uma lista de endereços',
  'campaigns.addresses': 'Endereços',
  'campaigns.createCampaign': 'Criar campanha',
  'campaigns.result': 'Resultado',
  'campaigns.when': 'Quando',
  'campaigns.preview': 'Prever',
  'campaigns.report': 'Relatório',
  'campaigns.sendNow': 'Enviar agora',
  'campaigns.addVariant': 'Testar variação',
  'campaigns.variantCount': '{n} variações (teste A/B)',
  'campaigns.previewLine': '{n} pessoa(s) receberiam',
  'campaigns.previewSkipped': '{suppressed} bloqueado(s), {consent} sem consentimento',
  'campaigns.sentLine': '{queued} de {total} enviadas',
  'campaigns.reportLine': '{opened} aberturas · {clicked} cliques · {unsubscribed} descadastros',
  'campaigns.notReported': 'Sem dados do provedor: {metrics}',
  'campaigns.metric.delivered': 'entregas',
  'campaigns.metric.bounced': 'rejeições',
  'campaigns.none': 'Nenhuma campanha',
  'campaigns.noneBody': 'Crie uma campanha para enviar a mesma mensagem a um segmento.',
  'campaigns.newSegment': 'Novo segmento',
  'campaigns.segmentName': 'Nome',
  'campaigns.segmentHint':
    'Um segmento guarda a pergunta, não a resposta: quem entra depois também entra na próxima campanha.',
  'campaigns.entityType': 'Registros',
  'campaigns.entity.CONTACT': 'Contatos',
  'campaigns.entity.LEAD': 'Leads',
  'campaigns.createSegment': 'Criar segmento',
  'campaigns.members': 'Pessoas',
  'campaigns.count': 'Contar',
  'campaigns.remove': 'Remover',
  'campaigns.noSegments': 'Nenhum segmento',
  'campaigns.noSegmentsBody': 'Um segmento define o público de uma campanha.',

  'campaigns.state.DRAFT': 'Rascunho',
  'campaigns.state.SCHEDULED': 'Agendada',
  'campaigns.state.SENDING': 'Enviando',
  'campaigns.state.SENT': 'Enviada',
  'campaigns.state.CANCELED': 'Cancelada',
  'campaigns.state.FAILED': 'Falhou',
  // AI agents (context.md sections 87-99)
  'ai.description':
    'Agentes que agem dentro do CRM com as mesmas permissões de quem os aciona — nunca mais que isso.',
  'ai.spent30': 'Gasto (30 dias)',
  'ai.tokens': '{n} tokens',
  'ai.newAgent': 'Novo agente',
  'ai.agentName': 'Nome',
  'ai.createAgent': 'Criar agente',
  'ai.open': 'Abrir',
  'ai.pause': 'Pausar',
  'ai.resume': 'Ativar',
  'ai.versionLabel': 'versão {n}',
  'ai.noAgents': 'Nenhum agente',
  'ai.noAgentsBody': 'Crie um agente, escreva o que ele deve fazer e escolha as ferramentas.',
  'ai.behaviour': 'Comportamento',
  'ai.versionHint':
    'Publicar cria uma versão nova e imutável. A versão em uso nunca muda sem deixar rastro.',
  'ai.instructions': 'Instruções',
  'ai.tools': 'Ferramentas',
  'ai.toolsHint': 'O agente só alcança o que estiver marcado aqui — e só o que você mesmo alcança.',
  'ai.publish': 'Publicar versão',
  'ai.publishedAs': 'Em uso: versão {n} ({hash}…)',
  'ai.try': 'Testar',
  'ai.question': 'Pergunta',
  'ai.ask': 'Perguntar',
  'ai.thinking': 'Pensando…',
  'ai.stepsLabel': 'Passos',
  'ai.tokensLabel': 'Tokens',
  'ai.costLabel': 'Custo',
  'ai.runs': 'Execuções',
  'ai.stepCount': '{n} passo(s)',
  'ai.when': 'Quando',
  'ai.inspect': 'Ver passos',
  'ai.noRuns': 'Nenhuma execução',
  'ai.noRunsBody': 'As execuções aparecem aqui, passo a passo.',

  'ai.state.DRAFT': 'Rascunho',
  'ai.state.ACTIVE': 'Ativo',
  'ai.state.PAUSED': 'Pausado',
  'ai.state.ARCHIVED': 'Arquivado',

  'ai.run.RUNNING': 'Executando',
  'ai.run.COMPLETED': 'Concluída',
  'ai.run.FAILED': 'Falhou',
  'ai.run.REFUSED': 'Recusada',
  'ai.run.STOPPED': 'Interrompida',

  'ai.refusal.not_in_allowlist': 'Ferramenta fora da lista do agente',
  'ai.refusal.unknown_tool': 'Ferramenta inexistente',
  'ai.refusal.step_limit': 'Limite de passos',
  // Tenant catalogues (context.md sections 34, 101)
  'catalogues.title': 'Cadastros',
  'catalogues.description':
    'As listas que cada empresa preenche do seu jeito: status, origens, tags, campos e pontuação.',
  'catalogues.tab.statuses': 'Status de Cliente',
  'catalogues.tab.sources': 'Origens',
  'catalogues.tab.tags': 'Tags',
  'catalogues.tab.fields': 'Campos Personalizados',
  'catalogues.tab.pipelines': 'Pipelines',
  'catalogues.tab.reasons': 'Motivos de Perda',
  'catalogues.tab.score': 'Score',
  'catalogues.statuses': 'Status de Cliente',
  'catalogues.statusesHint':
    'Em que ponto da relação o cliente está. O status padrão é o que todo cliente novo recebe.',
  'catalogues.sources': 'Origens de Cliente',
  'catalogues.sourcesHint':
    'De onde o cliente veio — é o que responde “o que está dando resultado”.',
  'catalogues.reasons': 'Motivos de Perda',
  'catalogues.reasonsHint':
    'Por que um negócio foi perdido. A lista existe para que a resposta possa ser contada.',
  'catalogues.tags': 'Tags de Cliente',
  'catalogues.tagsHint':
    'Marcadores livres para segmentar a base — a cor definida aqui é a que aparece nas telas.',
  'catalogues.fields': 'Campos Personalizados',
  'catalogues.fieldsHint':
    'O mesmo campo em todo registro, com o mesmo tipo — por isso pode ser filtrado e usado em relatório. A chave e o tipo não mudam depois de criados.',
  'catalogues.score': 'Configuração de Score',
  'catalogues.scoreHint':
    'Fatores são os sinais que valem ponto. Pontos negativos são normais: um cliente parado vale menos que um sem sinal nenhum.',
  'catalogues.name': 'Nome',
  'catalogues.code': 'Código',
  'catalogues.codePlaceholder': 'ex: lead',
  'catalogues.codeHint':
    'Gerado do nome se ficar em branco. Não muda depois — é o que integrações usam.',
  'catalogues.color': 'Cor',
  'catalogues.descriptionLabel': 'Descrição',
  'catalogues.default': 'Padrão',
  'catalogues.isDefault': 'Padrão',
  'catalogues.makeDefault': 'Tornar padrão',
  'catalogues.active': 'Ativo',
  'catalogues.inactive': 'Inativo',
  'catalogues.activate': 'Ativar',
  'catalogues.deactivate': 'Desativar',
  'catalogues.remove': 'Excluir',
  'catalogues.add': 'Adicionar',
  'catalogues.empty': 'Lista vazia',
  'catalogues.emptyBody': 'Cadastre a primeira entrada acima.',
  'catalogues.tag': 'Tag',
  'catalogues.usage': 'Registros',
  'catalogues.noTags': 'Nenhuma tag',
  'catalogues.noTagsBody': 'Tags marcam clientes para segmentar depois.',
  'catalogues.entity': 'Aplica-se a',
  'catalogues.entity.COMPANY': 'Clientes',
  'catalogues.entity.CONTACT': 'Contatos',
  'catalogues.entity.LEAD': 'Leads',
  'catalogues.entity.OPPORTUNITY': 'Oportunidades',
  'catalogues.field': 'Campo',
  'catalogues.fieldName': 'Nome do campo',
  'catalogues.type': 'Tipo',
  'catalogues.options': 'Opções (separadas por vírgula)',
  'catalogues.required': 'Obrigatório',
  'catalogues.yes': 'Sim',
  'catalogues.noFields': 'Nenhum campo personalizado',
  'catalogues.noFieldsBody':
    'Cadastre um acima — ele passa a aparecer no formulário e na ficha, para todo mundo.',
  'catalogues.factor': 'Fator',
  'catalogues.factorName': 'Nome do fator',
  'catalogues.category': 'Categoria',
  'catalogues.points': 'Pontos',
  'catalogues.pointsHint': 'Pode ser negativo.',
  'catalogues.noFactors': 'Nenhum fator',
  'catalogues.noFactorsBody': 'Fatores são os sinais que compõem a pontuação de um cliente.',

  'catalogues.newPipeline': 'Nova pipeline',
  'catalogues.pipelinesHint':
    'O funil de vendas: as etapas por onde um negócio passa e a chance de fechamento em cada uma. Uma etapa com negócios dentro não pode ser removida.',
  'catalogues.stageName': 'Nome da etapa',
  'catalogues.probability': 'Chance',
  'catalogues.won': 'Ganho',
  'catalogues.lost': 'Perdido',
  'catalogues.newStage': 'Nova etapa',
  'catalogues.addStage': 'Adicionar etapa',
  'catalogues.saveStages': 'Salvar etapas',

  'catalogues.scoreCategory.engagement': 'Engajamento',
  'catalogues.scoreCategory.finance': 'Financeiro',
  'catalogues.scoreCategory.sales': 'Vendas',
  'catalogues.scoreCategory.support': 'Suporte',

  'catalogues.fieldType.TEXT': 'Texto',
  'catalogues.fieldType.TEXTAREA': 'Texto longo',
  'catalogues.fieldType.INTEGER': 'Número inteiro',
  'catalogues.fieldType.DECIMAL': 'Número decimal',
  'catalogues.fieldType.CURRENCY': 'Moeda',
  'catalogues.fieldType.DATE': 'Data',
  'catalogues.fieldType.DATETIME': 'Data e hora',
  'catalogues.fieldType.BOOLEAN': 'Sim/Não',
  'catalogues.fieldType.SELECT': 'Lista de opções',
  'catalogues.fieldType.MULTISELECT': 'Lista (várias opções)',
  'catalogues.fieldType.EMAIL': 'E-mail',
  'catalogues.fieldType.PHONE': 'Telefone',
  'catalogues.fieldType.URL': 'Link',
  // Dashboard (context.md sections 34, 133)
  'dashboard.title': 'Visão geral',
  'dashboard.greeting': 'Olá, {name}.',
  'dashboard.description': 'Acompanhe os resultados e as prioridades do seu dia.',
  'dashboard.period': 'Período',
  'dashboard.period.TODAY': 'Hoje',
  'dashboard.period.LAST_7_DAYS': 'Últimos 7 dias',
  'dashboard.period.LAST_30_DAYS': 'Últimos 30 dias',
  'dashboard.period.THIS_MONTH': 'Este mês',
  'dashboard.period.THIS_YEAR': 'Este ano',
  'dashboard.arrange': 'Personalizar',
  'dashboard.done': 'Concluir',
  'dashboard.arranging': 'Cards do painel',
  'dashboard.arrangingHint':
    'Escolha o que aparece e em que ordem. Marque “para toda a equipe” para definir o painel padrão da empresa.',
  'dashboard.onIt': 'No painel',
  'dashboard.available': 'Disponíveis',
  'dashboard.allUsed': 'Todos os cards já estão no painel.',
  'dashboard.notAvailable': 'Indisponível no seu plano ou perfil de acesso',
  'dashboard.removeCard': 'Remover',
  'dashboard.moveUp': 'Mover para cima',
  'dashboard.moveDown': 'Mover para baixo',
  'dashboard.size.S': 'P',
  'dashboard.size.M': 'M',
  'dashboard.size.L': 'G',
  'dashboard.forEveryone': 'Para toda a equipe',
  'dashboard.save': 'Salvar painel',
  'dashboard.useCompany': 'Voltar ao painel da empresa',
  'dashboard.useDefault': 'Restaurar o painel padrão',
  'dashboard.useDefaultHint':
    'Remove o painel salvo para toda a equipe e volta ao padrão do produto.',
  'dashboard.personalNote': 'Você está vendo o seu painel pessoal.',
  'dashboard.empty': 'Painel vazio',
  'dashboard.emptyBody': 'Escolha os cards que quer acompanhar.',
  'dashboard.noData': 'Sem dados no período.',

  'dashboard.window': 'De {from} a {to}',
  'dashboard.previous': 'Período anterior: {v}',
  'dashboard.noBase': 'sem base anterior',
  'dashboard.total': 'total',
  'dashboard.seriesLabel': 'Série por dia',
  'dashboard.allClear': 'Nada pendente. Bom sinal.',
  'dashboard.card.crm.leads_by_day': 'Leads por dia',
  'dashboard.card.sales.won_by_day': 'Vendas ganhas por dia',
  'dashboard.card.inbox.messages_by_day': 'Mensagens por dia',
  'dashboard.card.work.attention': 'Precisa de atenção',
  'dashboard.attention.overdueTasks': 'Tarefas atrasadas',
  'dashboard.attention.dueToday': 'Tarefas para hoje',
  'dashboard.attention.unassigned': 'Conversas sem responsável',
  'dashboard.attention.breached': 'Conversas com SLA estourado',
  'dashboard.attention.staleLeads': 'Leads parados há mais de 7 dias',
  'dashboard.attention.undecidedProposals': 'Propostas vistas e sem resposta',
  'dashboard.attention.hint': 'clique para abrir',
  'dashboard.card.crm.new_leads': 'Novos leads',
  'dashboard.card.crm.new_customers': 'Novos clientes',
  'dashboard.card.crm.customers_by_status': 'Clientes por status',
  'dashboard.card.crm.customers_by_source': 'Origem dos clientes',
  'dashboard.card.crm.leads_by_status': 'Leads por situação',
  'dashboard.card.sales.open_pipeline': 'Valor do funil',
  'dashboard.card.sales.won_value': 'Vendas ganhas no período',
  'dashboard.card.sales.win_rate': 'Taxa de ganho',
  'dashboard.card.sales.by_stage': 'Funil por etapa',
  'dashboard.card.sales.loss_reasons': 'Motivos de perda',
  'dashboard.card.work.open_tasks': 'Tarefas abertas',
  'dashboard.card.work.overdue_tasks': 'Tarefas atrasadas',
  'dashboard.card.work.my_tasks': 'Minhas tarefas',
  'dashboard.card.inbox.open_conversations': 'Conversas abertas',
  'dashboard.card.inbox.unassigned': 'Sem responsável',
  'dashboard.card.marketing.sms_balance': 'Créditos de SMS',
  'dashboard.card.marketing.campaign_results': 'Campanhas recentes',
  'dashboard.card.ai.spend': 'Gasto com IA',

  'dashboard.secondary.crm.new_leads': '{n} no total',
  'dashboard.secondary.crm.new_customers': '{n} no total',
  'dashboard.secondary.sales.open_pipeline': '{n} oportunidade(s)',
  'dashboard.secondary.sales.won_value': '{n} negócio(s) · Taxa de ganho: {p}%',
  'dashboard.secondary.sales.win_rate': '{n} decidido(s) no período',
  'dashboard.secondary.marketing.sms_balance': '{n} centavos reservados',
  'dashboard.secondary.ai.spend': '{n} execução(ões)',

  /* The home screen, redrawn (Phase 54). */
  'dashboard.create': 'Criar',
  'dashboard.create.lead': 'Novo lead',
  'dashboard.create.deal': 'Novo negócio',
  'dashboard.create.contact': 'Novo contato',
  'dashboard.create.company': 'Nova empresa',
  'dashboard.create.task': 'Nova tarefa',
  'dashboard.sub.by_stage': 'Posição da carteira',
  'dashboard.seeBoard': 'Ver funil',
  'dashboard.seeAll': 'Ver todas',
  'dashboard.newTask': 'Nova tarefa',
  'dashboard.stage': 'Etapa',
  'dashboard.value': 'Valor',
  'dashboard.pipelineTotal': 'Total no funil',
  'dashboard.foot.crm.customers_by_status': '{n} cliente(s) no total',
  'dashboard.foot.crm.customers_by_source': '{n} cliente(s) no total',
  'dashboard.foot.crm.leads_by_status': '{n} lead(s) no total',
  'dashboard.foot.sales.loss_reasons': '{n} negócio(s) perdido(s) no período',
  'dashboard.task': 'Tarefa',
  'dashboard.due': 'Prazo',
  'dashboard.priority': 'Prioridade',
  'dashboard.finish': 'Concluir',
  'dashboard.finishTask': 'Concluir a tarefa {title}',
  'dashboard.overdueTasks': '{n} tarefa(s) atrasada(s)',
  'dashboard.seeOverdue': 'Ver atrasadas',
  'dashboard.showing': 'Exibindo {n} de {m} tarefa(s)',
  'dashboard.noTasks': 'Nenhuma tarefa aberta. Bom sinal.',
  'dashboard.noCampaigns': 'Nenhuma campanha concluída no período.',
  'dashboard.campaignLine': '{sent} enviadas · {opened} aberturas',
  // The deals board (context.md sections 35, 134)
  'board.description': 'Acompanhe suas oportunidades em cada etapa.',
  'board.breadcrumb': 'Onde você está',

  'board.openHint': '{n} negócio(s) em andamento',
  'board.forecastHint': 'Estimativa de receita',
  'board.wonHint': '{n} negócio(s) concluído(s)',
  'board.openDeals': 'Negócios abertos',
  'board.stagesHint': 'Distribuídos em {n} etapa(s)',

  'board.view': 'Modo de visualização',
  'board.kanban': 'Kanban',
  'board.list': 'Lista',

  'board.addDeal': 'Adicionar negócio',
  'board.dragHint': 'Arraste os cards para mover entre etapas.',
  'board.emptyLost': 'Nenhum negócio perdido',
  'board.emptyWon': 'Nenhum negócio ganho ainda',

  'board.cardMenu': 'Ações do negócio',
  'board.stageMenu': 'Ações da etapa',
  'board.moveToStage': 'Mover para {name}',
  'board.editStages': 'Configurar etapas',
  'board.stageProbability': 'Probabilidade desta etapa: {n}%',

  'board.deal': 'Negócio',
  'board.stage': 'Etapa',
  'board.amount': 'Valor',
  'board.age': 'Tempo na etapa',
  'board.expectedClose': 'Previsão de fechamento',
  'board.deals': '{n} negócio(s)',
  'board.newDeal': 'Novo negócio',
  'board.newDealPlaceholder': 'Nome do negócio',
  'board.newDealHint': 'Entra na primeira etapa do funil.',
  'board.moveTo': 'Mover para',
  'board.emptyColumn': 'Nada aqui.',
  'board.more': '+{n} não exibido(s)',
  'board.forecast': 'Previsão ponderada',
  'board.won': 'Ganho',
  'board.open': 'Em aberto',
  'board.inStage': '{n} dia(s) na etapa',
  'board.allSources': 'Todas as origens',
  'board.searchPlaceholder': 'Buscar pelo título',
  'board.owner': 'Responsável',
  'board.everyone': 'Todos',
  'board.noPipeline': 'Nenhum funil configurado',
  'board.noPipelineBody': 'Crie um funil em Configurações › Cadastros › Pipelines.',

  // The deal screen (context.md sections 35, 131)
  'deal.back': 'Funil',
  'deal.status.OPEN': 'Em aberto',
  'deal.status.WON': 'Ganho',
  'deal.status.LOST': 'Perdido',
  'deal.inStage': 'Nesta etapa há {n} dia(s)',
  'deal.negotiation': 'Negociação',
  'deal.value': 'Valor',
  'deal.expected': 'Fechamento previsto',
  'deal.notes': 'Notas',
  'deal.save': 'Salvar',
  'deal.owner': 'Responsável',
  'deal.noOwner': '— sem responsável —',
  'deal.noSource': '— sem origem —',
  'deal.temperature': 'Temperatura do cliente',
  'deal.temperature.COLD': 'Frio',
  'deal.temperature.WARM': 'Morno',
  'deal.temperature.HOT': 'Quente',
  'deal.temperature.CLOSED': 'Fechado',
  'deal.priority': 'Prioridade',
  'deal.priority.HIGH': 'Alta',
  'deal.priority.MEDIUM': 'Média',
  'deal.priority.LOW': 'Baixa',
  'deal.origin': 'Origem do lead',
  'deal.page': 'Página',
  'deal.referrer': 'Veio de',
  'deal.campaign': 'Campanha',
  'deal.tab.items': 'Produtos e serviços',
  'deal.tab.company': 'Contato e empresa',
  'deal.tab.history': 'Histórico',
  'deal.total': 'Total',
  'deal.item': 'Item',
  'deal.quantity': 'Qtd.',
  'deal.unitPrice': 'Preço unitário',
  'deal.discount': 'Desconto (%)',
  'deal.addItem': 'Adicionar item',
  'deal.saveItems': 'Salvar produtos',
  'deal.noCompany': 'Este negócio não está vinculado a um cliente.',
  'deal.createdAt': 'Criado em',
  'deal.movedAt': 'Entrou na etapa em',
  'deal.historyHint': 'A linha do tempo completa fica na ficha do cliente.',

  /* The deal screen, redrawn (Phase 53). */
  'deal.backToBoard': 'Voltar ao funil',
  'deal.schedule': 'Agendar atividade',
  'deal.saveChanges': 'Salvar alterações',
  'deal.actions': 'Ações do negócio',
  'deal.discard': 'Descartar alterações',
  'deal.openCompany': 'Abrir ficha da empresa',
  'deal.amountLabel': 'Valor do negócio',
  'deal.noExpected': 'Sem data',
  'deal.noTemperature': '— sem temperatura —',
  'deal.noIndustry': 'Setor não informado',
  'deal.stagePanel': 'Etapa do funil',
  'deal.current': 'Atual',
  'deal.data': 'Dados do negócio',
  'deal.linkedCompany': 'Empresa vinculada',
  'deal.subtotal': 'Subtotal',
  'deal.itemsTotal': 'Total dos itens',
  'deal.noItems': 'Nenhum item adicionado',
  'deal.noItemsBody': 'Inclua produtos ou serviços para compor o valor deste negócio.',
  'deal.internalNotes': 'Notas internas',
  'deal.notesEmpty': 'Nenhuma nota registrada ainda.',
  'deal.edit': 'Editar',
  'deal.nextAction': 'Próxima ação',
  'deal.nextActionEmpty': 'Planeje o próximo contato',
  'deal.nextActionHint': 'Um negócio sem próximo passo é um negócio que para sozinho.',
  'deal.scheduleTitle': 'Agendar atividade',
  'deal.scheduleWhat': 'O que será feito',
  'deal.scheduleWhen': 'Quando',
  'deal.schedulePlaceholder': 'Ligar para confirmar a proposta',
  'deal.scheduleCreate': 'Agendar',
  'deal.lostTitle': 'Marcar como perdido',
  'deal.lostHint': 'Registrar o motivo é o que transforma uma perda em informação.',
  'deal.lostReason': 'Motivo da perda',
  'deal.lostPick': '— escolha um motivo —',
  'deal.lostOther': 'Outro (descrever)',
  'deal.lostOtherLabel': 'Descreva o motivo',
  'deal.lostConfirm': 'Marcar como perdido',
  // The campaign wizard (context.md sections 45, 46)
  'wizard.title': 'Nova campanha de e-mail',
  'wizard.description': 'Quatro decisões: a mensagem, para quem, quando, e o que isso dá.',
  'wizard.step.1': 'Configurações',
  'wizard.step.2': 'Público',
  'wizard.step.3': 'Agendamento',
  'wizard.step.4': 'Revisão',
  'wizard.name': 'Nome da campanha',
  'wizard.nameHint': 'Nome interno, só a sua equipe vê.',
  'wizard.fromName': 'Nome do remetente',
  'wizard.fromNameHint': 'Aparece no rodapé e no descadastro.',
  'wizard.subject': 'Assunto',
  'wizard.subjectHint': 'É o que decide se alguém abre.',
  'wizard.body': 'Mensagem',
  'wizard.plainText': 'Texto simples',
  'wizard.blocks': 'Blocos',
  'wizard.blocksHint':
    'Os mesmos blocos das landing pages. O e-mail sai com estilo embutido e uma versão em texto feita a partir dos mesmos blocos.',
  'wizard.audience.ALL': 'Todos os clientes',
  'wizard.audienceHint.ALL': 'todos com e-mail, respeitando consentimento e lista de bloqueio',
  'wizard.audience.FILTER': 'Filtrar clientes',
  'wizard.audienceHint.FILTER': 'status, origem, tags e data de cadastro',
  'wizard.audience.SEGMENT': 'Segmento salvo',
  'wizard.audienceHint.SEGMENT': 'um público que você já definiu',
  'wizard.audience.LIST': 'Lista de endereços',
  'wizard.audienceHint.LIST': 'endereços colados à mão',
  'wizard.chooseSegment': '— escolha um segmento —',
  'wizard.createdFrom': 'Cadastrados a partir de',
  'wizard.createdTo': 'Cadastrados até',
  'wizard.timing.NOW': 'Enviar assim que eu confirmar',
  'wizard.timingHint.NOW': 'sai na próxima passagem do agendador',
  'wizard.timing.WINDOW': 'Só dentro de uma janela',
  'wizard.timingHint.WINDOW': 'dias da semana e horário, no fuso da sua empresa',
  'wizard.weekdays': 'Dias da semana',
  'wizard.weekday.mon': 'Seg',
  'wizard.weekday.tue': 'Ter',
  'wizard.weekday.wed': 'Qua',
  'wizard.weekday.thu': 'Qui',
  'wizard.weekday.fri': 'Sex',
  'wizard.weekday.sat': 'Sáb',
  'wizard.weekday.sun': 'Dom',
  'wizard.from': 'A partir das',
  'wizard.to': 'Até as',
  'wizard.until': 'Parar depois de',
  'wizard.windowHint':
    'Fora da janela a campanha espera a próxima — nada é perdido e nada sai de madrugada.',
  'wizard.startAt': 'Começar em',
  'wizard.startAtHint': 'Em branco, começa agora.',
  'wizard.audienceSize': 'No público',
  'wizard.willReceive': 'Vão receber',
  'wizard.withoutConsent': 'Sem consentimento',
  'wizard.reviewHint':
    'Quem está bloqueado ou pediu descadastro não recebe — e aparece contado acima, não sumido.',
  'wizard.nobody': 'Ninguém receberia esta campanha. Revise o público antes de confirmar.',
  'wizard.back': 'Voltar',
  'wizard.next': 'Próximo',
  'wizard.review': 'Revisar',
  'wizard.confirm': 'Confirmar e agendar',
  // Notifications (context.md sections 133, 206)
  'notify.empty': 'Nada por aqui ainda.',
  'notify.readAll': 'Marcar todas como lidas',
  'notify.task_assigned': 'A tarefa "{title}" é sua agora.',
  'notify.task_due': 'A tarefa "{title}" está chegando no prazo.',
  'notify.task_overdue': 'A tarefa "{title}" passou do prazo.',
  'notify.event_soon': 'O compromisso "{title}" começa em breve.',
  'notify.key_date_today': '{label} — {subject} é hoje.',
  'notify.key_date_soon': '{label} — {subject} acontece em {days} dias.',
  'toast.open': 'Abrir',
  /* O lembrete de compromisso, com o que dá para fazer a respeito (Phase 125). */
  'reminder.title': 'Lembrete',
  'reminder.inMinutes': 'Começa em {n} min',
  'reminder.now': 'Começa agora',
  'reminder.startedAgo': 'Começou há {n} min',
  'reminder.join': 'Entrar na reunião',
  'reminder.snooze': 'Adiar',
  'reminder.minutes': '{n} min',
  'reminder.reschedule': 'Remarcar',
  'reminder.plus30': '+30 min',
  'reminder.plus60': '+1 hora',
  'reminder.tomorrow': 'Amanhã',
  'reminder.otherTime': 'Outro horário',
  'reminder.cancelEvent': 'Cancelar compromisso',
  'reminder.silence': 'Silenciar',
  'toast.dismiss': 'Dispensar',
  'notify.campaign_sent': 'A campanha "{name}" saiu: {queued} enviada(s), {skipped} ignorada(s).',
  'notify.campaign_failed': 'A campanha "{name}" não pôde ser enviada.',
  'notify.sms_sent': 'A campanha de SMS "{name}" saiu: {queued} mensagem(ns).',
  'notify.sms_failed': 'A campanha de SMS "{name}" não pôde ser enviada.',

  // ----- Propostas e contratos -----
  'deal.propose': 'Gerar proposta',
  'proposals.description':
    'O que foi enviado, quem abriu, quem aceitou. Uma proposta é uma fotografia do negócio no dia em que saiu.',
  'proposals.one': 'Proposta',
  'proposals.all': 'Todas',
  'proposals.none': 'Nenhuma proposta ainda. Gere uma a partir dos itens de um negócio.',
  'proposals.title': 'Título',
  'proposals.total': 'Total',
  'proposals.unit': 'Unitário',
  'proposals.recurring': 'Mensal',
  'proposals.oneOff': 'Único',
  'proposals.monthly': 'mensal',
  'proposals.lines': 'Itens',
  'proposals.text': 'Texto da proposta',
  'proposals.introduction': 'Apresentação',
  'proposals.terms': 'Condições',
  'proposals.termMonths': 'Vigência (meses)',
  'proposals.validityDays': 'Validade (dias)',
  'proposals.term': 'Vigência',
  'proposals.noTerm': 'Sem prazo',
  'proposals.months': '{n} meses',
  'proposals.validity': 'Validade',
  'proposals.days': '{n} dias',
  'proposals.validUntil': 'Válida até',
  'proposals.sentAt': 'Enviada em',
  'proposals.viewedAt': 'Aberta em',
  'proposals.saveDraft': 'Salvar rascunho',
  'proposals.send': 'Enviar',
  'proposals.resend': 'Gerar novo link',
  'proposals.withdraw': 'Retirar',
  'proposals.openDeal': 'Abrir o negócio',
  'proposals.openPdf': 'Abrir PDF',
  'proposals.linkReady': 'Link para o cliente',
  'proposals.linkOnce':
    'Este link aparece só agora — ele não fica guardado. Se perder, gere outro; o anterior deixa de funcionar.',
  'proposals.copy': 'Copiar',
  'proposals.copied': 'Copiado',
  'proposals.acceptedBy': 'Aceite',
  'proposals.who': 'Quem',
  'proposals.when': 'Quando',
  'proposals.from': 'De onde (IP)',
  'proposals.document': 'Documento (sha256)',
  'proposals.declinedBecause': 'Recusada porque',
  'nav.ads': 'Anúncios',
  'ads.title': 'Anúncios',
  'ads.description':
    'O que cada campanha custou, ao lado do que ela trouxe: leads no CRM, negócios ganhos e receita.',
  'ads.accounts': 'Contas de anúncios',
  'ads.accountsHint':
    'Uma conta é lida, nunca alterada: campanhas e gastos entram aqui; nada aqui cria ou pausa um anúncio.',
  'ads.noAccountsYet': 'Nenhuma conta de anúncios conectada ainda.',
  'ads.noAccounts': 'Conecte uma conta de anúncios para ver o relatório.',
  'ads.connectFirst': 'Conectar agora',
  'ads.network.LOCAL': 'Local (sem rede)',
  'ads.network.META': 'Meta Ads',
  'ads.network.GOOGLE': 'Google Ads',
  'ads.externalId.META': 'ID da conta de anúncios (act_…)',
  'ads.externalId.GOOGLE': 'ID do cliente (Customer ID)',
  'ads.externalId.LOCAL': 'Identificador',
  'ads.developerToken': 'Developer token',
  'ads.tokenHint':
    'Cole o token gerado no painel de desenvolvedor da rede. Fica cifrado e não é exibido de novo.',
  'ads.accountStatus.CONNECTED': 'Conectada',
  'ads.accountStatus.EXPIRED': 'Token expirado',
  'ads.accountStatus.REVOKED': 'Acesso revogado',
  'ads.accountStatus.DISABLED': 'Desativada',
  'ads.synced': 'sincronizada',
  'ads.neverSynced': 'ainda não sincronizada',
  'ads.syncNow': 'Sincronizar agora',
  'ads.lastDays': 'Últimos {n} dias',
  'ads.to': 'até',
  'ads.allAccounts': 'Todas as contas',
  'ads.spend': 'Investimento',
  'ads.crmLeads': 'Leads no CRM',
  'ads.networkSays': 'a rede diz',
  'ads.cpl': 'Custo por lead',
  'ads.won': 'Ganhos',
  'ads.cpa': 'Custo por venda',
  'ads.roas': 'ROAS',
  'ads.revenue': 'Receita',
  'ads.clicks': 'Cliques',
  'ads.leads': 'Leads (CRM / rede)',
  'ads.campaign': 'Campanha',
  'ads.campaigns': 'Campanhas',
  'ads.spendByDay': 'Investimento por dia',
  'ads.noneYet': 'Nenhuma campanha ainda. Conecte uma conta e sincronize.',
  'ads.noneInRange': 'Nenhuma campanha com dados neste período.',
  'ads.utmCampaign': 'utm_campaign',
  'ads.declareUtm': 'declarar',
  'deal.ad': 'Anúncio',
  'error.ads.missingToken': 'Conectar esta rede exige um token de acesso.',
  'error.ads.missingAccountId': 'Conectar esta rede exige o ID da conta de anúncios.',
  'error.ads.missingDeveloperToken': 'O Google Ads exige também o developer token.',
  'error.ads.accountTaken': 'Essa conta de anúncios já está conectada.',
  'nav.integrations': 'Integrações',
  'integrations.title': 'Integrações',
  'integrations.description':
    'Chaves de API para a sua integração e webhooks para o que acontece aqui chegar lá.',
  'integrations.apiKeys': 'Chaves de API',
  'integrations.apiKeysHint':
    'Uma chave age como você, com o seu acesso — ou só leitura, se marcar. Ela aparece uma única vez.',
  'integrations.keyName': 'Nome',
  'integrations.keyNamePlaceholder': 'Para que serve (ex.: Zapier, ERP)',
  'integrations.keyPrefix': 'Chave',
  'integrations.scopes': 'Escopo',
  'integrations.scopesN': '{n} permissões',
  'integrations.fullAccess': 'o mesmo acesso que você',
  'integrations.lastUsed': 'Último uso',
  'integrations.neverUsed': 'nunca usada',
  'integrations.revoke': 'Revogar',
  'integrations.revoked': 'Revogada',
  'integrations.readOnly': 'Somente leitura',
  'integrations.createKey': 'Gerar chave',
  'integrations.yourKey': 'Sua chave de API',
  'integrations.yourSecret': 'Segredo de assinatura do webhook',
  'integrations.revealHint': 'Copie agora. Por segurança, não é possível ver de novo.',
  'integrations.copy': 'Copiar',
  'integrations.copied': 'Copiado',
  'integrations.savedIt': 'Já guardei',
  'integrations.webhooks': 'Webhooks',
  'integrations.webhooksHint':
    'Cada evento escolhido vira um POST assinado (HMAC SHA-256) na URL. Falhas são repetidas com espera crescente; dez seguidas desativam o webhook.',
  'integrations.hookName': 'Nome',
  'integrations.hookUrl': 'URL',
  'integrations.events': 'Eventos',
  'integrations.createHook': 'Criar webhook',
  'integrations.active': 'Ativo',
  'integrations.paused': 'Pausado',
  'integrations.disabled': 'Desativado',
  'integrations.pause': 'Pausar',
  'integrations.resume': 'Reativar',
  'integrations.deliveries': 'Entregas',
  'integrations.noDeliveries': 'Nenhuma entrega ainda.',
  'integrations.noKeys': 'Nenhuma chave de API ainda.',
  'integrations.expiresAt': 'Expira em',
  'integrations.eventId': 'ID do evento',
  'integrations.nextAttempt': 'Próxima tentativa',
  'integrations.event': 'Evento',
  'integrations.attempts': 'Tentativas',
  'integrations.response': 'Resposta',
  'integrations.when': 'Quando',
  'integrations.retry': 'Tentar de novo',
  'integrations.docs': 'Documentação da API',
  'integrations.docsHint': 'Toda rota /v1 está descrita no documento OpenAPI:',
  'error.integrations.scopeNotHeld': 'Você não pode dar a uma chave uma permissão que não tem.',
  'error.integrations.badPattern':
    'Padrão de evento inválido. Use algo como crm.lead.* ou crm.lead.created.',
  'error.integrations.badUrl': 'A URL do webhook precisa ser pública e começar com https://.',
  'table.filter': 'Filtrar nesta lista',
  'table.count': '{n} registros',
  'table.matching': '{n} de {total} registros',
  'table.columns': 'Colunas',
  'contracts.signedAt': 'Assinado em',
  'contracts.endedAt': 'Encerrado em',
  'cs.scoredOn': 'Avaliada em',
  'ai.charactersLabel': 'Caracteres',
  'ads.impressions': 'Impressões',
  'ads.dailyBudget': 'Orçamento diário',
  'table.resetColumns': 'Voltar ao padrão',
  'table.noMatch': 'Nada corresponde ao filtro.',
  'table.perPage': 'Por página',
  'table.page': 'Página {n} de {pages}',
  'prefs.appearance': 'Aparência',
  'prefs.theme': 'Tema',
  'prefs.themeHint': 'Claro, escuro, ou o que o seu aparelho estiver usando.',
  'prefs.theme.system': 'Automático',
  'prefs.theme.light': 'Claro',
  'prefs.theme.dark': 'Escuro',
  'prefs.toasts': 'Avisos na tela',
  'prefs.toastsHint':
    'Além do sino, uma notificação nova aparece por alguns segundos no canto da tela.',
  'prefs.toastsOn': 'Mostrar avisos na tela',
  'prefs.tables': 'Tabelas',
  'prefs.tablesHint': 'As colunas e o tamanho de página de cada lista ficam salvos no seu perfil.',
  'prefs.resetTables': 'Restaurar todas as tabelas',
  'sms.rate': '{v} por SMS',
  'sms.topup.title': 'Adicionar créditos',
  'sms.topup.hint':
    'Escolha um valor e como pagar. O saldo entra assim que o pagamento for confirmado.',
  'sms.topup.other': 'Outro valor (R$)',
  'sms.topup.how': 'Forma de pagamento',
  'sms.topup.method.PIX': 'Pix',
  'sms.topup.method.CARD': 'Cartão de crédito',
  'sms.topup.start': 'Gerar pagamento',
  'sms.topup.qrAlt': 'QR Code do Pix',
  'sms.topup.pixHint': 'Pague pelo QR Code ou copie o código Pix no seu banco.',
  'sms.topup.copyPix': 'Copiar código Pix',
  'sms.topup.expires': 'vale até',
  'sms.topup.waiting': 'Aguardando a confirmação do pagamento… esta tela atualiza sozinha.',
  'sms.topup.openCheckout': 'Pagar com cartão',
  'sms.topup.paid': 'Pagamento confirmado. O saldo já está disponível.',
  'sms.topup.failed': 'O pagamento não foi concluído.',
  'sms.topup.simulate': 'Simular pagamento (ambiente de desenvolvimento)',
  'sms.topup.later': 'Pagar depois',
  'sms.topup.resume': 'Continuar',
  'sms.topup.status.PENDING': 'Aguardando pagamento',
  'sms.topup.status.PAID': 'Pago',
  'sms.topup.status.FAILED': 'Falhou',
  'sms.topup.status.EXPIRED': 'Expirado',
  'sms.topup.status.CANCELED': 'Cancelado',
  'error.sms.topupAmount': 'Escolha um valor entre R$ 10,00 e R$ 5.000,00.',
  'connection.online': 'Conectado · {ms} ms',
  'connection.slow': 'Conexão lenta · {ms} ms',
  'connection.offline': 'Sem conexão com o servidor',
  'nav.social': 'Redes sociais',
  'social.title': 'Redes sociais',
  'social.description':
    'O calendário do que vai sair, em qual conta e quando — com aprovação quando a conta pedir.',
  'social.accounts': 'Contas',
  'social.accountsHint':
    'Uma conta é um perfil ou página onde a plataforma publica em seu nome. O token fica cifrado e nunca é exibido de novo.',
  'social.noAccountsYet': 'Nenhuma conta conectada ainda.',
  'social.noAccounts': 'Conecte uma conta para começar a agendar.',
  'social.connectFirst': 'Conectar agora',
  'social.connectAccount': 'Conectar conta',
  'social.connect': 'Conectar',
  'social.disconnect': 'Desconectar',
  'social.networkLabel': 'Rede',
  'social.accountName': 'Nome da conta',
  'social.accessToken': 'Token de acesso',
  'social.tokenHint':
    'Cole o token gerado no painel de desenvolvedor da rede. O fluxo de login direto na rede chega quando o app estiver registrado.',
  'social.tokenUntil': 'token até',
  'social.externalId.INSTAGRAM': 'ID da conta Instagram Business',
  'social.externalId.FACEBOOK': 'ID da página',
  'social.externalId.LINKEDIN': 'URN da organização',
  'social.externalId.LOCAL': 'Identificador',
  'social.requiresApproval': 'Exige aprovação',
  'social.requiresApprovalHint': 'Toda publicação nesta conta espera a aprovação de uma pessoa.',
  'social.network.LOCAL': 'Local (sem rede)',
  'social.network.INSTAGRAM': 'Instagram',
  'social.network.FACEBOOK': 'Facebook',
  'social.network.LINKEDIN': 'LinkedIn',
  'social.accountStatus.CONNECTED': 'Conectada',
  'social.accountStatus.EXPIRED': 'Token expirado',
  'social.accountStatus.REVOKED': 'Acesso revogado',
  'social.accountStatus.DISABLED': 'Desativada',
  'social.newPost': 'Nova publicação',
  'social.editPost': 'Editar publicação',
  'social.post': 'Publicação',
  'social.thisMonth': 'Neste mês',
  'social.all': 'Todas',
  'social.none': 'Nada agendado neste mês.',
  'social.when': 'Quando',
  'social.account': 'Conta',
  'social.unscheduled': 'Sem data',
  'social.images': 'imagens',
  'social.byAgent': 'escrita por agente de IA',
  'social.text': 'Texto',
  'social.textPlaceholder': 'O que vai sair?',
  'social.selected': 'selecionadas',
  'social.pickImages': 'Escolher imagens',
  'social.changeImages': 'Trocar imagens',
  'social.clearImages': 'Remover',
  'social.useImages': 'Usar estas',
  'social.noImages': 'Nenhuma imagem na biblioteca. Envie em Arquivos.',
  'social.needsMedia': 'Esta rede exige pelo menos uma imagem.',
  'social.link': 'Link (opcional)',
  'social.date': 'Data',
  'social.time': 'Hora',
  'social.willBeDraft': 'Sem data, fica como rascunho.',
  'social.willWaitApproval':
    'Esta conta exige aprovação: a publicação espera alguém aprovar antes de sair.',
  'social.willBeScheduled': 'Sai automaticamente no horário, no fuso da sua conta.',
  'social.schedule': 'Agendar',
  'social.saveDraft': 'Salvar rascunho',
  'social.approve': 'Aprovar',
  'social.cancel': 'Cancelar publicação',
  'social.edit': 'Editar',
  'social.delete': 'Excluir',
  'social.loading': 'Carregando...',
  'social.previousMonth': 'Mês anterior',
  'social.nextMonth': 'Próximo mês',
  'social.publishedAt': 'Publicada em',
  'social.openOnNetwork': 'abrir na rede',
  'social.failure': 'Falha',
  'social.attempts': 'Tentativas',
  'social.retrying': 'Nova tentativa',
  'social.capturedAt': 'medido',
  'social.metric.impressions': 'impressões',
  'social.metric.reach': 'alcance',
  'social.metric.likes': 'curtidas',
  'social.metric.comments': 'comentários',
  'social.metric.shares': 'compartilhamentos',
  'social.metric.clicks': 'cliques',
  'social.status.DRAFT': 'Rascunho',
  'social.status.PENDING_APPROVAL': 'Aguardando aprovação',
  'social.status.SCHEDULED': 'Agendada',
  'social.status.PUBLISHING': 'Publicando',
  'social.status.PUBLISHED': 'Publicada',
  'social.status.FAILED': 'Falhou',
  'social.status.CANCELED': 'Cancelada',
  'error.social.tooLong': 'O texto passa do limite desta rede.',
  'error.social.needsMedia': 'Esta rede exige pelo menos uma imagem.',
  'error.social.missingToken': 'Conectar esta rede exige um token de acesso.',
  'error.social.notWaiting': 'Esta publicação não está aguardando aprovação.',
  'error.social.alreadyOut': 'Uma publicação que já saiu não pode ser alterada.',
  'error.social.cannotCancel': 'Esta publicação não pode mais ser cancelada.',
  'proposals.status.DRAFT': 'Rascunho',
  'proposals.status.SENT': 'Enviada',
  'proposals.status.VIEWED': 'Aberta',
  'proposals.status.ACCEPTED': 'Aceita',
  'proposals.status.DECLINED': 'Recusada',
  'proposals.status.EXPIRED': 'Expirada',
  'proposals.status.WITHDRAWN': 'Retirada',
  'contracts.description':
    'O que foi assinado, por quanto tempo, e quando vence. A conta de sucesso do cliente lê daqui.',
  'contracts.none': 'Nenhum contrato ainda. Um aceite de proposta cria o primeiro.',
  'contracts.period': 'Período',
  'contracts.openProposal': 'Ver proposta',
  'contracts.end': 'Encerrar',
  'contracts.endReason': 'Por que está encerrando?',
  'contracts.confirmEnd': 'Confirmar encerramento',
  'contracts.status.ACTIVE': 'Vigente',
  'contracts.status.ENDED': 'Encerrado',
  'contracts.status.CANCELED': 'Cancelado',
  'public.proposal': 'Proposta nº {n}',
  'public.for': 'Para {name}',
  'public.downloadPdf': 'Baixar em PDF',
  'public.yourName': 'Seu nome',
  'public.yourEmail': 'Seu e-mail',
  'public.acceptHint':
    'Ao aceitar, registramos seu nome, e-mail, endereço de rede e a versão exata deste documento.',
  'public.accept': 'Aceitar proposta',
  'public.declineInstead': 'Prefiro recusar',
  'public.declineReason': 'Se quiser, diga o motivo',
  'public.decline': 'Recusar',
  'public.accepted': 'Proposta aceita. Contrato nº {n} registrado — obrigado!',
  'public.declined': 'Recusa registrada. Obrigado pelo retorno.',
  'public.closed': 'Esta proposta não está mais aberta.',

  // ----- Bots de canal -----
  'inbox.handling.BOT': 'Agente respondendo',
  'inbox.handling.HANDOFF_REQUESTED': 'Agente pediu uma pessoa',
  'inbox.takeOver': 'Assumir',
  'inbox.handBack': 'Devolver ao agente',
  'channels.bot': 'Agente',
  'channels.noBot': 'Pessoas respondem',

  // ----- Agentes: modelos e aprovações -----
  'ai.startFrom': 'Começar a partir de um modelo',
  'ai.startFromHint':
    'Cada modelo vira um agente comum, seu para editar. Nada nele continua especial depois de criado.',
  'ai.templateUse': 'Usar este',
  'ai.templateTools': '{n} ferramenta(s)',
  'ai.templateGuarded': '{n} com aprovação',
  'ai.approvalTools': 'Ferramentas que esperam uma pessoa',
  'ai.approvalToolsHint':
    'Antes de usar uma destas, o agente para e pede aprovação. A conversa continua depois da resposta.',
  'ai.approvals': 'Aguardando sua aprovação',
  'ai.approvalsHint':
    'O agente quer fazer isto. Aprove com os argumentos como estão, ou recuse dizendo por quê — a razão vai para o agente.',
  'ai.approve': 'Aprovar',
  'ai.reject': 'Recusar',
  'ai.rejectReason': 'Motivo, se for recusar',
  'ai.parkedHint':
    'O agente parou numa ação que precisa de aprovação. Responda no painel de aprovações e ele continua de onde parou.',
  'ai.run.AWAITING_APPROVAL': 'Aguardando aprovação',
  'ai.refusal.rejected_by_person': 'recusado por uma pessoa',
  'ai.refusal.approval_expired': 'aprovação expirou',

  // ----- Execuções em lote -----
  'ai.batches': 'Execuções em lote',
  'ai.batchesDescription':
    'Rodar um agente sobre muitos registros. Acontece em segundo plano e dá para parar no meio.',
  'ai.newBatch': 'Nova execução',
  'ai.noBatches': 'Nenhuma execução ainda.',
  'ai.batch': 'Execução',
  'ai.agent': 'Agente',
  'ai.pickAgent': 'Escolha um agente',
  'ai.noPublishedAgent': 'Nenhum agente publicado. Publique uma versão antes de rodar em lote.',
  'ai.batchEntity': 'Sobre o quê',
  'ai.batchName': 'Nome',
  'ai.batchInstruction': 'O que perguntar sobre cada registro',
  'ai.batchInstructionPlaceholder': 'Avalie se {{name}} vale um contato e diga por quê.',
  'ai.batchInstructionHint':
    'Use {{campo}} para trazer dados do registro. O registro inteiro vai junto, como conteúdo — nunca como instrução.',
  'ai.batchBudgetField': 'Teto de gasto (R$, opcional)',
  'ai.batchBudgetPlaceholder': 'Sem teto',
  'ai.batchBudget': 'de {value}',
  'ai.runBatch': 'Rodar',
  'ai.stopBatch': 'Parar',
  'ai.batchProgress': 'Andamento',
  'ai.batchSpent': 'Gasto',
  'ai.batchCounts': '{done} de {total}',
  'ai.batchFailed': '{n} com erro',
  'ai.batchNoItems': 'Nada registrado ainda.',

  'ai.batch.PENDING': 'Na fila',
  'ai.batch.RUNNING': 'Rodando',
  'ai.batch.COMPLETED': 'Concluída',
  'ai.batch.FAILED': 'Interrompida',
  'ai.batch.CANCELED': 'Cancelada',

  'ai.item.PENDING': 'Na fila',
  'ai.item.RUNNING': 'Rodando',
  'ai.item.DONE': 'Pronto',
  'ai.item.FAILED': 'Erro',
  'ai.item.SKIPPED': 'Ignorado',

  'ai.entity.LEAD': 'Leads',
  'ai.entity.CONTACT': 'Contatos',
  'ai.entity.COMPANY': 'Empresas',
  'ai.entity.OPPORTUNITY': 'Oportunidades',

  // ----- Base de conhecimento -----
  'ai.knowledge': 'Base de conhecimento',
  'ai.knowledgeDescription':
    'O que os agentes sabem além do CRM. Só o que está indexado pode ser encontrado.',
  'ai.documents': 'Documentos',
  'ai.document': 'Documento',
  'ai.noDocuments': 'Nenhum documento ainda.',
  'ai.addDocument': 'Novo documento',
  // Importing what the product already holds (Phase 143).
  'ai.import.open': 'Trazer do sistema',
  'ai.import.title': 'Trazer conteúdo que já existe',
  'ai.import.description':
    'Páginas publicadas, arquivos de texto e as perguntas dos seus formulários viram documentos da base — sem copiar e colar.',
  'ai.import.kind.page': 'Páginas',
  'ai.import.kind.file': 'Arquivos',
  'ai.import.kind.form': 'Formulários',
  'ai.import.formNote':
    'Trazemos as perguntas do formulário, não as respostas: uma resposta é dado do cliente e precisa sumir quando ele pedir.',
  'ai.import.fileNote':
    'Por enquanto: texto, Markdown, CSV, JSON e HTML. PDF ainda não — sem um extrator, o que entraria na base seria ruído.',
  'ai.import.bring': 'Trazer',
  'ai.import.empty': 'Nada aqui para trazer ainda.',
  'ai.documentTitle': 'Título',
  'ai.documentContent': 'Conteúdo',
  'ai.documentPlaceholder':
    '## Boleto e cobrança\n\nO boleto é gerado todo dia cinco…\n\n## Cancelamento\n\n…',
  'ai.documentHint':
    'Use "##" para separar seções. O título da seção vai junto de cada trecho e costuma ser a palavra que a pessoa procura.',
  'ai.saveDocument': 'Salvar documento',
  'ai.characters': '{n} caracteres',
  'ai.chunks': 'Trechos',
  'ai.indexedAt': 'Indexado em',
  'ai.reindex': 'Reindexar',
  'ai.notReady': '{n} ainda não indexado(s)',
  'ai.status.PENDING': 'Na fila',
  'ai.status.INDEXING': 'Indexando',
  'ai.status.READY': 'Pronto',
  'ai.status.FAILED': 'Falhou',
  'ai.status.ARCHIVED': 'Arquivado',
  'ai.tryIt': 'Testar a busca',
  'ai.tryItHint':
    'Roda exatamente a busca que o agente faz. Serve para saber se ele acharia isso antes do cliente perguntar.',
  'ai.tryItPlaceholder': 'Ex.: como emitir a segunda via do boleto',
  'ai.searching': 'Procurando…',
  'ai.noHits':
    'Nada suficientemente próximo. O agente também não encontraria — e responderia que não sabe, em vez de inventar.',

  // ----- Atendimento: chamados -----
  'support.description':
    'A fila de chamados, ordenada pelo que está atrasado — não pelo que chegou por último.',
  'support.newTicket': 'Novo chamado',
  'support.newTicketHint':
    'Só o assunto é obrigatório. O prazo vem da fila e da categoria; o resto dá para completar depois.',
  'support.openTicket': 'Abrir chamado',
  'support.noTickets': 'Nenhum chamado por aqui.',
  'support.searchPlaceholder': 'Assunto, e-mail ou número do chamado',
  'support.subject': 'Assunto',
  'support.subjectPlaceholder': 'Ex.: não consigo emitir o boleto',
  'support.body': 'Descrição',
  'support.category': 'Categoria',
  'support.noCategory': 'Sem categoria',
  'support.priority': 'Prioridade',
  'support.assignee': 'Responsável',
  'support.byQueue': 'Deixar a fila decidir',
  'support.requester': 'Quem pediu',
  'support.noRequester': 'Sem solicitante informado',
  'support.source': 'Origem',
  'support.details': 'Dados',
  'support.history': 'Histórico',
  'support.rating': 'Avaliação',
  'support.sla': 'Prazos',
  'support.firstResponse': 'Primeira resposta',
  'support.resolution': 'Solução',
  'support.inTime': 'em {value}',
  'support.lateBy': 'há {value}',
  'support.noMessages': 'Nada foi dito ainda.',
  'support.customer': 'Cliente',
  'support.agent': 'Atendimento',
  'support.system': 'Sistema',
  'support.internal': 'Interno',
  'support.publicReply': 'Resposta ao cliente',
  'support.internalNote': 'Nota interna',
  'support.replyPlaceholder': 'O cliente vai ler isto.',
  'support.notePlaceholder': 'Só a equipe vê esta nota.',
  'support.andThen': 'E depois',
  'support.keepStatus': 'Manter como está',
  'support.send': 'Enviar',
  'support.sending': 'Enviando…',

  // Phase 47 — the customer portal.
  'portal.title': 'Seus chamados',
  'portal.none': 'Você ainda não tem chamados abertos com esta empresa.',
  'portal.openedAt': 'Aberto {when}',
  'portal.noMessages': 'Ainda não há mensagens neste chamado.',
  'portal.replyPlaceholder': 'Escreva aqui a sua resposta',
  'portal.send': 'Enviar resposta',
  'portal.finished': 'Este chamado foi encerrado. Para algo novo, fale com a empresa.',
  'portal.rateTitle': 'Resolvemos o seu chamado?',
  'portal.rateBody':
    'Diga como foi o atendimento. Se ainda não está resolvido, responda abaixo em vez de avaliar — o chamado volta para a equipe.',
  'portal.rateDeadline': 'Avalie até {when}. Sem resposta, o chamado é cancelado.',
  'portal.ratePlaceholder': 'Quer contar mais alguma coisa? (opcional)',
  'portal.rateSend': 'Enviar avaliação',
  'portal.gone': 'Este link não funciona mais',
  'portal.goneBody': 'Peça um novo link para quem está atendendo você.',
  'support.ticketClosed': 'Este chamado está encerrado. Para continuar, abra um novo.',

  'support.tab.open': 'Em aberto',
  'support.tab.mine': 'Meus',
  'support.tab.watching': 'Acompanho',
  'support.tab.unassigned': 'Sem responsável',
  'support.tab.review': 'Em avaliação',
  'support.tab.breached': 'Atrasados',
  'support.tab.all': 'Todos',

  'support.status.OPEN': 'Aberto',
  'support.status.IN_PROGRESS': 'Em andamento',
  'support.status.PENDING_CUSTOMER': 'Aguardando cliente',
  'support.status.ON_HOLD': 'Pendente',
  'support.status.RESOLVED': 'Resolvido',
  'support.status.AWAITING_REVIEW': 'Em avaliação',
  'support.status.CLOSED': 'Encerrado',
  'support.status.CANCELED': 'Cancelado',

  'support.move.OPEN': 'Reabrir',
  'support.move.IN_PROGRESS': 'Assumir',
  'support.move.PENDING_CUSTOMER': 'Aguardar cliente',
  'support.move.ON_HOLD': 'Deixar pendente',
  'support.move.RESOLVED': 'Resolver',
  'support.move.AWAITING_REVIEW': 'Enviar para avaliação',
  'support.move.CLOSED': 'Encerrar',
  'support.move.CANCELED': 'Cancelar',

  'support.priority.LOW': 'Baixa',
  'support.priority.NORMAL': 'Normal',
  'support.priority.HIGH': 'Alta',
  'support.priority.URGENT': 'Urgente',

  'support.urgency': 'Urgência',
  'support.urgency.LOW': 'Pode esperar',
  'support.urgency.MEDIUM': 'Normal',
  'support.urgency.HIGH': 'Alta',
  'support.urgency.CRITICAL': 'Crítica',

  'support.impact': 'Impacto',
  'support.impact.LOW': 'Uma pessoa',
  'support.impact.MEDIUM': 'Uma equipe',
  'support.impact.HIGH': 'Um setor',
  'support.impact.CRITICAL': 'A empresa toda',

  'support.kind': 'Tipo',
  'support.kind.INCIDENT': 'Incidente',
  'support.kind.REQUEST': 'Requisição',
  'support.kind.PROBLEM': 'Problema',
  'support.kind.CHANGE': 'Mudança',

  'support.priorityFromMatrix': 'Calculada a partir da urgência e do impacto.',
  'support.priorityPinned': 'Fixada',
  'support.priorityWillBe': 'Prioridade: {value}',
  'support.useMatrix': 'Voltar ao cálculo',
  'support.reviewDue': 'Avaliar até',

  'support.watchers': 'Observadores',
  'support.noWatchers': 'Ninguém acompanhando além de quem atende.',
  'support.addWatcher': 'Adicionar observador',
  'support.unwatch': 'Remover',

  'support.sla.MET': 'No prazo',
  'support.sla.RUNNING': 'Correndo',
  'support.sla.AT_RISK': 'Quase estourando',
  'support.sla.BREACHED': 'Estourado',
  'support.sla.PAUSED': 'Parado',
  'support.sla.NONE': 'Sem prazo',

  'support.from.MANUAL': 'Cadastro manual',
  'support.from.EMAIL': 'E-mail',
  'support.from.PORTAL': 'Portal',
  'support.from.CHAT': 'Chat',
  'support.from.FORM': 'Formulário',
  'support.from.PHONE': 'Telefone',
  'support.from.API': 'API',

  'support.event.created': 'Chamado aberto',
  'support.event.status_changed': 'Situação alterada',
  'support.event.assigned': 'Responsável alterado',
  'support.event.replied': 'Respondido ao cliente',
  'support.event.noted': 'Nota interna',
  'support.event.customer_replied': 'Cliente respondeu',
  'support.event.rated': 'Cliente avaliou',
  'support.event.sla_breached_first_response': 'Prazo de resposta estourado',
  'support.event.sla_breached_resolution': 'Prazo de solução estourado',
  'support.event.watcher_added': 'Observador adicionado',
  'support.event.watcher_removed': 'Observador removido',
  'support.event.review_expired': 'Cancelado por falta de avaliação',

  'support.kpi.breached': 'Prazo estourado',
  'support.kpi.breachedHint': 'Precisam de resposta agora.',
  'support.kpi.breachedClear': 'Nada atrasado.',
  'support.kpi.unassigned': 'Sem responsável',
  'support.kpi.unassignedHint': 'Ninguém assumiu ainda.',
  'support.kpi.open': 'Em aberto',
  'support.kpi.waiting': 'Aguardando cliente',
  'support.kpi.waitingHint': 'O relógio está parado nestes.',
  'support.kpi.review': 'Em avaliação',
  'support.kpi.reviewHint': 'Sem resposta do cliente, são cancelados.',
  'support.kpi.firstResponse': 'Primeira resposta',
  'support.kpi.median': 'Mediana do período.',
  'support.kpi.satisfaction': 'Satisfação',
  'support.kpi.resolvedToday': '{n} resolvido(s) hoje.',

  // ----- Sucesso do cliente -----
  'cs.description': 'A carteira ordenada pelo que precisa de você primeiro.',
  'cs.manageCompany': 'Gerenciar uma empresa',
  'cs.manageHint':
    'A empresa já existe no CRM. Aqui você diz quem cuida dela — valor e renovação dá para preencher depois.',
  'cs.startManaging': 'Começar a gerenciar',
  'cs.company': 'Empresa',
  'cs.pickCompany': 'Escolha uma empresa',
  'cs.account': 'Conta',
  'cs.noAccounts': 'Nenhuma conta por aqui.',
  'cs.searchPlaceholder': 'Nome da empresa',
  'cs.health': 'Saúde',
  'cs.notScored': 'Sem nota',
  'cs.notScoredYet':
    'Esta conta ainda não foi avaliada. Use "Recalcular" para gerar a primeira nota.',
  'cs.neverScored': 'Ainda sem avaliação',
  'cs.scoredAt': 'Avaliada em {when}',
  'cs.rescore': 'Recalcular',
  'cs.scoring': 'Calculando…',
  'cs.openCompany': 'Abrir a empresa',
  'cs.coverage': '{n}% dos sinais com dados',
  'cs.noData': 'sem dados',
  'cs.nothingRecorded': 'Nada registrado.',
  'cs.trend': 'Evolução',
  'cs.trendUp': 'Subiu {n} pontos desde {from}.',
  'cs.trendDown': 'Caiu {n} pontos desde {from}.',
  'cs.trendLabel': 'Variação de {n} pontos no período',
  'cs.contract': 'Contrato',
  'cs.csm': 'Responsável',
  'cs.noCsm': 'Sem responsável',
  'cs.mrr': 'Receita mensal',
  'cs.mrrMonthly': 'Receita mensal (R$)',
  'cs.since': 'Cliente desde',
  'cs.renewal': 'Renovação',
  'cs.renewalDate': 'Data da renovação',
  'cs.renewalStatus': 'Situação da renovação',
  'cs.renewalIn': 'Em {n} dias',
  'cs.renewalLate': 'Venceu há {n} dias',
  'cs.churnReason': 'Motivo da saída',
  'cs.churnReasonPlaceholder': 'Por que o cliente saiu?',
  'cs.recordChurn': 'Registrar a saída',

  'cs.tab.risk': 'Em risco',
  'cs.tab.mine': 'Minhas',
  'cs.tab.renewals': 'Renovações',
  'cs.tab.unscored': 'Sem nota',
  'cs.tab.all': 'Todas',

  'cs.band.HEALTHY': 'Saudável',
  'cs.band.WATCH': 'De olho',
  'cs.band.AT_RISK': 'Em risco',
  'cs.band.CRITICAL': 'Crítica',

  'cs.renewal.PENDING': 'Em aberto',
  'cs.renewal.AT_RISK': 'Em risco',
  'cs.renewal.RENEWED': 'Renovada',
  'cs.renewal.CHURNED': 'Saiu',

  'cs.signal.ENGAGEMENT': 'Contato',
  'cs.signal.SUPPORT': 'Atendimento',
  'cs.signal.SATISFACTION': 'Satisfação',
  'cs.signal.COMMERCIAL': 'Comercial',
  'cs.signal.ONBOARDING': 'Onboarding',

  'cs.detail.days_since_last_activity': 'último contato há (dias)',
  'cs.detail.activities': 'interações',
  'cs.detail.open_tickets': 'chamados abertos',
  'cs.detail.breached_tickets': 'prazos estourados',
  'cs.detail.tickets_in_window': 'chamados no período',
  'cs.detail.satisfaction_average': 'nota média',
  'cs.detail.nps': 'NPS',
  'cs.detail.open_opportunities': 'negócios abertos',
  'cs.detail.won': 'ganhos',
  'cs.detail.lost': 'perdidos',
  'cs.detail.steps_done': 'passos concluídos',
  'cs.detail.steps_total': 'passos no total',
  'cs.detail.steps_overdue': 'passos atrasados',

  'cs.kpi.atRiskRevenue': 'Receita em risco',
  'cs.kpi.atRiskAccounts': '{n} conta(s) em risco ou críticas.',
  'cs.kpi.renewals': 'Renovações em 30 dias',
  'cs.kpi.renewalsOverdue': '{n} já venceram.',
  'cs.kpi.renewalsClear': 'Nenhuma vencida.',
  'cs.kpi.mrr': 'Receita sob gestão',
  'cs.kpi.managed': 'Contas gerenciadas',
  'cs.kpi.unscored': '{n} ainda sem nota.',
  'cs.kpi.healthy': 'Saudáveis',
  'cs.kpi.churned': 'Saíram',
  'cs.kpi.thisQuarter': 'Neste trimestre.',

  'cs.onboarding': 'Onboarding',
  'cs.pickTemplate': 'Escolha um modelo',
  'cs.startOnboarding': 'Iniciar onboarding',
  'cs.finishOnboarding': 'Concluir',
  'cs.planProgress': '{done} de {total} passos concluídos',
  'cs.planOverdue': '{n} atrasado(s)',
  'cs.templates': 'Modelos de onboarding',
  'cs.templatesDescription':
    'A mesma lista de passos para todo cliente novo. Cada passo vira uma tarefa de verdade.',
  'cs.newTemplate': 'Novo modelo',
  'cs.templateName': 'Nome',
  'cs.templateSteps': 'Passos',
  'cs.templateStepsPlaceholder':
    'Reunião de boas-vindas @ 0\nImportar a base @ 3\nTreinamento da equipe @ 7',
  'cs.templateStepsHint': 'Um passo por linha. Use "@ n" para o dia em que ele vence.',
  'cs.saveTemplate': 'Salvar modelo',
  'cs.noTemplates': 'Nenhum modelo ainda.',
  'cs.stepCount': '{n} passo(s)',
  'cs.dueOnDay': 'Vence no dia {n}',
  'notify.event_reminder': 'Lembrete: "{title}" começa em breve.',
  'notify.event_moved': '"{title}" foi remarcado. Confirme se você continua podendo ir.',
  'notify.ticket_response_soon':
    'O chamado #{number} — "{subject}" — está perto do prazo da primeira resposta.',
  'notify.ticket_response_late':
    'O chamado #{number} — "{subject}" — passou do prazo da primeira resposta.',
  'notify.ticket_resolution_soon':
    'O chamado #{number} — "{subject}" — está perto do prazo de solução.',
  'notify.ticket_resolution_late':
    'O chamado #{number} — "{subject}" — passou do prazo de solução.',
  'notify.account_at_risk':
    'A saúde da conta {company} caiu para "em risco". Vale uma conversa esta semana.',
  'notify.webhook_disabled':
    'O webhook {name} falhou dez vezes seguidas e foi desativado. Corrija o destino e reative.',
  'notify.post_failed': 'A publicação em {account} falhou e não será tentada de novo: {reason}',
  'notify.account_critical': 'A saúde da conta {company} está crítica. Ligue para o cliente.',
  'nav.work': 'Trabalho',
  'nav.projects': 'Projetos',
  'nav.tasks': 'Tarefas',
  'nav.backlog': 'Backlog e sprints',
  'nav.calendar': 'Agenda',
  'nav.chat': 'Chat interno',
  'nav.automations': 'Automações',
  'nav.files': 'Arquivos',
  'nav.settings': 'Configurações',
  'nav.account': 'Minha conta',
  'nav.company': 'Empresa',
  'nav.team': 'Equipe e permissões',
  'nav.audit': 'Auditoria',
  'nav.sso': 'Login único',
  'nav.privacy': 'Privacidade (LGPD)',
  'admin.pickFeature': 'Escolha um recurso…',
  'admin.onlyByOverride': 'Só por liberação individual (nenhum plano inclui)',
  'admin.inSomePlan': 'Incluído em algum plano',
  'admin.featureNoPlan':
    'Nenhum plano inclui este recurso. Mudar o plano do cliente não libera — só esta liberação individual.',
  'admin.featureInPlans': 'Já vem nos planos: {plans}.',
  'nav.billing': 'Plano e assinatura',
  'nav.admin': 'Administração',

  'shell.mainNav': 'Navegação principal',
  'shell.openMenu': 'Abrir o menu',
  'shell.closeMenu': 'Fechar o menu',
  'shell.collapse': 'Recolher menu',
  'shell.search': 'Busca',
  'shell.searchPlaceholder': 'Buscar em tudo',
  'shell.notifications': 'Notificações',
  'shell.soon': 'em breve',
  'shell.appearance': 'Aparência',
  'shell.menuColour': 'Cor do menu',
  'shell.flyoutMenus': 'Abrir submenus ao lado',
  'shell.savedToProfile': 'Salvo no seu perfil, vale em qualquer dispositivo.',
  'shell.saving': 'Salvando...',
  'theme.midnight': 'Meia-noite',
  'theme.graphite': 'Grafite',
  'theme.indigo': 'Índigo',
  'theme.forest': 'Floresta',
  'theme.plum': 'Ameixa',
  'theme.ocean': 'Oceano',
  'theme.light': 'Claro',

  'common.yes': 'Sim',
  'common.no': 'Não',
  'common.createdAt': 'Criado em',
  'common.save': 'Salvar',
  'common.saving': 'Salvando...',
  'common.close': 'Fechar',

  'state.loading': 'Carregando...',
  'boundary.title': 'Esta tela travou',
  'boundary.body':
    'O resto do sistema continua funcionando. Você pode tentar de novo ou ir para outra área pelo menu.',
  'boundary.rootBody':
    'Algo falhou antes da tela abrir. Recarregue a página; se continuar, avise o suporte com a mensagem abaixo.',
  'boundary.reload': 'Recarregar a página',
  'home.storage': 'Armazenamento',
  'home.storageOf': 'de {total}',
  'home.storageUsed': 'Armazenamento usado',
  'home.planIncludes': 'O que seu plano inclui',
  'home.seePlan': 'Ver plano',
  'home.inboxUnavailable': 'Não foi possível carregar as conversas agora.',
  'billing.status.TRIALING': 'Em teste',
  'billing.status.ACTIVE': 'Ativa',
  'billing.status.PAST_DUE': 'Em atraso',
  'billing.status.CANCELED': 'Cancelada',
  'billing.status.SUSPENDED': 'Suspensa',
  'limit.landing_pages': 'Páginas de captura',
  'limit.custom_domains': 'Domínios próprios',
  'limit.automation_runs': 'Execuções de automação',
  'limit.campaigns_monthly': 'Campanhas por mês',
  'limit.api_requests_daily': 'Requisições de API por dia',
  'error.network': 'Não foi possível contatar o servidor. Verifique sua conexão.',
  'error.sessionExpired': 'Sua sessão expirou. Entre novamente.',
  'error.accountDisabled': 'Esta conta não está ativa. Fale com o suporte.',
  'error.forbidden': 'Você não tem permissão para isso.',
  'error.noSubscription': 'Esta conta não tem assinatura ativa.',
  'error.featureUnavailable': 'Este recurso não faz parte do seu plano.',
  'error.limitReached': 'Você atingiu o limite do seu plano.',
  'error.readOnly': 'Sua conta está em modo somente leitura.',
  'error.validation': 'Confira os dados informados.',
  'error.notFound': 'Não encontramos o que você procura.',
  'error.rateLimited': 'Muitas tentativas. Aguarde um momento e tente de novo.',
  'state.errorTitle': 'Não foi possível carregar',
  'state.retry': 'Tentar de novo',
  'state.requestId': 'ID da requisição',
  'state.lockedTitle': 'Este recurso não está no seu plano',
  'state.lockedBody': 'Ele existe e está pronto; falta apenas incluí-lo na sua assinatura.',
  'state.lockedAction': 'Ver planos',
  'state.notBuiltDescription':
    'A API desta área já existe e está testada; a tela ainda não foi construída. Preferimos dizer isso a mostrar um painel que finge funcionar.',
  'state.availableApi': 'Endpoints disponíveis hoje',

  'home.subtitle': 'O que precisa da sua atenção agora',
  'home.trialTitle': 'Você está no teste do plano {plan}',
  'home.trialBody': 'Restam {days} dia(s) — o teste termina em {date}.',
  'home.daysLeft': '{days} dia(s)',
  'home.openConversations': 'Conversas abertas',
  'home.openHint': 'Em todos os canais',
  'home.assignedToMe': 'Atribuídas a você',
  'home.assignedHint': 'Aguardando sua resposta',
  'home.plan': 'Plano',
  'home.needsAttention': 'Precisa de atenção',
  'home.openInbox': 'Abrir atendimento',
  'home.inboxClear': 'Nenhuma conversa aberta. Tudo em dia.',
  'home.limits': 'Uso do plano',

  'limit.users': 'Usuários',
  'limit.contacts': 'Contatos',
  'limit.storage_bytes': 'Armazenamento',
  'limit.emails_per_month': 'E-mails por mês',
  'limit.automations': 'Automações',
  'limit.whatsapp_channels': 'Canais de WhatsApp',

  'inbox.conversations': 'Conversas',
  'inbox.thread': 'Conversa',
  'inbox.view.mine': 'Minhas',
  'inbox.view.unassigned': 'Sem dono',
  'inbox.view.all': 'Todas',
  'shell.messages': 'Mensagens',
  'shell.messagesAll': 'Ver atendimento',
  'shell.messagesCustomers': 'Clientes',
  'shell.messagesTeam': 'Equipe',
  'shell.messagesEmpty': 'Nenhuma mensagem esperando resposta.',
  'inbox.transfer.title': 'Transferir',
  'inbox.transfer.body': 'Passe esta conversa para outra pessoa ou para outra fila.',
  'inbox.transfer.toPerson': 'Para uma pessoa',
  'inbox.transfer.toQueue': 'Para uma fila',
  'inbox.transfer.person': 'Quem vai atender',
  'inbox.transfer.queue': 'Fila de destino',
  'inbox.transfer.pickPerson': 'Escolha uma pessoa',
  'inbox.transfer.pickQueue': 'Escolha uma fila',
  'inbox.transfer.confirm': 'Transferir',
  'inbox.transfer.sending': 'Transferindo…',
  'inbox.status.OPEN': 'Aberta',
  'inbox.status.PENDING': 'Pendente',
  'inbox.status.SNOOZED': 'Adiada',
  'inbox.status.RESOLVED': 'Resolvida',
  'inbox.status.ALL': 'Todos os status',
  'inbox.filterStatus': 'Filtrar por status',
  'inbox.searchPlaceholder': 'Buscar por nome ou número',
  'inbox.emptyTitle': 'Nenhuma conversa aqui',
  'inbox.emptyBody':
    'Quando alguém escrever por um canal conectado, a conversa aparece nesta lista.',
  'inbox.pickTitle': 'Escolha uma conversa',
  'inbox.pickBody': 'Selecione alguém na lista ao lado para ler e responder.',
  'inbox.noMessages': 'Nenhuma mensagem nesta conversa ainda.',
  'inbox.back': 'Voltar para a lista',
  'inbox.resolve': 'Resolver',
  'inbox.reopen': 'Reabrir',
  'inbox.note': 'Nota interna',
  'inbox.privateNote': 'Nota interna (não é enviada ao cliente)',
  'inbox.replyPlaceholder': 'Escreva sua resposta — Enter envia, Shift+Enter quebra a linha',
  'inbox.notePlaceholder': 'Escreva uma nota que só a equipe vê',
  'inbox.send': 'Enviar',

  /* The omnichannel screen, redrawn (Phase 60). */
  'presence.online': 'Online agora',
  'presence.offline': 'Offline',
  'inbox.live': 'Ao vivo',
  'inbox.reconnecting': 'Reconectando…',
  'inbox.pageTitle': 'Atendimento omnichannel',
  'inbox.pageDescription': 'Todas as conversas, em um só lugar.',
  'inbox.manageChannels': 'Gerenciar canais',
  'inbox.filterChannel': 'Canal',
  'inbox.allChannels': 'Todos os canais',
  'inbox.group.handled': 'Em atendimento',
  'inbox.group.waiting': 'Aguardando atendimento',
  'inbox.waited': '{n} min',
  'inbox.reply': 'Responder',
  'inbox.via': 'Via {channel}',
  'inbox.contactDetails': 'Detalhes do contato',
  'inbox.openContact': 'Abrir ficha do contato',
  'inbox.noContact': 'Ainda não virou um contato',
  'inbox.peer': 'Origem',
  'inbox.assignee': 'Responsável',
  'inbox.unassigned': 'Sem responsável',
  // Working a conversation (Phase 139).
  'inbox.takeIt': 'Atender',
  'inbox.giveBack': 'Devolver',
  'inbox.transferToQueue': 'Transferir para fila',
  'inbox.noOtherQueue': 'Não há outra fila para onde transferir.',
  'inbox.priority': 'Prioridade',
  'inbox.tags': 'Etiquetas',
  'inbox.noTags': 'Nenhuma etiqueta.',
  // The contact panel, which slides (Phase 144).
  'inbox.openDetails': 'Ver dados do contato',
  // The four tools in the bar, and the two in the composer (Phase 146).
  'inbox.createDealHint': 'O negócio já nasce ligado a {name}.',
  'inbox.dealAmount': 'Valor (R$)',
  'inbox.dealPipeline': 'Funil',
  'inbox.dealStage': 'Etapa',
  'inbox.createTaskHint': 'A tarefa fica ligada a {name} e aparece no registro dele.',
  'inbox.taskNotes': 'Detalhes',
  'inbox.taskDue': 'Prazo',
  'inbox.taskCreated': 'Tarefa criada.',
  'inbox.summaryHint': 'Lê as últimas mensagens da conversa e escreve o que aconteceu.',
  'inbox.summaryUnavailable': 'O resumo por IA não está disponível no seu plano.',
  'inbox.summaryEmpty': 'Ainda não há um resumo desta conversa.',
  'inbox.objection': 'Sugerir resposta',
  'inbox.objectionHint':
    'Lê a conversa e o material da sua empresa e sugere uma resposta para a objeção do cliente. Nada é enviado: você decide.',
  'inbox.objectionWindow': 'Quantas mensagens considerar',
  'inbox.objectionSources': 'Baseado em:',
  'inbox.objectionNoSources':
    'Nenhum artigo da base foi usado — a sugestão não afirma preço, prazo nem garantia.',
  'inbox.objectionEmpty': 'Escolha quantas mensagens considerar e peça a sugestão.',
  'inbox.suggest': 'Gerar sugestão',
  'inbox.suggestAgain': 'Gerar outra',
  'inbox.suggesting': 'Pensando...',
  'inbox.copySuggestion': 'Copiar',
  'inbox.scheduleHint': 'A mensagem que você digitou sai na hora marcada.',
  'inbox.scheduleWhen': 'Enviar em',
  'inbox.scheduleClear': 'Tirar o agendamento',
  'inbox.fixSpelling': 'Corrigir ortografia',
  'inbox.improve': 'Melhorar o texto',
  'inbox.polishHint': 'Compare e decida. Seu texto só muda se você mandar.',
  'inbox.yourText': 'O que você escreveu',
  'inbox.suggestionLabel': 'Sugestão',
  'inbox.polishing': 'Reescrevendo...',
  'inbox.keepMine': 'Manter o meu',
  'inbox.moreArgumentative': 'Mais argumentativa',
  'inbox.useThis': 'Substituir pelo sugerido',
  'inbox.editContact': 'Editar contato',
  'inbox.contactRecord': 'Dados cadastrais',
  'inbox.contactSaved': 'Contato salvo.',
  'inbox.channelNumber': 'Número no canal',
  'inbox.extraInfo': 'Informações adicionais',
  'inbox.serviceBlock': 'Atendimento',
  'inbox.addTag': 'Nova etiqueta',
  'inbox.removeTag': 'Remover a etiqueta {tag}',
  // Named apart from `inbox.note`, which is the internal note on a *message*: one is a line in
  // the thread and the other is a line in the customer's history.
  'inbox.contactNote': 'Observação',
  'inbox.contactNotePlaceholder': 'Fica no histórico do contato, com data e autor.',
  'inbox.addContactNote': 'Salvar observação',
  'inbox.discardTitle': 'Você tem alterações não salvas',
  'inbox.discardBody': 'Se sair agora, o que você digitou será descartado.',
  'inbox.keepEditing': 'Continuar editando',
  'inbox.discard': 'Descartar',
  'inbox.contactProblem.name_required': 'O contato precisa de um nome.',
  'inbox.contactProblem.email_invalid': 'Esse e-mail não parece válido.',
  'inbox.contactProblem.document_too_short': 'Um CPF tem 11 dígitos e um CNPJ, 14.',
  'inbox.dealTitle': 'Do que se trata o negócio?',
  'inbox.taskTitle': 'O que precisa ser feito?',
  'inbox.noPipeline': 'Nenhum funil configurado para criar o negócio.',
  'inbox.nextStep': 'Próximo passo',
  'inbox.createDeal': 'Criar negócio',
  'inbox.createTask': 'Agendar tarefa',

  /* Pedidos, com as transições escritas (Phase 123). */
  'orders.title': 'Pedidos',
  'orders.description':
    'O que acontece depois que o negócio é ganho: confirmação, produção, envio e entrega.',
  'orders.number': 'Pedido',
  'orders.status': 'Situação',
  'orders.items': 'Itens',
  'orders.itemCount': '{n} itens',
  'orders.expected': 'previsto para {date}',
  'orders.total': 'Total',
  'orders.all': 'Todos',
  'orders.delete': 'Excluir rascunho',
  'orders.whyCancel': 'Por que este pedido está sendo cancelado?',
  'orders.emptyTitle': 'Nenhum pedido',
  'orders.emptyBody': 'Um pedido nasce de um negócio ganho e leva as linhas dele junto.',
  'orders.state.DRAFT': 'Rascunho',
  'orders.state.CONFIRMED': 'Confirmado',
  'orders.state.IN_PRODUCTION': 'Em produção',
  'orders.state.READY': 'Pronto',
  'orders.state.SHIPPED': 'Enviado',
  'orders.state.DELIVERED': 'Entregue',
  'orders.state.CANCELLED': 'Cancelado',
  'orders.state.RETURNED': 'Devolvido',
  'orders.move.DRAFT': 'Voltar para rascunho',
  'orders.move.CONFIRMED': 'Confirmar',
  'orders.move.IN_PRODUCTION': 'Pôr em produção',
  'orders.move.READY': 'Marcar como pronto',
  'orders.move.SHIPPED': 'Enviar',
  'orders.move.DELIVERED': 'Marcar como entregue',
  'orders.move.CANCELLED': 'Cancelar',
  'orders.move.RETURNED': 'Registrar devolução',

  /* Cadências de prospecção (Phase 122). */
  'cadences.title': 'Cadências de prospecção',
  'cadences.description':
    'A sequência de contatos que um lead recebe: e-mails que saem sozinhos e tarefas com o roteiro pronto.',
  'cadences.new': 'Nova cadência',
  'cadences.name': 'Nome',
  'cadences.create': 'Criar cadência',
  'cadences.addStep': 'Adicionar passo',
  'cadences.removeStep': 'Remover',
  'cadences.dayLabel': 'Dia',
  'cadences.kindLabel': 'Tipo',
  'cadences.channelLabel': 'Canal',
  'cadences.subject': 'Assunto do e-mail',
  'cadences.body': 'Texto do e-mail',
  'cadences.taskTitle': 'Título da tarefa',
  'cadences.script': 'Roteiro para quem for fazer',
  'cadences.placeholders':
    'Use {primeiro_nome}, {nome} ou {empresa}. Se o dado não existir, a cadência para naquela pessoa em vez de enviar a chave.',
  'cadences.day0': 'No mesmo dia',
  'cadences.day': 'Dia {n}',
  'cadences.active': 'Ativa',
  'cadences.paused': 'Pausada',
  'cadences.pause': 'Pausar',
  'cadences.resume': 'Retomar',
  'cadences.remove': 'Excluir',
  'cadences.who': 'Quem está nela',
  'cadences.nobody': 'Ninguém nesta cadência ainda.',
  'cadences.onStep': 'passo {n}',
  'cadences.stop': 'Tirar da cadência',
  'cadences.emptyTitle': 'Nenhuma cadência ainda',
  'cadences.emptyBody':
    'Uma cadência é a sequência que transforma "entrei em contato" em seis toques ao longo de três semanas.',
  'cadences.kind.EMAIL': 'E-mail',
  'cadences.kind.TASK': 'Tarefa',
  'cadences.channel.CALL': 'Ligação',
  'cadences.channel.WHATSAPP': 'WhatsApp',
  'cadences.channel.LINKEDIN': 'LinkedIn',
  'cadences.channel.OTHER': 'Outro',
  'cadences.status.ACTIVE': 'Em andamento',
  'cadences.status.FINISHED': 'Concluída',
  'cadences.status.STOPPED': 'Interrompida',
  'cadences.status.FAILED': 'Falhou',

  /* As datas de que um relacionamento é feito (Phase 121). */
  'keyDates.title': 'Datas-chave',
  'keyDates.description':
    'Aniversários, renovações, fim de contrato: os motivos de ligar para alguém antes que passem.',
  'keyDates.window': 'Janela',
  'keyDates.nextDays': 'Próximos {n} dias',
  'keyDates.now': 'Hoje e amanhã',
  'keyDates.ahead': 'Mais adiante',
  'keyDates.today': 'hoje',
  'keyDates.tomorrow': 'amanhã',
  'keyDates.inDays': 'em {n} dias',
  'keyDates.nothingToday': 'Nada para hoje nem para amanhã.',
  'keyDates.emptyTitle': 'Nenhuma data nessa janela',
  'keyDates.emptyBody':
    'Aniversários vêm da ficha do contato. Renovações e fins de contrato são cadastrados no registro.',
  'keyDates.forget': 'Remover',
  'keyDates.kind.BIRTHDAY': 'Aniversário',
  'keyDates.kind.ANNIVERSARY': 'Data comemorativa',
  'keyDates.kind.RENEWAL': 'Renovação',
  'keyDates.kind.CONTRACT_END': 'Fim de contrato',
  'keyDates.kind.CUSTOM': 'Data',

  /* Previsão de vendas e negócios que precisam de atenção (Phase 120). */
  'forecast.title': 'Previsão de vendas',
  'forecast.description':
    'O que o funil diz que vai fechar, e os negócios que estão escorregando sem ninguém notar.',
  'forecast.window': 'Período',
  'forecast.months': '{n} meses',
  'forecast.summary': 'No período',
  'forecast.byMonth': 'Mês a mês',
  'forecast.won': 'Fechado',
  'forecast.weighted': 'Ponderado',
  'forecast.weightedHint': 'Cada negócio aberto vezes a probabilidade da etapa em que ele está.',
  'forecast.bestCase': 'Otimista',
  'forecast.bestCaseHint': 'Todo negócio aberto pelo valor cheio, se nada der errado.',
  'forecast.coverage': 'Cobertura da meta',
  'forecast.coverageHint': 'Meta do período: {target}',
  'forecast.noTarget': 'Nenhuma meta mensal definida.',
  'forecast.noTargetLink': 'Definir em Configurações › Empresa',
  'forecast.emptyTitle': 'Nenhum negócio com data de fechamento no período',
  'forecast.attention': 'Negócios que precisam de atenção',
  'forecast.attentionScanned': 'Entre {n} negócios abertos.',
  'forecast.attentionEmptyTitle': 'Nada escorregando',
  'forecast.attentionEmptyBody': 'Todo negócio aberto tem próximo passo e movimento recente.',
  'forecast.idle': 'parado há {n} dias',
  'forecast.reason.OVERDUE': 'Passou da data',
  'forecast.reason.CLOSING_BLIND': 'Fecha essa semana sem próximo passo',
  'forecast.reason.STUCK': 'Travado na etapa',
  'forecast.reason.SILENT': 'Sem contato',
  'forecast.reason.NO_NEXT_STEP': 'Sem próximo passo',
  'forecast.reason.NO_CONTACT': 'Sem contato vinculado',
  'forecast.reason.UNOWNED': 'Sem responsável',

  /* Campanhas de WhatsApp (Phase 117). */
  'whatsappCampaigns.description':
    'Envie um template aprovado para uma lista de contatos, no ritmo que o seu número aguenta.',
  'whatsappCampaigns.new': 'Nova campanha',
  'whatsappCampaigns.name': 'Nome',
  'whatsappCampaigns.channel': 'Número',
  'whatsappCampaigns.template': 'Template aprovado',
  'whatsappCampaigns.audience': 'Público (visão salva de contatos)',
  'whatsappCampaigns.variable': 'Variável {n}',
  'whatsappCampaigns.placeholders':
    'Use {nome}, {primeiro_nome} ou {empresa}. Quem não tiver o dado é pulado, nunca recebe a chave.',
  'whatsappCampaigns.perMinute': 'Mensagens por minuto',
  'whatsappCampaigns.paceHint': 'Nesse ritmo, 2.000 pessoas levam cerca de {m} minutos.',
  'whatsappCampaigns.create': 'Criar rascunho',
  'whatsappCampaigns.send': 'Disparar',
  'whatsappCampaigns.cancel': 'Cancelar',
  'whatsappCampaigns.status': 'Situação',
  'whatsappCampaigns.progress': 'Enviadas',
  'whatsappCampaigns.skipped': '{n} puladas',
  'whatsappCampaigns.failed': '{n} com falha',
  'whatsappCampaigns.when': 'Agendada para',
  'whatsappCampaigns.notYet': 'Rascunho',
  'whatsappCampaigns.pace': '{n}/min',
  'whatsappCampaigns.recipients': 'Quem recebeu',
  'whatsappCampaigns.noRecipients':
    'Ninguém ainda: o público é resolvido quando a campanha começa.',
  'whatsappCampaigns.emptyTitle': 'Nenhuma campanha ainda',
  'whatsappCampaigns.emptyBody':
    'Uma campanha envia um template aprovado para uma lista de contatos.',
  'whatsappCampaigns.noChannelTitle': 'Nenhum número oficial conectado',
  'whatsappCampaigns.noChannelBody':
    'Campanhas rodam na API oficial do WhatsApp Business. O celular pareado atende conversas, mas não dispara listas.',
  'whatsappCampaigns.state.DRAFT': 'Rascunho',
  'whatsappCampaigns.state.SCHEDULED': 'Agendada',
  'whatsappCampaigns.state.SENDING': 'Enviando',
  'whatsappCampaigns.state.SENT': 'Enviada',
  'whatsappCampaigns.state.CANCELLED': 'Cancelada',
  'whatsappCampaigns.state.FAILED': 'Falhou',
  'whatsappCampaigns.recipient.PENDING': 'Na fila',
  'whatsappCampaigns.recipient.SENT': 'Enviada',
  'whatsappCampaigns.recipient.SKIPPED': 'Pulada',
  'whatsappCampaigns.recipient.FAILED': 'Falhou',

  /* What this conversation is about, on request (Phase 116). */
  'inbox.summary': 'Resumo da conversa',
  'inbox.summarise': 'Resumir com IA',
  'inbox.summaryRefresh': 'Atualizar resumo',
  'inbox.summarising': 'Resumindo...',
  'inbox.summaryStale': 'Desatualizado',
  'inbox.summaryWhen': 'Resumido {when}',

  /* Interactive messages: something to tap (Phase 57). */
  'inbox.quickReplies': 'Respostas rápidas',
  'inbox.quickRepliesClose': 'Fechar respostas rápidas',
  'inbox.quickReplyQuestion': 'Pergunta',
  'inbox.quickReplyPlaceholder': 'Podemos confirmar a visita para amanhã?',
  'inbox.quickReplyOption': 'Opção {n}',
  'inbox.quickReplyAdd': 'Mais uma opção',
  'inbox.quickReplyLimit': 'Até {n} opções, regra do WhatsApp.',
  'inbox.tapped': 'Escolheu',
  'inbox.sending': 'Enviando...',
  'inbox.sendTemplate': 'Enviar template',
  'inbox.chooseTemplate': 'Escolher template',
  'inbox.sla.breached': 'SLA estourado',
  'inbox.mediaFromDevice': 'Anexo enviado pelo aparelho — não fica guardado aqui',
  'inbox.viewerDownload': 'Baixar',
  'inbox.viewerClose': 'Fechar',
  'inbox.viewerNoPreview': 'Este arquivo não tem visualização aqui. ',
  'inbox.attachmentOpen': 'Abrir anexo',
  'inbox.attachmentRetry': 'Tentar carregar de novo',
  'inbox.failure.refused': 'O WhatsApp recusou este envio.',
  'inbox.failure.offline': 'A sessão do WhatsApp não estava conectada neste momento.',
  'inbox.failure.unpaired': 'Este canal não tem um WhatsApp conectado.',
  'inbox.failure.blocked': 'O WhatsApp bloqueou este número.',
  'inbox.failure.template': 'Este canal só envia mensagens simples, sem modelos da Meta.',
  'inbox.failure.address': 'Este endereço não é um número que o WhatsApp alcança.',
  'inbox.failure.attachment': 'Não foi possível ler o anexo desta mensagem.',
  'inbox.failure.channel': 'O canal desta conversa não existe mais.',
  'inbox.failure.window': 'A janela da API oficial estava fechada; só um modelo aprovado passaria.',
  'inbox.failure.unknown': 'Não foi possível entregar esta mensagem.',
  'inbox.deleteForCustomer': 'Apagar para o cliente',
  'inbox.deleteConfirm': 'Apagar esta mensagem também no WhatsApp do cliente?',
  'inbox.deleteConfirmShort': 'Apagar?',
  'inbox.deleted': 'Mensagem apagada',
  'inbox.byAgent': 'IA · {name}',
  'inbox.byAgentUnknown': 'Agente de IA',
  'inbox.schedule': 'Agendar',
  'inbox.scheduling': 'Agendando…',
  'inbox.scheduleFor': 'Enviar mais tarde',
  'inbox.scheduledFor': 'Agendada para {when}',
  'inbox.sign': 'Assinar',
  'inbox.transcribe': 'Transcrever áudio',
  'inbox.transcribing': 'Transcrevendo…',
  'inbox.replyTo': 'Responder esta mensagem',
  'inbox.quoteMine': 'Você',
  'inbox.quoteTheirs': 'Cliente',
  'inbox.waitingForChannel': 'aguardando o canal reconectar',
  'inbox.failureRetry': 'Tentar de novo',
  'inbox.failureRetrying': 'Enviando…',
  'inbox.windowClosed':
    'A janela de 24 horas da API oficial fechou. Fora dela a Meta só entrega um modelo aprovado. (Não vale para números conectados por QR Code.)',
  'inbox.windowOpen': 'Janela aberta — restam {minutes} minuto(s) para responder livremente.',
  'inbox.delivery.PENDING': 'Na fila',
  'inbox.delivery.SENT': 'Enviada',
  'inbox.delivery.DELIVERED': 'Entregue',
  'inbox.delivery.READ': 'Lida',
  'inbox.delivery.FAILED': 'Falhou',

  'channels.description': 'Os canais por onde seus clientes falam com você.',
  'channels.name': 'Canal',
  'channels.kind': 'Tipo',
  'channels.kind.WHATSAPP': 'WhatsApp',
  'channels.kind.SMS': 'SMS',
  'channels.kind.EMAIL': 'E-mail',
  'channels.kind.WEBCHAT': 'Chat do site',
  'channels.kind.INSTAGRAM': 'Instagram',
  'channels.kind.FACEBOOK': 'Facebook',
  'channels.kind.TELEGRAM': 'Telegram',
  'channels.providerName.whatsapp_cloud': 'API oficial da Meta',
  'channels.providerName.whatsapp_web': 'QR Code (não oficial)',
  'channels.providerName.local': 'Simulado (desenvolvimento)',
  'channels.providerName.twilio': 'Twilio',
  'channels.providerName.smtp': 'SMTP',
  'channels.providerName.meta': 'Meta',
  'channels.providerName.telegram': 'Telegram',
  'channels.providerName.webchat': 'Chat no site',
  'channels.provider': 'Conector',
  'channels.status': 'Situação',
  'inbox.stat.waiting': 'aguardando',
  'inbox.stat.handled': 'em atendimento',
  'inbox.stat.resolvedToday': 'resolvidos hoje',
  'prefs.sounds': 'Som de aviso',
  'prefs.soundsHint':
    'Toca um som curto quando chega mensagem ou notificação. O navegador só libera o som depois do primeiro clique na página.',
  'prefs.soundsOn': 'Tocar som ao receber',
  'inbox.newConversation': 'Nova conversa',
  'inbox.newConversationChannel': 'Canal',
  'inbox.newConversationChannelHint': 'Por onde a mensagem vai sair.',
  'inbox.newConversationTo': 'Para',
  'inbox.newConversationToHint': 'Número com DDI e DDD, ou o endereço usado no canal escolhido.',
  'inbox.newConversationBody': 'Mensagem',
  'inbox.newConversationBodyPlaceholder': 'Escreva a primeira mensagem…',
  'inbox.newConversationNoChannel':
    'Nenhum canal pode iniciar conversa. O WhatsApp oficial exige um modelo aprovado para falar primeiro.',
  'inbox.availability': 'Sua disponibilidade',
  'inbox.availability.AVAILABLE': 'Disponível',
  'inbox.availability.BUSY': 'Ocupado',
  'inbox.availability.AWAY': 'Ausente',
  'inbox.ready': '{count} disponíveis',
  'inbox.readyHint': 'Pessoas que se marcaram disponíveis e estão com a plataforma aberta.',
  'audit.action.inbox.availability_changed': 'Disponibilidade alterada',
  'inbox.history': 'Histórico',
  'inbox.attach': 'Anexar arquivo',
  'inbox.attaching': 'enviando…',
  'inbox.attachment': 'Anexo',
  'inbox.removeAttachment': 'Remover anexo',
  'inbox.record': 'Gravar áudio',
  'inbox.recordStop': 'Parar e anexar',
  'inbox.recordDenied': 'Não foi possível usar o microfone. Verifique a permissão do navegador.',
  'inbox.recordUnsupported': 'Este navegador não grava áudio.',
  'inbox.voiceNote': 'Áudio gravado',
  'inbox.mediaUnavailable': 'Anexo indisponível',
  // Deleted on purpose, which is not the same as broken (Phase 137).
  'inbox.mediaRemoved': 'Arquivo removido para liberar espaço',
  'inbox.event.opened': 'Conversa iniciada',
  'inbox.event.status_changed': 'Situação alterada',
  'inbox.event.handoff': 'Transferida',
  'inbox.event.handoff_requested': 'Transferência pedida',
  'inbox.event.handling': 'Atendimento alterado',
  'inbox.event.reopened': 'Conversa reaberta',
  // Two people turned out to be one: the same person writing from a number and from a LID.
  'inbox.event.identity.merged': 'Contatos unificados',
  'inbox.event.identity.resolved': 'Contato identificado',
  'inbox.today': 'Hoje',
  'inbox.yesterday': 'Ontem',

  // ----- login único e provisionamento (fase 65) -----
  'sso.description':
    'Deixe sua equipe entrar pelo provedor de identidade da empresa e mantenha o acesso em dia automaticamente.',
  'sso.connections': 'Conexões',
  'sso.connect': 'Conectar provedor',
  'sso.edit': 'Editar',
  'sso.remove': 'Remover',
  'sso.save': 'Salvar',
  'sso.label': 'Nome',
  'sso.labelPlaceholder': 'Entra ID da Acme',
  'sso.labelHint': 'É o texto que aparece no botão da tela de entrada.',
  'sso.issuer': 'Issuer',
  'sso.issuerHint':
    'O endereço que o provedor publica em /.well-known/openid-configuration. Copie exatamente como ele mostra.',
  'sso.clientId': 'Client ID',
  'sso.clientSecret': 'Client secret',
  'sso.secretKept': 'Mantido — preencha só para trocar',
  'sso.domains': 'Domínios de e-mail',
  'sso.domainsHint':
    'Só endereços nesses domínios entram por esta conexão. Sem nenhum domínio, ninguém entra.',
  'sso.noDomains': 'Sem domínio',
  'sso.autoProvision': 'Criar a conta de quem ainda não tem acesso',
  'sso.autoProvisionShort': 'Criação automática',
  'sso.autoProvisionHint':
    'Qualquer pessoa autenticada pelo provedor, dentro dos domínios acima, passa a ter acesso com esse perfil.',
  'sso.defaultRole': 'Perfil inicial',
  'sso.enabled': 'Conexão ativa',
  'sso.on': 'Ativa',
  'sso.off': 'Inativa',
  'sso.redirectUri': 'URL de retorno para cadastrar no provedor',
  'sso.emptyTitle': 'Nenhum provedor conectado',
  'sso.emptyBody':
    'Conecte o Entra ID, o Okta ou o Google Workspace da empresa para que a equipe entre sem senha própria.',
  'sso.identities': 'Quem já entrou pelo provedor',
  'sso.identitiesDescription':
    'O identificador que o provedor usa para cada pessoa. É por ele que alguém continua sendo a mesma pessoa mesmo trocando de e-mail.',
  'sso.subject': 'Identificador no provedor',
  'sso.lastLogin': 'Última entrada',
  'sso.noIdentities': 'Ninguém entrou por aqui ainda',
  'sso.enterWith': 'Entrar com {label}',
  'sso.orUseYourPassword': 'Ou use sua senha abaixo.',
  'sso.startFailed': 'Não foi possível falar com o provedor de identidade agora.',
  'sso.redirecting': 'Levando você ao provedor',
  'sso.redirectingBody': 'Aguarde um instante.',
  'sso.backToSignIn': 'Voltar para a entrada',
  'sso.error.providerRefused': 'O provedor de identidade não autorizou a entrada.',
  'sso.error.invalidCallback': 'A resposta do provedor veio incompleta. Tente novamente.',
  'sso.error.expired': 'Esta entrada expirou ou já foi concluída. Comece de novo.',
  'sso.error.changed': 'A conexão mudou durante a entrada. Comece de novo.',
  'sso.error.rejected': 'O provedor enviou uma credencial que não pôde ser aceita.',
  'sso.error.domainNotAllowed': 'Este endereço não está liberado nesta conexão.',
  'sso.error.unverifiedEmail': 'O provedor não confirmou este endereço de e-mail.',
  'sso.error.noAccount': 'Você ainda não tem acesso aqui. Peça um convite a um administrador.',
  'sso.error.accountDisabled': 'Esta conta não está ativa.',
  'sso.error.notFound': 'Esta conexão não está mais disponível.',
  'sso.error.generic': 'Não foi possível concluir a entrada pelo provedor.',

  'scim.title': 'Provisionamento automático (SCIM)',
  'scim.description':
    'O provedor cria, atualiza e desativa as pessoas por aqui. Quem sai da empresa perde o acesso na hora.',
  'scim.endpoint': 'Endereço do SCIM',
  'scim.token': 'Token',
  'scim.tokenLabel': 'Nome',
  'scim.tokenLabelPlaceholder': 'Entra ID — produção',
  'scim.newToken': 'Novo token de provisionamento',
  'scim.issue': 'Gerar token',
  'scim.tokenIssued': 'Token gerado',
  'scim.tokenOnce': 'Copie agora: este token não será mostrado outra vez.',
  'scim.copied': 'Copiei',
  'scim.defaultRole': 'Perfil inicial',
  'scim.defaultRoleHint': 'Perfil de quem for criado pelo provedor. Nunca administrador.',
  'scim.lastUsed': 'Último uso',
  'scim.neverUsed': 'Nunca usado',
  'scim.revoke': 'Revogar',
  'scim.emptyTitle': 'Nenhum token de provisionamento',
  'scim.emptyBody':
    'Gere um token e cadastre-o no provedor para que a lista de pessoas seja mantida automaticamente.',

  // ----- pedidos do titular / LGPD (fase 66) -----
  'privacy.description':
    'Responda pedidos de acesso e de exclusão de dados pessoais, com registro do que foi feito.',
  'privacy.answer': 'Responder a um pedido',
  'privacy.answerHint':
    'Informe quem pediu e por quê. A exportação traz tudo o que a sua empresa guarda sobre a pessoa.',
  'privacy.subjectKind': 'Tipo de cadastro',
  'privacy.type.CONTACT': 'Contato',
  'privacy.type.LEAD': 'Lead',
  'privacy.email': 'E-mail do titular',
  'privacy.reason': 'Motivo',
  'privacy.reasonPlaceholder': 'Pedido do titular por e-mail em 20/09',
  'privacy.reasonHint': 'Fica no comprovante. É o que mostra, depois, por que isto foi feito.',
  'privacy.export': 'Exportar dados',
  'privacy.exporting': 'Reunindo os dados…',
  'privacy.exported': 'Arquivo gerado para {name}. Nada ficou guardado no sistema.',
  'privacy.eraseTitle': 'Apagar os dados do titular',
  'privacy.eraseHint':
    'Não tem volta. Os registros comerciais da sua empresa continuam, sem apontar mais para a pessoa.',
  'privacy.confirm': 'Confirme digitando o e-mail do titular',
  'privacy.confirmPlaceholder': 'cliente@exemplo.com.br',
  'privacy.confirmHint': 'Precisa ser igual ao e-mail acima. É a última checagem antes de apagar.',
  'privacy.erase': 'Apagar definitivamente',
  'privacy.erasing': 'Apagando…',
  'privacy.erased': 'Dados de {name} apagados.',
  'privacy.history': 'Pedidos respondidos',
  'privacy.historyHint': 'O comprovante de cada pedido: quem, quando, por quê e o que foi tocado.',
  'privacy.subject': 'Titular',
  'privacy.kind': 'Pedido',
  'privacy.kind.EXPORT': 'Acesso',
  'privacy.kind.ERASURE': 'Exclusão',
  'privacy.status.COMPLETED': 'Concluído',
  'privacy.status.REFUSED': 'Recusado',
  'privacy.affected': 'Registros',
  'privacy.noRequests': 'Nenhum pedido respondido ainda',
  'privacy.noRequestsBody':
    'Quando um titular pedir acesso ou exclusão, o comprovante aparece aqui.',
  'privacy.policy': 'O que acontece com cada tipo de registro',
  'privacy.policyHint': 'Lido do próprio sistema, então é exatamente o que ele faz.',
  'privacy.excluded': 'O que continua guardado, e por quê:',
  'privacy.policy.ERASE': 'Apagado',
  'privacy.policy.ANONYMISE': 'Anonimizado',
  'privacy.policy.KEEP': 'Mantido',
  'privacy.record.contact': 'Cadastro do contato',
  'privacy.record.lead': 'Cadastro do lead',
  'privacy.record.orders': 'Pedidos',
  'privacy.record.cadenceEnrollments': 'Sequências de prospecção',
  'privacy.record.keyDates': 'Datas-chave',
  'privacy.record.activities': 'Anotações, ligações e reuniões',
  'privacy.record.conversations': 'Conversas do atendimento',
  'privacy.record.messages': 'Mensagens trocadas',
  'privacy.record.tickets': 'Chamados',
  'privacy.record.ticketMessages': 'Mensagens dos chamados',
  'privacy.record.portalLinks': 'Links de acesso ao portal',
  'privacy.record.formSubmissions': 'Respostas de formulários',
  'privacy.record.opportunities': 'Oportunidades',
  'privacy.record.proposals': 'Propostas',
  'privacy.record.campaignRecipients': 'Envios de campanha',
  'privacy.record.tags': 'Etiquetas',
  'privacy.record.notifications': 'Notificações',
  'privacy.record.emailMessages': 'E-mails enviados',
  'privacy.record.smsMessages': 'SMS enviados',
  'privacy.record.eventAttendees': 'Participação em compromissos',
  'privacy.record.consents': 'Consentimentos',

  'channels.default': 'Padrão',
  'channels.state.CONNECTED': 'Conectado',
  'channels.state.CONNECTING': 'Conectando',
  'channels.state.DISCONNECTED': 'Desconectado',
  'channels.state.DISABLED': 'Desativado',
  'channels.state.ERROR': 'Com erro',
  'channels.emptyTitle': 'Nenhum canal conectado',
  'channels.emptyBody': 'Conecte um número de WhatsApp, uma caixa de e-mail ou um widget de chat.',

  /* Unofficial WhatsApp: experimental, and the risk is the customer's number (context.md §68). */

  /* Connecting a channel: every tenant's own accounts, with their own credentials (Phase 59). */
  'channels.connect': 'Conectar canal',
  // When a channel is attended, and what it says otherwise (Phase 139).
  'channels.service': 'Atendimento',
  'channels.ownHours': 'Este canal tem horário próprio',
  'channels.inheritHours': 'Segue o horário da empresa: {from} às {to}.',
  'channels.opensAt': 'Abre às',
  'channels.closesAt': 'Fecha às',
  'channels.days': 'Dias de atendimento',
  // A week with a break in it (Phase 141).
  'channels.closed': 'fechado',
  'channels.openThisDay': 'Abrir neste dia',
  'channels.addWindow': '+ intervalo',
  'channels.removeWindow': 'Remover faixa',
  'channels.weekOverlap': 'Duas faixas do mesmo dia se sobrepõem. Ajuste os horários.',
  'channels.weekBackwards': 'Uma faixa fecha antes de abrir.',
  'channels.awayMessage': 'Mensagem fora do horário',
  'channels.awayHint':
    'Ex.: Recebemos sua mensagem! Nosso atendimento é de segunda a sexta, das 9h às 18h.',
  'channels.awayOnce': 'Enviada uma vez por conversa, não a cada mensagem.',
  'channels.waitingMessage': 'Mensagem de espera',
  'channels.waitingHint': 'Ex.: Já estamos com sua mensagem, em instantes alguém responde.',
  'channels.waitingAfter': 'Enviar depois de quantos minutos esperando',
  'channels.serviceSaved': 'Atendimento salvo.',
  'channels.providerChoice': 'Como conectar',
  'channels.nameField': 'Nome do canal',
  'channels.edit': 'Editar canal',
  'channels.save': 'Salvar canal',
  'channels.namePlaceholder': 'WhatsApp comercial',
  'channels.makeDefault': 'Usar como canal padrão',
  'channels.secretKept': '•••••• (mantém o valor atual)',
  'channels.connected': 'Canal conectado',
  'channels.connectedNoWebhook': 'Pronto. Este canal não precisa de configuração externa.',
  'channels.finishAt': 'Falta a outra metade, no painel do {provider}:',
  'channels.webhookUrl': 'URL do webhook',
  'channels.webhookUrl.hint': 'Cole em Configuração → Webhooks, no campo de URL de callback.',
  'channels.verifyToken': 'Token de verificação',
  'channels.verifyToken.once':
    'Aparece só aqui, uma vez. Se perder, será preciso conectar o canal de novo.',
  'channels.subscribeMessages':
    'No painel da Meta, assine o campo “messages”. Sem ele nada chega aqui.',
  'channels.provider.whatsapp_cloud': 'WhatsApp oficial (Cloud API da Meta)',
  'channels.provider.whatsapp_cloud.hint':
    'Número registrado como WhatsApp Business. Tem modelos aprovados, janela de 24 horas e suporte da Meta.',
  'channels.provider.whatsapp_web': 'WhatsApp não oficial (QR Code)',
  'channels.provider.whatsapp_web.hint':
    'Experimental. Conecta um WhatsApp pessoal por QR Code, sem vínculo com a Meta — o risco de bloqueio é do número.',
  'channels.provider.twilio': 'SMS (Twilio)',
  'channels.provider.twilio.hint': 'Envio e recebimento de SMS pelo número contratado na Twilio.',
  'channels.provider.local': 'Chat no site',
  'channels.provider.local.hint': 'Widget de chat hospedado por aqui. Não precisa de credenciais.',
  'channels.field.address': 'Número',
  'channels.field.address.hint': 'O número como o cliente vê, com DDI.',
  'channels.field.addressWeb.hint': 'Preenchido sozinho depois que o QR Code for lido.',
  'channels.field.phoneNumberId': 'Phone number ID',
  'channels.field.phoneNumberId.hint':
    'Meta → WhatsApp → Configuração da API. É um número longo, não o telefone.',
  'channels.field.wabaId': 'WhatsApp Business Account ID',
  'channels.field.wabaId.hint': 'Necessário para gerenciar modelos de mensagem.',
  'channels.field.accessToken': 'Token de acesso permanente',
  'channels.field.accessToken.hint':
    'Gerado a partir de um usuário de sistema. Fica criptografado e nunca volta para esta tela.',
  'channels.field.appSecret': 'App secret',
  'channels.field.appSecret.hint':
    'Meta → Configurações do app → Básico. É com ele que a assinatura de cada webhook é conferida.',
  'channels.field.verifyToken': 'Token de verificação (opcional)',
  'channels.field.verifyToken.hint': 'Se deixar em branco, geramos um e mostramos na próxima tela.',
  'channels.field.accountSid': 'Account SID',
  'channels.field.authToken': 'Auth token',
  'whatsappWeb.title': 'WhatsApp não oficial (experimental)',
  'whatsappWeb.experimental':
    'Conexão por QR Code, sem vínculo com a Meta. Use por sua conta e risco: o número é seu.',
  'whatsappWeb.unavailable': 'Não foi possível ler as sessões agora. Tente novamente em instantes.',
  // A plan feature since ADR 0041, so pointing at the plan is now true — and actionable, which it
  // was not while no plan on sale included it.
  'whatsappWeb.notOnPlan':
    'O WhatsApp não oficial não está incluído no seu plano atual. Veja os planos em Configurações › Plano — ou use o WhatsApp oficial, que é o caminho recomendado para qualquer volume.',
  'whatsappWeb.notPaired': 'Nenhum número conectado',
  'whatsappWeb.pair': 'Conectar número',
  'whatsappWeb.pairAgain': 'Conectar de novo',
  'whatsappWeb.disconnect': 'Desconectar',
  'whatsappWeb.importContacts': 'Importar contatos',
  'whatsappWeb.importing': 'Importando…',
  'whatsappWeb.imported':
    '{created} contato(s) criado(s). {existing} já existiam e não foram alterados; {skipped} sem número utilizável.',
  'whatsappWeb.importTruncated': 'Paramos no limite desta rodada — rode de novo para continuar.',
  'whatsappWeb.banned': 'Número bloqueado pelo WhatsApp. Conectar de novo não resolve.',
  'whatsappWeb.lastSeen': 'Visto {when}',
  'whatsappWeb.codeExpires': 'O código vale até {when}',
  // Pairing, said as three different things instead of one (Phase 136).
  'whatsappWeb.codeCountdown': 'O código vale por mais {left}',
  'whatsappWeb.codeExpired': 'O código expirou. Toque em Conectar número para gerar outro.',
  'whatsappWeb.syncing': 'Celular conectado. Sincronizando as conversas...',
  'whatsappWeb.syncingHint':
    'Isso leva alguns minutos na primeira conexão e acontece no servidor — pode fechar esta tela. Aguardando há {elapsed}.',
  'whatsappWeb.scan':
    'No celular: WhatsApp → Aparelhos conectados → Conectar um aparelho, e aponte para o código.',
  'whatsappWeb.simulated':
    'O serviço de sessões não está configurado, então a conexão roda em modo de desenvolvimento: ela pareia sozinha e o código não vale para nenhum celular. Para conectar um número de verdade, suba o serviço de sessões (WHATSAPP_WEB_ENGINE=remote).',
  'whatsappWeb.simulatedCode':
    'Código de desenvolvimento — nenhum celular consegue ler. Suba o serviço de sessões para gerar um QR Code de verdade.',
  'whatsappWeb.health.HEALTHY': 'Conectado',
  'whatsappWeb.health.PAIRING': 'Aguardando leitura',
  'whatsappWeb.health.SYNCING': 'Sincronizando',
  'whatsappWeb.health.STALE': 'Sem resposta há um tempo',
  'whatsappWeb.health.DOWN': 'Desconectado',
  'whatsappWeb.health.BLOCKED': 'Bloqueado',
  'whatsappWeb.riskTitle': 'Antes de conectar, leia',
  'whatsappWeb.riskChannel': 'Você vai conectar um WhatsApp ao canal “{name}”.',
  'whatsappWeb.risk.unofficial':
    'Esta conexão não é homologada pela Meta. É o mesmo mecanismo do WhatsApp Web.',
  'whatsappWeb.risk.ban':
    'O WhatsApp pode bloquear o número a qualquer momento, sem aviso e sem recurso. O número bloqueado é o seu.',
  'whatsappWeb.risk.session':
    'A sessão fica guardada criptografada e dá acesso de leitura e envio a tudo naquele WhatsApp.',
  'whatsappWeb.risk.noTemplates':
    'Não há modelos nem janela de 24 horas aqui — e também não há suporte da Meta quando algo para.',
  'whatsappWeb.risk.official':
    'Para volume, campanhas ou qualquer coisa que o seu negócio dependa, use o WhatsApp oficial.',
  'whatsappWeb.riskAccept': 'Entendi os riscos e quero conectar mesmo assim.',
  'whatsappWeb.riskConfirm': 'Gerar código de conexão',

  'queues.description': 'Onde uma conversa espera quando é do time, e não de uma pessoa.',
  'queues.name': 'Fila',
  'queues.strategy': 'Distribuição',
  'queues.strategy.MANUAL': 'Manual',
  'queues.strategy.ROUND_ROBIN': 'Rodízio',
  'queues.strategy.LEAST_BUSY': 'Menos ocupado',
  'queues.firstResponse': 'Primeira resposta',
  'queues.resolution': 'Resolução',
  'queues.businessHours': 'Só em horário comercial',
  'queues.default': 'Padrão',
  'queues.noPromise': 'Sem promessa',
  'queues.minutes': '{n} min',
  'queues.hours': '{n} h',
  'queues.emptyTitle': 'Nenhuma fila criada',
  'queues.emptyBody':
    'Uma fila define para quem a conversa vai e em quanto tempo você promete responder.',

  'templates.description':
    'Templates aprovados pela Meta — a única forma de falar com quem não escreve há mais de 24 horas.',
  'templates.name': 'Template',
  'templates.language': 'Idioma',
  'templates.category': 'Categoria',
  'templates.parameters': 'Variáveis',
  'templates.noParameters': 'Nenhuma',
  'templates.status': 'Situação',
  'templates.synced': 'Sincronizado',
  'templates.neverSynced': 'Nunca',
  'templates.filterChannel': 'Filtrar por canal',
  'templates.allChannels': 'Todos os canais',
  'templates.cat.MARKETING': 'Marketing',
  'templates.cat.UTILITY': 'Utilidade',
  'templates.cat.AUTHENTICATION': 'Autenticação',
  'templates.state.DRAFT': 'Rascunho',
  'templates.state.PENDING': 'Em análise',
  'templates.state.APPROVED': 'Aprovado',
  'templates.state.REJECTED': 'Recusado',
  'templates.state.PAUSED': 'Pausado',
  'templates.state.DISABLED': 'Desativado',
  'templates.emptyTitle': 'Nenhum template cadastrado',
  'templates.emptyBody': 'Cadastre um template e envie para aprovação da Meta.',
  'templates.noChannel': 'Conecte um canal de WhatsApp antes de cadastrar templates.',

  'crm.leadsDescription': 'Quem chegou, quem está esperando resposta e o que vira cliente.',
  'crm.contactsDescription': 'As pessoas com quem você fala.',
  'crm.opportunitiesDescription': 'Negócios em andamento e o que já foi ganho ou perdido.',
  'crm.searchPlaceholder': 'Buscar por nome',
  'crm.search': 'Buscar',
  'crm.clearSearch': 'Limpar busca',
  'crm.emptyBody': 'Nada encontrado com os filtros atuais.',
  'crm.scopedEmpty': 'Nada por aqui — para você',
  'crm.scopedEmpty.OWN':
    'Seu perfil vê apenas os registros de que você é responsável. Peça a um administrador para atribuir registros a você ou para ampliar seu perfil.',
  'crm.scopedEmpty.TEAMS':
    'Seu perfil vê apenas os registros da sua equipe. Peça a um administrador para ampliar seu perfil se precisar ver os demais.',
  'crm.scopedEmpty.NONE':
    'Seu perfil não dá acesso a estes registros. Fale com um administrador da conta.',
  'crm.noLeads': 'Nenhum lead com esses filtros.',
  'crm.noContacts': 'Nenhum contato ainda',
  'crm.noOpportunities': 'Nenhuma oportunidade ainda',
  'crm.field.contact': 'Contato',
  'crm.field.email': 'E-mail',
  // Phase 144: the contact panel edits these in place, so they need names of their own.
  'crm.field.first_name': 'Nome',
  'crm.field.last_name': 'Sobrenome',
  'crm.field.job_title': 'Cargo',
  'crm.field.document': 'CPF/CNPJ',
  'crm.field.person_type': 'Tipo de pessoa',
  'crm.field.lifecycle_stage': 'Estágio no funil',
  'crm.field.lifecycle_stage.hint':
    'É a fase deste contato no seu funil. Não cria um registro em CRM › Leads — lá ficam os contatos captados que ainda não viraram pessoa no CRM.',
  'crm.personType.INDIVIDUAL': 'Pessoa física',
  'crm.personType.COMPANY': 'Pessoa jurídica',
  'crm.field.phone': 'Telefone',
  'crm.field.status': 'Situação',
  'crm.field.source': 'Origem',
  'crm.field.score': 'Score',
  'crm.field.title': 'Oportunidade',
  'crm.field.amount': 'Valor',
  'crm.field.expectedClose': 'Fechamento previsto',
  'crm.lead.NEW': 'Novo',
  'crm.lead.CONTACTED': 'Contatado',
  'crm.lead.QUALIFIED': 'Qualificado',
  'crm.lead.UNQUALIFIED': 'Descartado',
  'crm.lead.CONVERTED': 'Convertido',
  'crm.opportunity.OPEN': 'Em aberto',
  'crm.opportunity.WON': 'Ganha',
  'crm.opportunity.LOST': 'Perdida',

  'scope.OWN': 'Próprios',
  'scope.TEAM': 'Do time',
  'scope.BUSINESS_UNIT': 'Da unidade',
  'scope.ALL': 'Todos',

  'work.projectsDescription': 'Onde o trabalho se organiza.',
  'work.tasksDescription': 'O que precisa ser feito, e por quem.',
  'work.project': 'Projeto',
  'work.task': 'Tarefa',
  'work.priority': 'Prioridade',
  'work.due': 'Prazo',
  'work.newProject': 'Criar projeto',
  'work.newTask': 'Criar tarefa',
  'work.creating': 'Criando...',
  'work.newProjectPlaceholder': 'Nome do novo projeto',
  'work.newTaskPlaceholder': 'O que precisa ser feito?',
  'work.filterProject': 'Filtrar por projeto',
  'work.filterStatus': 'Filtrar por situação',
  'work.allProjects': 'Todos os projetos',
  'work.allStatuses': 'Todas as situações',
  'work.changeStatus': 'Mudar a situação',
  'work.noProjects': 'Nenhum projeto ainda',
  'work.noProjectsBody': 'Crie o primeiro acima para começar a organizar as tarefas.',
  'work.noTasks': 'Nenhuma tarefa aqui',
  'work.noTasksBody': 'Nada pendente com os filtros atuais.',
  'work.projectDescription': 'Acompanhe a situação, o prazo e as sprints deste projeto.',
  'work.whereItStands': 'Situação',
  'work.progress': 'Andamento',
  'work.health': 'Saúde',
  'work.health.ON_TRACK': 'No prazo',
  'work.health.AT_RISK': 'Em risco',
  'work.health.OFF_TRACK': 'Atrasado',
  'work.healthNote': 'Observação',
  'work.healthNotePlaceholder': 'O que está em risco e por quê',
  'work.healthSetAt': 'Informado em {date}',
  'work.owner': 'Responsável',
  'work.noOwner': 'Sem responsável',
  'work.startDate': 'Início',
  'work.endDate': 'Fim',
  'work.doneOf': '{done} de {total} tarefas concluídas ({percent}%)',
  'work.overdueTasks': '{n} tarefa(s) atrasada(s)',
  'work.overdueShort': '{n} atrasada(s)',
  'work.noTasksYet': 'Este projeto ainda não tem tarefas.',
  'work.sprints': 'Sprints',
  'work.noSprints': 'Nenhuma sprint criada ainda.',
  'work.sprintsOff': 'Este projeto não planeja em sprints.',
  'work.turnSprintsOn': 'Ativar sprints',
  'work.addSprint': 'Nova sprint',
  'work.sprintName': 'Nome',
  'work.sprintNamePlaceholder': 'Ex.: Cadastro de clientes',
  'work.sprintGoal': 'Objetivo',
  'work.sprintGoalPlaceholder': 'O que esta sprint precisa entregar',
  'work.sprintNumber': 'Sprint {n}',
  'work.sprintState.PLANNED': 'Planejada',
  'work.sprintState.ACTIVE': 'Em andamento',
  'work.sprintState.COMPLETED': 'Concluída',
  'work.noWindow': 'Sem datas definidas',
  'work.sprintProgress': '{done}/{total} itens · {points}/{allPoints} pontos',
  'work.unestimated': '{n} item(ns) sem estimativa',
  'work.startSprint': 'Iniciar sprint',
  'work.completeSprint': 'Encerrar sprint',
  'work.sprintReport': 'Relatório',
  'work.oneSprintAtATime': 'Já existe uma sprint em andamento neste projeto.',
  'work.intoTheWork': 'Ir para o trabalho',
  'work.status.PLANNED': 'Planejado',
  'work.status.ACTIVE': 'Em andamento',
  'work.status.ON_HOLD': 'Em espera',
  'work.status.DONE': 'Concluído',
  'work.status.CANCELED': 'Cancelado',
  'work.task.TODO': 'A fazer',
  'work.status.TODO': 'A fazer',
  'work.status.IN_PROGRESS': 'Em andamento',
  'work.status.BLOCKED': 'Bloqueada',
  'work.view.board': 'Quadro',
  'work.view.list': 'Lista',
  'work.mine': 'Minhas',
  'work.columnEmpty': 'Nada aqui.',
  'work.moveBack': 'Voltar uma coluna',
  'work.moveForward': 'Avançar uma coluna',
  'work.task.IN_PROGRESS': 'Em andamento',
  'work.task.BLOCKED': 'Bloqueada',
  'work.task.DONE': 'Concluída',
  'work.task.CANCELED': 'Cancelada',
  'work.priority.LOW': 'Baixa',
  'work.priority.MEDIUM': 'Média',
  'work.priority.HIGH': 'Alta',
  'work.priority.URGENT': 'Urgente',

  // Phase 36 — sprints, item keys and the backlog.
  'work.item.EPIC': 'Épico',
  'work.item.STORY': 'História',
  'work.item.TASK': 'Tarefa',
  'work.item.BUG': 'Defeito',
  'work.item.SUBTASK': 'Subtarefa',
  'sprint.backlog': 'Backlog e sprints',
  'sprint.backlogHint': 'O que entra na próxima sprint e o que continua esperando.',
  'sprint.noProject': 'Crie um projeto para planejar o trabalho.',
  'sprint.offForProject': 'Este projeto não usa sprints.',
  'sprint.offHint':
    'As tarefas ficam no quadro, sem ciclos. Ative "usa sprints" no projeto para planejar em sprints.',
  'sprint.waiting': 'Backlog',
  'sprint.backlogEmpty': 'Nada esperando. Tudo que existe já está em uma sprint.',
  'sprint.state.PLANNED': 'Planejada',
  'sprint.state.ACTIVE': 'Em andamento',
  'sprint.state.COMPLETED': 'Encerrada',
  'sprint.new': 'Nova sprint',
  'sprint.name': 'Nome da sprint',
  'sprint.goal': 'Objetivo',
  'sprint.starts': 'Começa em',
  'sprint.ends': 'Termina em',
  'sprint.create': 'Criar sprint',
  'sprint.start': 'Iniciar',
  'sprint.complete': 'Encerrar',
  'sprint.confirmClose': 'Encerrar sprint',
  'sprint.closeHint': 'Restam {n} item(ns) em aberto. Para onde eles vão?',
  'sprint.toBacklog': 'Voltar para o backlog',
  'sprint.toSprint': 'Mover para {name}',
  'sprint.empty': 'Nenhum item nesta sprint ainda.',
  'sprint.summary': '{done} de {items} itens · {pointsDone} de {points} pontos',
  'sprint.unestimated': '{n} sem estimativa',
  'sprint.moveTo': 'Mover para',
  'sprint.points': 'Pontos',
  'sprint.pointsShort': 'pts',
  'sprint.itemTitle': 'O que precisa ser feito?',
  'sprint.addItem': 'Adicionar ao backlog',
  'sprint.report': 'Relatório',
  'sprint.burndown': 'Burndown da sprint',
  'sprint.remaining': 'Restante',
  'sprint.scope': 'Escopo',
  'sprint.ideal': 'Ideal',
  'sprint.noBurndown': 'A sprint precisa estar em andamento para desenhar o burndown.',
  'sprint.burndownDayOne': 'A linha aparece a partir do segundo dia da sprint.',
  'sprint.noLedger': 'Nada aconteceu nesta sprint ainda.',
  'sprint.ledger.ADDED': 'adicionou',
  'sprint.ledger.REMOVED': 'retirou',
  'sprint.ledger.COMPLETED': 'concluiu',
  'sprint.ledger.REOPENED': 'reabriu',
  'work.itemType': 'Tipo do item',
  'work.key': 'Chave',
  'work.usesSprints': 'Usa sprints',
  'work.filterSprint': 'Filtrar por sprint',
  'work.allSprints': 'Todas as sprints',
  'calendar.showWork': 'Tarefas e sprints',
  'calendar.sprintStart': 'Início · {name}',
  'calendar.sprintEnd': 'Fim · {name}',
  'calendar.kind.EVENT': 'Evento',
  'calendar.kind.TASK': 'Tarefa com prazo',
  'calendar.kind.SPRINT_START': 'Início de sprint',
  'calendar.kind.SPRINT_END': 'Fim de sprint',
  'work.activity': 'Atividade',
  'work.noActivity': 'Nada aconteceu nesta tarefa ainda.',
  'work.loggedTime': 'registrou {n} minutos',
  'work.changedField': '{field}: {from} → {to}',
  'work.empty': 'vazio',
  'work.someValue': 'definido',
  'work.field.title': 'Título',
  'work.field.description': 'Descrição',
  'work.field.status': 'Situação',
  'work.field.priority': 'Prioridade',
  'work.field.assigneeId': 'Responsável',
  'work.field.dueDate': 'Prazo',
  'work.field.startDate': 'Início',
  'work.field.estimatedMinutes': 'Estimativa (min)',
  'work.field.itemType': 'Tipo',
  'work.field.storyPoints': 'Pontos',
  'work.field.sprintId': 'Sprint',
  'work.field.epicId': 'Épico',
  'work.field.projectId': 'Projeto',
  'work.noProject': 'Sem projeto',
  'work.dueOn': 'Prazo: {date}',
  'work.checklist': 'Checklist',
  'work.noChecklist': 'Nenhum item ainda.',
  'work.newChecklistItem': 'Novo item',
  'work.addItem': 'Adicionar',
  'work.removeItem': 'Remover item',
  'work.time': 'Tempo',
  'work.people': 'Pessoas',
  'work.assignee': 'Responsável',
  'work.unassigned': 'Sem responsável',
  'work.watchers': 'Acompanhando',
  'work.noWatchers': 'Ninguém acompanhando ainda.',
  'work.watchThis': 'Acompanhar',
  'work.unwatch': 'Deixar de acompanhar',
  'work.addWatcher': 'Adicionar quem acompanha…',
  'work.startTimer': 'Iniciar cronômetro',
  'work.stopTimer': 'Parar',
  'work.logTime': 'Apontar',
  'work.minutes': 'Minutos',
  'work.timeNote': 'O que você fez?',
  'work.minutesShort': '{n} min',
  'work.hoursShort': '{n} h',
  'work.dependencies': 'Dependências',
  'work.dependsOn': 'Depende de',
  'work.blocks': 'Bloqueia',
  'work.comments': 'Comentários',
  'work.comment': 'Comentar',
  'work.commentPlaceholder': 'Escreva um comentário para a equipe',
  'work.noComments': 'Nenhum comentário ainda.',

  'automations.description': 'O que roda sozinho, e o que aconteceu em cada execução.',

  // Phase 38 — the automation builder.
  'automations.new': 'Nova automação',
  'automations.notInPlan': 'O plano {plan} não inclui automações.',
  'automations.create': 'Criar rascunho',
  'automations.namePlaceholder': 'Nome da automação',
  'automations.open': 'Abrir',
  'automations.save': 'Salvar rascunho',
  'automations.saving': 'Salvando...',
  'automations.builderHint': 'Quando isto acontecer, faça aquilo. Nada roda até você ativar.',
  'automations.details': 'Detalhes',
  'automations.when': 'Quando',
  'automations.then': 'Então',
  'automations.trigger': 'Gatilho',
  'automations.onlyWhen': 'Somente quando',
  'automations.noTriggerConditions': 'Sem filtro: roda em todo evento deste tipo.',
  'automations.noStepConditions': 'Adicione ao menos um teste para esta condição decidir algo.',
  'automations.noSteps': 'Nenhum passo ainda. Comece adicionando uma ação.',
  'automations.noVersions': 'Nenhuma versão publicada ainda.',
  'automations.versions': 'Versões',
  'automations.versionN': 'Versão {n}',
  'automations.published': 'publicada',
  'automations.problems': '{n} pendência(s)',
  'automations.fixFirst': 'Salve e resolva as pendências antes de ativar.',
  'automations.deleteTitle': 'Excluir esta automação?',
  'automations.deleteHint': 'As execuções já registradas continuam no histórico.',
  'automations.action': 'Ação',
  'automations.pickAction': 'Escolha uma ação…',
  'automations.pickAgent': 'Escolha um agente publicado…',
  'automations.unset': 'Não definir',
  'automations.field': 'Campo',
  'automations.otherField': 'Outro campo…',
  'automations.operator': 'Operador',
  'automations.value': 'Valor',
  'automations.match': 'Combinar',
  'automations.match.ALL': 'todas as condições',
  'automations.match.ANY': 'qualquer condição',
  'automations.addCondition': 'Adicionar condição',
  'automations.removeCondition': 'Remover condição',
  'automations.removeStep': 'Remover passo',
  'automations.moveUp': 'Mover para cima',
  'automations.moveDown': 'Mover para baixo',
  'automations.stopHint': 'Encerra a execução aqui. Nada depois deste passo roda.',
  'automations.waitAmount': 'Quantidade de espera',
  'automations.waitUnit': 'Unidade da espera',
  'automations.unit.minutes': 'minutos',
  'automations.unit.hours': 'horas',
  'automations.unit.days': 'dias',
  'automations.untilBusinessHours': 'Só em horário comercial',
  'automations.businessHoursHint': 'Usa o horário definido em Configurações › Empresa.',

  'automations.step.ACTION': 'Ação',
  'automations.step.CONDITION': 'Condição',
  'automations.step.WAIT': 'Espera',
  'automations.step.STOP': 'Parar',
  'automations.add.ACTION': '+ Ação',
  'automations.add.CONDITION': '+ Condição',
  'automations.add.WAIT': '+ Espera',
  'automations.add.STOP': '+ Parar',
  'automations.branch.then': 'Quando for verdade',
  'automations.branch.else': 'Caso contrário',

  'automations.problem.no_action': 'Escolha uma ação para este passo.',
  'automations.problem.missing_config': 'Preencha "{field}".',
  'automations.problem.empty_branch': 'Nenhum dos caminhos tem passos.',
  'automations.problem.no_conditions': 'Esta condição não testa nada.',
  'automations.problem.no_wait': 'Defina quanto tempo esperar.',

  'automations.op.equals': 'é igual a',
  'automations.op.not_equals': 'é diferente de',
  'automations.op.contains': 'contém',
  'automations.op.not_contains': 'não contém',
  'automations.op.starts_with': 'começa com',
  'automations.op.is_empty': 'está vazio',
  'automations.op.is_not_empty': 'não está vazio',
  'automations.op.greater_than': 'é maior que',
  'automations.op.less_than': 'é menor que',
  'automations.op.in': 'está entre',
  'automations.op.not_in': 'não está entre',
  'automations.op.changed_to': 'mudou para',
  'automations.op.changed_from': 'mudou de',

  'automations.action.run_agent': 'Acionar um agente de IA',
  'automations.action.add_note': 'Adicionar uma nota',
  'automations.action.add_tag': 'Adicionar uma etiqueta',
  'automations.action.create_task': 'Criar uma tarefa',
  'automations.action.emit_event': 'Emitir um evento',
  'automations.action.send_sms': 'Enviar um SMS',
  'automations.action.send_email': 'Enviar um e-mail',
  'automations.action.set_variable': 'Guardar um valor',
  'automations.action.cs_task': 'Criar uma tarefa para o CSM da conta',
  'automations.action.cs_flag_renewal': 'Marcar a renovação',

  'automations.config.agentId': 'Agente',
  'automations.config.instruction': 'Instrução',
  'automations.config.budgetCents': 'Orçamento (centavos)',
  'automations.config.body': 'Texto',
  // The e-mail action names a template the tenant owns rather than carrying its own words.
  'automations.config.template': 'Modelo de e-mail',
  'automations.config.kind': 'Tipo (MARKETING ou TRANSACTIONAL)',
  'automations.config.subject': 'Assunto',
  'automations.config.tag': 'Etiqueta',
  'automations.config.color': 'Cor',
  'automations.config.title': 'Título',
  'automations.config.description': 'Descrição',
  'automations.config.dueInDays': 'Prazo (dias a partir de hoje)',
  'automations.config.priority': 'Prioridade',
  'automations.config.ownerId': 'Responsável',
  'automations.config.eventType': 'Tipo do evento',
  'automations.config.payload': 'Conteúdo (JSON)',
  // Shared by the SMS and e-mail actions: empty means "the one on the record".
  'automations.config.to': 'Destinatário (vazio usa o do registro)',
  'automations.config.channelId': 'Canal',
  'automations.config.name': 'Nome da variável',
  'automations.config.value': 'Valor',
  'automations.config.accountId': 'Conta gerenciada',
  'automations.config.status': 'Situação',
  'automations.config.entityType': 'Agir sobre (tipo)',
  'automations.config.entityId': 'Agir sobre (id)',

  'automations.area.crm': 'CRM',
  'automations.area.sales': 'Vendas',
  'automations.area.work': 'Trabalho',
  'automations.area.inbox': 'Atendimento',
  'automations.area.marketing': 'Marketing',
  'automations.area.support': 'Suporte',
  'automations.area.forms': 'Formulários',
  'automations.area.calendar': 'Agenda',
  'automations.area.files': 'Arquivos',
  'automations.area.success': 'Sucesso do cliente',
  'automations.area.team': 'Equipe',

  'automations.trigger.cs.account.health_changed': 'Saúde da conta mudou',
  'automations.trigger.cs.account.renewal_due': 'Renovação se aproximando',
  'automations.trigger.cs.account.renewal_changed': 'Situação da renovação mudou',
  'automations.trigger.cs.account.created': 'Conta passou a ser gerenciada',

  'automations.trigger.crm.lead.created': 'Lead criado',
  'automations.trigger.crm.lead.updated': 'Lead atualizado',
  'automations.trigger.crm.lead.status_changed': 'Lead mudou de status',
  'automations.trigger.crm.lead.converted': 'Lead convertido',
  'automations.trigger.crm.contact.created': 'Contato criado',
  'automations.trigger.crm.contact.updated': 'Contato atualizado',
  'automations.trigger.crm.company.created': 'Empresa criada',
  'automations.trigger.crm.company.updated': 'Empresa atualizada',
  'automations.trigger.crm.opportunity.created': 'Oportunidade criada',
  'automations.trigger.crm.opportunity.updated': 'Oportunidade atualizada',
  'automations.trigger.crm.opportunity.stage_changed': 'Oportunidade mudou de etapa',
  'automations.trigger.sales.proposal.sent': 'Proposta enviada',
  'automations.trigger.sales.proposal.viewed': 'Proposta aberta pelo cliente',
  'automations.trigger.sales.proposal.accepted': 'Proposta aceita',
  'automations.trigger.sales.proposal.declined': 'Proposta recusada',
  'automations.trigger.work.task.created': 'Tarefa criada',
  'automations.trigger.work.task.updated': 'Tarefa atualizada',
  'automations.trigger.work.task.status_changed': 'Tarefa mudou de situação',
  'automations.trigger.work.task.commented': 'Comentário em tarefa',
  'automations.trigger.work.project.created': 'Projeto criado',
  'automations.trigger.message.inbound.received': 'Mensagem recebida',
  'automations.trigger.message.conversation.handoff_requested': 'Cliente pediu atendimento humano',
  'automations.trigger.message.conversation.resolved': 'Conversa resolvida',
  'automations.trigger.message.channel.connected': 'Canal conectado',
  'automations.trigger.form.submission.received': 'Formulário respondido',
  'automations.trigger.marketing.campaign.dispatched': 'Campanha disparada',
  'automations.trigger.page.page.published': 'Página publicada',
  'automations.trigger.support.ticket.created': 'Chamado aberto',
  'automations.trigger.support.ticket.status_changed': 'Chamado mudou de situação',
  'automations.trigger.support.ticket.replied': 'Resposta em chamado',
  'automations.trigger.calendar.booking.created': 'Agendamento criado',
  'automations.trigger.calendar.booking.canceled': 'Agendamento cancelado',
  'automations.trigger.file.object.uploaded': 'Arquivo enviado',
  'automations.trigger.identity.member.joined': 'Pessoa entrou na equipe',
  'automations.name': 'Automação',
  'automations.runs': 'Execuções',
  'automations.failures': '{n} com falha',
  'automations.failuresLabel': 'Com falha',
  'automations.lastRun': 'Última execução',
  'automations.pause': 'Pausar',
  'automations.activate': 'Ativar',
  'automations.runLog': 'Execuções',
  'automations.startedAt': 'Início',
  'automations.finishedAt': 'Fim',
  'automations.resumesAt': 'retoma em {when}',
  'automations.depth': 'Nível',
  'automations.empty': 'Nenhuma automação criada',
  'automations.emptyBody': 'Crie a primeira acima: escolha o gatilho e monte os passos.',
  'automations.noRuns': 'Nenhuma execução ainda',
  'automations.noRunsBody':
    'Assim que o gatilho disparar, cada execução aparece aqui com o resultado.',
  'automations.state.DRAFT': 'Rascunho',
  'automations.state.ACTIVE': 'Ativa',
  'automations.state.PAUSED': 'Pausada',
  'automations.state.ARCHIVED': 'Arquivada',
  'automations.run.PENDING': 'Na fila',
  'automations.run.RUNNING': 'Rodando',
  'automations.run.WAITING': 'Aguardando',
  'automations.run.COMPLETED': 'Concluída',
  'automations.run.FAILED': 'Falhou',
  'automations.run.CANCELLED': 'Cancelada',
  'automations.run.STOPPED': 'Interrompida',

  'nav.preferences': 'Preferências',

  'prefs.title': 'Preferências do Sistema',
  'prefs.description': 'Como o sistema mostra as coisas para você, e o que chega no seu e-mail.',
  'prefs.saved': 'Salvo',
  'prefs.regional': 'Região e formato',
  'prefs.timezone': 'Fuso Horário',
  'prefs.timezoneHint': 'Fuso horário usado para exibir datas e horários no sistema',
  'prefs.commonZones': 'Mais usados',
  'prefs.allZones': 'Todos os fusos',
  'prefs.language': 'Idioma',
  'prefs.languageHint': 'Idioma da interface do sistema',
  'prefs.languagePartial':
    'Esta tradução ainda está em andamento; o que faltar aparece em português.',
  'prefs.dateFormat': 'Formato de Data',
  'prefs.dateFormatHint': 'Formato usado para exibir datas no sistema',
  'prefs.timeFormat': 'Formato de Hora',
  'prefs.timeFormatHint': 'Formato usado para exibir horários',
  'prefs.time.24h': '24 horas',
  'prefs.time.12h': '12 horas',

  // Phase 40 — the tenant's own settings.
  'company.title': 'Empresa',
  'company.description': 'Onde vocês estão, o que falam e quando trabalham.',
  'company.identity': 'Identificação',
  'company.slugFixed': 'O endereço da conta não muda: links já compartilhados continuam válidos.',
  'company.regional': 'Região e idioma',
  'company.timezone': 'Fuso horário',
  'company.timezoneHint': 'Todo agendamento, prazo e disparo é calculado neste fuso.',
  'company.locale': 'Idioma padrão',
  'company.localeHint': 'O idioma dos e-mails que o sistema envia em nome da empresa.',
  'company.currency': 'Moeda',
  'company.hours': 'Horário de funcionamento',
  'company.hoursHint':
    'Usado pelos prazos de atendimento e pela espera "só em horário comercial" das automações.',
  'company.opensAt': 'Abre às',
  'company.closesAt': 'Fecha às',
  'company.days': 'Dias de funcionamento',
  'company.needADay': 'Escolha ao menos um dia.',
  'company.badRange': 'O horário de fechar precisa ser depois do de abrir.',
  /* A cara da empresa (Phase 118). */
  'company.salesTarget': 'Meta de vendas por mês',
  'company.salesTargetHint':
    'Usada pela previsão para calcular a cobertura. Deixe em branco se a empresa não trabalha com meta.',
  'company.brandingTitle': 'Identidade visual',
  'company.brandingBody':
    'O logo e a cor da sua empresa, aplicados no sistema inteiro para quem faz parte dela.',
  'company.brandLogo': 'Logo',
  'company.brandLogoHelp':
    'PNG, JPEG, WebP ou SVG, até 1 MB. Substitui a marca da Safe Cortex no menu.',
  'company.brandLogoRemove': 'Remover logo',
  'company.brandColor': 'Cor principal',
  'company.brandColorHelp':
    'Botões, destaques e o item ativo do menu. O texto por cima é escolhido automaticamente para continuar legível.',
  'company.brandColorClear': 'Usar a cor padrão',
  'company.brandCss': 'CSS personalizado',
  'company.brandCssHelp':
    'Aplicado apenas para quem é da sua empresa. Não é permitido @import nem url() externa: isso entregaria o IP de cada pessoa do time a um terceiro.',

  'company.signatureTitle': 'Assinatura nas respostas',
  'company.signatureBody':
    'Quando ligada, a resposta sai com o nome de quem escreveu. Cada pessoa pode desligar para uma mensagem específica, no próprio atendimento.',
  'company.signatureEnabled': 'Assinar as respostas por padrão',
  'company.signatureFormat': 'Formato',
  'company.signatureFormatHelp':
    'Use {name} para o nome e {team} para a equipe. Exemplo: *{name}* · {team}. A assinatura entra em uma linha antes da mensagem.',
  'company.botClosingTitle': 'Encerramento automático pelo bot',
  'company.botClosingBody':
    'Quando o cliente para de responder e o atendimento está com o bot, a conversa pode ser encerrada sozinha. Conversas que uma pessoa assumiu nunca são encerradas assim.',
  'company.botClosingWhen': 'Encerrar após',
  'company.botClosingNever': 'Nunca encerrar sozinho',
  'company.botClosing30m': '30 minutos em silêncio',
  'company.botClosing1h': '1 hora em silêncio',
  'company.botClosing4h': '4 horas em silêncio',
  'company.botClosing24h': '24 horas em silêncio',
  'company.botClosingMessage': 'Mensagem de despedida',
  'company.botClosingMessageHelp':
    'Enviada antes de encerrar. Terminar em silêncio parece que o cliente foi ignorado.',
  'company.reopenTitle': 'Conversa resolvida',
  'company.reopenBody':
    'O que acontece quando alguém escreve — ou quando você escreve — em uma conversa que já foi resolvida.',
  'company.reopenPolicy': 'Quando chega uma mensagem nova',
  'company.reopenContinue': 'Reabrir a conversa e continuar o histórico',
  'company.reopenAlwaysNew': 'Abrir sempre uma conversa nova',
  'company.reopenWindow': 'Reabrir até',
  'company.reopenWindow1h': '1 hora depois de resolvida',
  'company.reopenWindow8h': '8 horas depois de resolvida',
  'company.reopenWindow24h': '24 horas depois de resolvida',
  'company.reopenWindow3d': '3 dias depois de resolvida',
  'company.reopenWindow7d': '7 dias depois de resolvida',
  'company.reopenWindow30d': '30 dias depois de resolvida',
  'company.reopenWindowHelp':
    'Passado esse prazo, a próxima mensagem abre uma conversa nova e a antiga continua resolvida.',
  'company.saved': 'Salvo.',
  'company.save': 'Salvar',
  'company.saving': 'Salvando...',
  'company.weekday.0': 'Dom',
  'company.weekday.1': 'Seg',
  'company.weekday.2': 'Ter',
  'company.weekday.3': 'Qua',
  'company.weekday.4': 'Qui',
  'company.weekday.5': 'Sex',
  'company.weekday.6': 'Sáb',

  'prefs.emailNotifications': 'Notificações por E-mail',
  'prefs.desktopNotifications': 'Avisos na área de trabalho',
  /* Notificações que chegam no celular (Phase 119). */
  'prefs.pushNotifications': 'Notificações no celular',
  'prefs.pushHint':
    'Avisa neste aparelho mesmo com o sistema fechado. Cada navegador precisa ser autorizado uma vez.',
  'prefs.pushEnable': 'Ativar neste aparelho',
  'prefs.pushDisable': 'Desativar neste aparelho',
  'prefs.pushOn': 'Ativado neste aparelho',
  'prefs.pushBlocked':
    'Este navegador bloqueou as notificações. Só dá para liberar nas configurações do próprio navegador.',
  'prefs.pushUnsupported': 'Este navegador não suporta notificações desse tipo.',
  'prefs.pushLastSent': 'último aviso {when}',
  'prefs.desktopHint':
    'Um aviso do navegador quando algo acontece e você não está com o sistema aberto na frente — como um lembrete de reunião.',
  'prefs.desktopEnable': 'Ativar avisos',
  'prefs.desktopOn': 'Ativado',
  'prefs.desktopBlocked':
    'O navegador está bloqueando os avisos deste site. Para liberar, use as permissões do próprio navegador.',
  'prefs.desktopUnsupported': 'Este navegador não oferece avisos na área de trabalho.',

  'prefs.emailNotificationsNote':
    'O sino continua avisando de tudo. Estas opções decidem apenas o que também chega por e-mail.',
  'prefs.notify.ASSIGNMENT': 'Algo atribuído a você',
  'prefs.notify.ASSIGNMENT.hint': 'Uma tarefa, um negócio ou um atendimento que passou a ser seu',
  'prefs.notify.MENTION': 'Menções e Comentários',
  'prefs.notify.MENTION.hint': 'Quando alguém menciona você ou comenta em algo seu',
  'prefs.notify.DUE': 'Lembretes e Vencimentos',
  'prefs.notify.DUE.hint': 'Tarefas e follow-ups que estão vencendo',
  'prefs.notify.FAILED': 'Falhas no que você iniciou',
  'prefs.notify.FAILED.hint': 'Uma campanha, importação ou automação que não pôde ser concluída',
  'prefs.notify.FINISHED': 'Conclusões do que você iniciou',
  'prefs.notify.FINISHED.hint': 'Uma campanha ou importação que terminou com sucesso',
  'prefs.notify.SYSTEM': 'Avisos da plataforma',
  'prefs.notify.SYSTEM.hint': 'Mudanças de plano, limites e manutenção',

  'prefs.security': 'Segurança',
  'prefs.lastPasswordChange': 'Última Atualização de Senha',
  'prefs.passwordNeverChanged': 'Nunca alterada',
  'prefs.twoFactor': 'Autenticação em Dois Fatores (2FA)',
  'prefs.twoFactorHint': 'Adicione uma camada extra de segurança',
  'prefs.comingSoon': 'Em breve',
  'prefs.goToAccount': 'Alterar senha e ver sessões',

  'files.description': 'Tudo que sua equipe enviou, com cota e validação.',
  'files.name': 'Arquivo',
  'files.size': 'Tamanho',
  'files.visibility': 'Visibilidade',
  'files.tags': 'Etiquetas',
  'files.open': 'Abrir',
  'files.searchPlaceholder': 'Buscar por nome',
  'files.filterFolder': 'Filtrar por pasta',
  'files.allFolders': 'Todas as pastas',
  'files.empty': 'Nenhum arquivo aqui',
  'files.emptyBody':
    'Arquivos enviados em conversas, formulários e registros aparecem nesta lista.',
  'files.vis.PRIVATE': 'Privado',
  'files.vis.TENANT': 'Do tenant',
  'files.vis.PUBLIC': 'Público',
  'files.type': 'Tipo',
  'files.status': 'Situação',
  'files.status.PENDING': 'Enviando',
  'files.status.READY': 'Pronto',
  'files.status.FAILED': 'Falhou',
  'files.status.QUARANTINED': 'Em quarentena',
  'files.details': 'Detalhes do arquivo',
  'files.upload': 'Enviar arquivos',
  'files.upload.sending': 'Enviando…',
  'files.upload.done': 'Enviado',
  'files.uploadFailed': 'Não foi possível enviar.',
  'files.download': 'Baixar',
  'files.delete': 'Excluir',
  'files.deleteConfirm': 'Confirmar exclusão',
  'files.deleteFolder': 'Excluir pasta',
  'files.folders': 'Pastas',
  // The tree the product keeps for conversation media (Phase 137).
  'files.maintained': 'do sistema',
  // Share links (Phase 138).
  'share.title': 'Arquivo compartilhado',
  'share.passwordNeeded': 'Este link é protegido. Digite a senha para abrir.',
  'share.password': 'Senha',
  'share.open': 'Abrir',
  'share.download': 'Baixar',
  'share.empty': 'Não há arquivos aqui.',
  'share.expiresAt': 'Disponível até {when}',
  'share.noExpiry': 'Sem prazo de validade',
  'share.downloadsLeft': 'Downloads restantes: {n}',
  'share.expired': 'Este link expirou. Peça um novo a quem enviou.',
  'share.revoked': 'Este link foi cancelado por quem enviou.',
  'share.viewLimit': 'Este link atingiu o número de acessos permitidos.',
  'share.notFound': 'Link inválido. Confira o endereço com quem enviou.',
  // The owner's side.
  'files.share': 'Compartilhar',
  // The library, redrawn (Phase 145).
  'files.shelf.ALL': 'Todos os arquivos',
  'files.shelf.RECENT': 'Recentes',
  'files.shelf.SHARED': 'Compartilhados',
  'files.shelf.FAVOURITES': 'Favoritos',
  'files.shelf.TRASH': 'Lixeira',
  'files.select': 'Selecionar {name}',
  'files.selected': '{n} selecionados',
  'files.loose': 'Fora de pastas',
  'files.folderActions': 'Ações da pasta',
  'files.fileActions': 'Ações do arquivo',
  'files.storage': 'Armazenamento',
  'files.ofQuota': 'de',
  'files.search': 'Buscar arquivos...',
  'files.searchAction': 'Buscar',
  'files.layout': 'Formato da lista',
  'files.restore': 'Restaurar',
  'files.emptyTrash': 'A lixeira está vazia.',
  'files.favourite': 'Marcar como favorito',
  'files.unfavourite': 'Tirar dos favoritos',
  'audit.action.files.restored': 'Arquivo restaurado',
  'files.shares': 'Links compartilhados',
  'files.shareFile': 'Compartilhar arquivo',
  'files.shareFolder': 'Compartilhar esta pasta',
  'files.shareExpiry': 'Válido até',
  'files.shareMaxViews': 'Limite de acessos',
  'files.shareMaxDownloads': 'Limite de downloads',
  'files.sharePassword': 'Senha (opcional)',
  'files.shareNote': 'Para que é este link',
  'files.shareNoLimit': 'sem limite',
  'files.shareCreate': 'Gerar link',
  'files.shareCreated': 'Link gerado. Copie agora — ele não aparece de novo.',
  'files.shareCopy': 'Copiar link',
  'files.shareCopied': 'Copiado',
  'files.shareRevoke': 'Cancelar',
  'files.shareForget': 'Remover da lista',
  'files.shareRevoked': 'Cancelado',
  'files.shareNone': 'Nenhum link gerado ainda.',
  'files.shareUsage': '{views} acessos · {downloads} downloads',
  'files.shareProtected': 'com senha',
  'files.shareOpen': 'sem senha',
  'files.folder': 'Pasta',
  'files.noFolder': 'Sem pasta',
  'files.newFolder': 'Nova pasta',
  'files.rootFolder': 'Fora de pastas',
  'files.filterType': 'Filtrar por tipo',
  'files.allTypes': 'Todos os tipos',
  'files.filterTag': 'Filtrar por etiqueta',
  'files.allTags': 'Todas as etiquetas',
  'files.tagsPlaceholder': 'separadas por vírgula',
  'files.cat.image': 'Imagens',
  'files.cat.video': 'Vídeos',
  'files.cat.audio': 'Áudios',
  'files.cat.pdf': 'PDF',
  'files.cat.text': 'Texto',
  'files.fileCount': 'arquivos',
  'files.maxUpload': 'Tamanho máximo por arquivo',
  'files.acceptedTypes': 'formatos aceitos',
  'files.dropHint': 'Arraste arquivos para cá ou use o botão Enviar arquivos.',

  'domains.description': 'Seus domínios próprios, o DNS que falta e o certificado.',
  'domains.hostname': 'Domínio',
  'domains.primary': 'Principal',
  'domains.record': 'Registro DNS',
  'domains.noRecordNeeded': 'Nada pendente',
  'domains.serving': 'Servindo',
  'domains.verifyNow': 'Verificar agora',
  'domains.empty': 'Nenhum domínio conectado',
  'domains.emptyBody': 'Conecte um domínio para publicar suas páginas no seu próprio endereço.',
  'domains.phase.PENDING_DNS': 'Aguardando DNS',
  'domains.phase.VERIFYING': 'Verificando',
  'domains.phase.PENDING_CERTIFICATE': 'Emitindo certificado',
  'domains.phase.ACTIVE': 'Ativo',
  'domains.phase.SERVING': 'No ar',
  'domains.phase.FAILED': 'Com falha',

  'forms.description': 'Formulários, pesquisas e NPS — e o que cada um coletou.',
  'forms.name': 'Formulário',
  'forms.kind': 'Tipo',
  'forms.submissions': 'Respostas',
  'forms.views': 'Visualizações',
  'forms.conversion': 'Conversão',
  'forms.spam': 'Spam',
  'forms.nps': 'NPS',
  'forms.promoters': 'Promotores',
  'forms.detractors': 'Detratores',
  'forms.analyticsFor': 'Números de {name}',
  'forms.empty': 'Nenhum formulário criado',
  'forms.emptyBody': 'Crie o primeiro pelo campo acima e monte as perguntas no construtor.',
  'forms.type.FORM': 'Formulário',
  'forms.type.SURVEY': 'Pesquisa',
  'forms.type.NPS': 'NPS',
  'forms.type.CSAT': 'CSAT',
  'forms.type.QUIZ': 'Quiz',
  'forms.state.DRAFT': 'Rascunho',
  'forms.state.PUBLISHED': 'Publicado',
  'forms.state.ARCHIVED': 'Arquivado',
  'forms.new': 'Criar formulário',
  'forms.newPlaceholder': 'Nome do formulário',
  'forms.edit': 'Editar',
  'forms.backToList': 'Voltar',
  'forms.builderDescription': 'Monte as perguntas, publique e veja o que chegou.',
  'forms.tab.build': 'Construtor',
  'forms.tab.results': 'Respostas',
  'forms.settings': 'Ajustes',
  'forms.successMessage': 'Mensagem de agradecimento',
  'forms.successPlaceholder': 'Obrigado! Entraremos em contato.',
  'forms.fields': 'Perguntas',
  'forms.addField': 'Adicionar pergunta',
  'forms.noFields': 'Nenhuma pergunta ainda. Adicione a primeira.',
  'forms.fieldLabel': 'Pergunta',
  'forms.fieldLabelPlaceholder': 'Qual é o seu e-mail?',
  'forms.fieldType': 'Tipo',
  'forms.fieldKey': 'Chave',
  'forms.options': 'Opções',
  'forms.optionsPlaceholder': 'Uma por linha',
  'forms.required': 'Obrigatória',
  'forms.removeField': 'Remover',
  'forms.moveUp': 'Mover para cima',
  'forms.moveDown': 'Mover para baixo',
  'forms.incomplete': 'Toda pergunta precisa de um texto.',
  'forms.duplicateKey': 'Chave repetida',
  'forms.duplicateKeys': 'Duas perguntas com a mesma chave: uma delas se perde.',
  'forms.unsaved': 'Alterações não salvas',
  'forms.publish': 'Publicar',
  'forms.unpublish': 'Despublicar',
  'forms.publicUrl': 'Endereço público',
  'forms.noSubmissions': 'Nenhuma resposta ainda.',
  'forms.loadMore': 'Carregar mais',
  'forms.submission.RECEIVED': 'Recebida',
  'forms.submission.PROCESSED': 'Processada',
  'forms.submission.FAILED': 'Falhou',
  'forms.submission.SPAM': 'Spam',
  'forms.field.TEXT': 'Texto curto',
  'forms.field.TEXTAREA': 'Texto longo',
  'forms.field.EMAIL': 'E-mail',
  'forms.field.PHONE': 'Telefone',
  'forms.field.NUMBER': 'Número',
  'forms.field.DATE': 'Data',
  'forms.field.SELECT': 'Lista suspensa',
  'forms.field.MULTISELECT': 'Múltipla escolha',
  'forms.field.RADIO': 'Escolha única',
  'forms.field.CHECKBOX': 'Caixas de seleção',
  'forms.field.BOOLEAN': 'Sim ou não',
  'forms.field.RATING': 'Nota',
  'forms.field.NPS_SCORE': 'Nota NPS (0 a 10)',
  'forms.field.HIDDEN': 'Campo oculto',
  'forms.field.FILE': 'Arquivo',
  'forms.field.CONSENT': 'Consentimento',

  'pages.description': 'Páginas de captura e funis.',

  // Phase 39 — the landing page builder.
  'pages.new': 'Nova página',
  'pages.create': 'Criar página',
  'pages.namePlaceholder': 'Nome da página',
  'pages.edit': 'Editar',
  'pages.open': 'Ver publicada',
  'pages.slug': 'Endereço',
  'pages.kind': 'Tipo',
  'pages.visits': 'Visitas',
  'pages.conversions': 'Conversões',
  'pages.details': 'Detalhes',
  'pages.blocks': 'Blocos',
  'pages.noBlocks': 'Nenhum bloco ainda. Comece adicionando um título.',
  'templates.start': 'Ou comece de um modelo:',
  'pages.inside': 'Dentro das colunas',
  'pages.removeBlock': 'Remover bloco',
  'pages.builderHint': 'Monte a página e veja ao lado o que o visitante vai ver.',
  'pages.preview': 'Prévia',
  'pages.previewStale': 'Há alterações não salvas.',
  'pages.refresh': 'Atualizar',
  'pages.publish': 'Publicar',
  'pages.unpublish': 'Despublicar',
  'pages.fixFirst': 'Salve e preencha os campos obrigatórios antes de publicar.',
  'pages.deleteTitle': 'Excluir esta página?',
  'pages.deleteHint': 'O endereço público deixa de responder imediatamente.',
  'pages.notInPlan': 'O plano {plan} não inclui landing pages.',
  'pages.problem.required': 'Preencha "{field}".',
  'pages.pickForm': 'Escolha um formulário publicado…',
  'pages.richtextHint': 'Use **negrito**, *itálico* e [texto](https://link).',
  'pages.listHint': 'Um item por linha.',
  'pages.question': 'Pergunta',
  'pages.answer': 'Resposta',
  'pages.addItem': 'Adicionar item',
  'pages.removeItem': 'Remover item',
  'pages.align.default': 'Padrão',
  'pages.align.left': 'À esquerda',
  'pages.align.center': 'Centralizado',
  'pages.align.right': 'À direita',
  'pages.seo': 'SEO e compartilhamento',
  'pages.experiment': 'Teste A/B',
  'pages.experimentOn': 'Em andamento',
  'pages.experimentHint':
    'Divida o tráfego entre duas versões e compare. Cada visitante vê sempre a mesma.',
  'pages.needTwoVersions': 'Salve pelo menos duas versões da página para poder compará-las.',
  'pages.variantVersion': 'Versão da variante',
  'pages.variantWeight': 'Percentual de tráfego',
  'pages.variantResult': '{visits} visitas · {conversions} conversões · {rate}%',
  'pages.leading': 'à frente',
  'pages.splitMustBe100': 'A divisão precisa somar 100%. Hoje soma {total}%.',
  'pages.startExperiment': 'Iniciar teste',
  'pages.updateSplit': 'Atualizar divisão',
  'pages.seo.title': 'Título na busca',
  'pages.seo.description': 'Descrição na busca',
  'pages.seo.ogTitle': 'Título ao compartilhar',
  'pages.seo.ogDescription': 'Descrição ao compartilhar',
  'pages.seo.ogImage': 'Imagem ao compartilhar',
  'pages.seo.canonical': 'URL canônica',
  'pages.seo.noindex': 'Pedir que buscadores não indexem esta página',
  'pages.kind.LANDING': 'Landing page',
  'pages.kind.THANK_YOU': 'Obrigado',
  'pages.kind.CONTENT': 'Conteúdo',

  'blocks.HEADING': 'Título',
  'blocks.TEXT': 'Texto',
  'blocks.IMAGE': 'Imagem',
  'blocks.BUTTON': 'Botão',
  'blocks.FORM': 'Formulário',
  'blocks.VIDEO': 'Vídeo',
  'blocks.SPACER': 'Espaço',
  'blocks.DIVIDER': 'Linha',
  'blocks.TESTIMONIAL': 'Depoimento',
  'blocks.PRICING': 'Plano',
  'blocks.FAQ': 'Perguntas frequentes',
  'blocks.COLUMNS': 'Colunas',

  'blocks.prop.text': 'Texto',
  'blocks.prop.level': 'Nível',
  'blocks.prop.align': 'Alinhamento',
  'blocks.prop.src': 'Endereço do arquivo',
  'blocks.prop.alt': 'Texto alternativo',
  'blocks.prop.href': 'Link',
  'blocks.prop.width': 'Largura',
  'blocks.prop.height': 'Altura',
  'blocks.prop.label': 'Rótulo',
  'blocks.prop.style': 'Estilo',
  'blocks.prop.formId': 'Formulário',
  'blocks.prop.title': 'Título',
  'blocks.prop.quote': 'Depoimento',
  'blocks.prop.author': 'Quem disse',
  'blocks.prop.role': 'Cargo',
  'blocks.prop.avatar': 'Foto',
  'blocks.prop.name': 'Nome do plano',
  'blocks.prop.amountCents': 'Preço',
  'blocks.prop.currency': 'Moeda',
  'blocks.prop.period': 'Período',
  'blocks.prop.features': 'O que inclui',
  'blocks.prop.ctaLabel': 'Texto do botão',
  'blocks.prop.highlighted': 'Destacar este plano',
  'blocks.prop.items': 'Perguntas',
  'blocks.prop.gap': 'Espaço entre colunas',
  'pages.title': 'Página',
  'pages.publishedAt': 'Publicada em',
  'pages.empty': 'Nenhuma página criada',
  'pages.emptyBody': 'Crie a primeira acima: escolha o endereço e monte os blocos.',
  'pages.state.DRAFT': 'Rascunho',
  'pages.state.PUBLISHED': 'Publicada',
  'pages.state.ARCHIVED': 'Arquivada',

  'email.description': 'O que saiu, para quem, e o que o servidor respondeu.',
  'email.tab.messages': 'Mensagens',
  'email.tab.templates': 'Modelos',
  'email.tab.suppressions': 'Bloqueios',
  'email.recipient': 'Destinatário',
  'email.template': 'Modelo',
  'email.subject': 'Assunto',
  'email.kind': 'Tipo',
  'email.attempts': 'Tentativas',
  'email.sentAt': 'Enviado',
  'email.reason': 'Motivo',
  'email.expiresAt': 'Expira',
  'email.permanent': 'Permanente',
  'email.override': 'Personalização',
  'email.customised': 'Personalizado',
  'email.platformDefault': 'Padrão da plataforma',
  // The tenant's own wording (Phase 133).
  'email.editTemplate': 'Editar modelo',
  'email.templateName': 'Nome do modelo',
  'email.body': 'Texto do e-mail',
  'email.editHint': 'A sua versão vale só para a sua empresa.',
  'email.templatesHint':
    'Clique em um modelo para escrever a sua versão. Enquanto você não mexer, vale o texto padrão da plataforma — e ele continua recebendo melhorias.',
  'email.variablesHint':
    'Estes campos são preenchidos na hora do envio. Clique para inserir no texto; escritos errado, chegam em branco para o cliente.',
  'email.templateSaved': 'Modelo salvo. Vale a partir do próximo envio.',
  'email.preview': 'Pré-visualizar',
  'email.previewHtml': 'Como vai chegar',
  'email.previewText': 'Texto',
  'email.previewMissing': 'Ficaram em branco: {list}',
  'email.resetTemplate': 'Voltar ao padrão da plataforma',
  'email.filterStatus': 'Filtrar por situação',
  'email.allStatuses': 'Todas as situações',
  'email.noMessages': 'Nenhuma mensagem no período',
  'email.noMessagesBody': 'Toda mensagem que o sistema envia fica registrada aqui.',
  'email.noTemplates': 'Nenhum modelo disponível',
  'email.noSuppressions': 'Nenhum endereço bloqueado',
  'email.noSuppressionsBody':
    'Endereços que deram erro permanente ou pediram descadastro param de receber e aparecem aqui.',
  'email.state.QUEUED': 'Na fila',
  'email.state.SENDING': 'Enviando',
  'email.state.SENT': 'Enviada',
  'email.state.DELIVERED': 'Entregue',
  'email.state.BOUNCED': 'Devolvida',
  'email.state.COMPLAINED': 'Marcada como spam',
  'email.state.FAILED': 'Falhou',
  'email.state.SUPPRESSED': 'Bloqueada',
  'email.reason.HARD_BOUNCE': 'Endereço inexistente',
  'email.reason.SOFT_BOUNCE': 'Falha temporária',
  'email.reason.COMPLAINT': 'Marcou como spam',
  'email.reason.UNSUBSCRIBE': 'Descadastrou',
  'email.reason.MANUAL': 'Bloqueio manual',

  'account.profile': 'Seus dados',
  'account.verified': 'Confirmado',
  'account.unverified': 'Não confirmado',
  'account.resendVerification': 'Enviar link de confirmação',
  'account.verificationSent':
    'Link enviado. Abra a mensagem e clique para confirmar — vale por algumas horas.',
  'account.changePassword': 'Trocar a senha',
  'account.currentPassword': 'Senha atual',
  'account.savePassword': 'Salvar nova senha',
  'account.passwordChanged': 'Senha alterada. {n} sessão(ões) foram encerradas.',
  'account.sessions': 'Dispositivos conectados',
  'account.device': 'Dispositivo',
  'account.startedAt': 'Entrou em',
  'account.lastUsed': 'Último uso',
  'account.thisDevice': 'Este dispositivo',
  'account.revoke': 'Encerrar',
  'account.unknownDevice': 'Dispositivo desconhecido',
  'account.noSessions': 'Nenhuma sessão ativa',
  'avatar.choose': 'Escolher foto',
  'avatar.change': 'Trocar foto',
  'avatar.remove': 'Remover',
  'avatar.hint': 'JPEG, PNG ou WebP, até 2 MB. Você recorta antes de enviar.',
  'avatar.zoom': 'Aproximar',
  'avatar.dragHint': 'Arraste a imagem para enquadrar o rosto.',
  'avatar.save': 'Salvar foto',
  'avatar.saving': 'Enviando...',
  'avatar.cancel': 'Cancelar',
  'avatar.unreadable': 'Não foi possível ler esta imagem. Tente outro arquivo.',
  'error.unsupportedType': 'Este tipo de arquivo não é aceito aqui.',
  'error.tooLarge': 'O arquivo é maior do que o permitido.',

  'team.description': 'Quem pode fazer o quê, e sobre quais registros.',
  'team.roles': 'Perfis',
  'team.teams': 'Times',
  'team.role': 'Perfil',
  'team.team': 'Time',
  'team.parent': 'Time acima',
  'team.kind': 'Origem',
  'team.system': 'Do sistema',
  'team.custom': 'Personalizado',
  'team.permissions': 'Permissões',
  'team.whatCan': 'O que {role} pode fazer',
  'team.noRoles': 'Nenhum perfil configurado',
  'team.noTeams': 'Nenhum time criado',
  'team.noTeamsBody': 'Times delimitam quais registros cada pessoa enxerga.',
  'team.people': 'Pessoas',
  'team.person': 'Pessoa',
  'team.invite': 'Convidar',
  'team.inviting': 'Enviando...',
  'team.invitePlaceholder': 'E-mail de quem você quer convidar',
  'team.inviteSent': 'Convite enviado para {email}. O link vale por 7 dias.',
  'team.inviteExpires': 'Convite expira em {date}',
  'team.cancelInvite': 'Cancelar convite',
  'team.removeAccess': 'Remover acesso',
  'team.joined': 'Entrou em',
  'team.you': 'Você',
  'team.noMembers': 'Ninguém na equipe ainda',
  'team.member.ACTIVE': 'Ativo',
  'team.member.INVITED': 'Convidado',
  'team.member.SUSPENDED': 'Suspenso',
  'team.member.REMOVED': 'Removido',

  'invite.title': 'Você foi convidado',
  'invite.subtitle': 'Complete seu cadastro para entrar na equipe.',
  'invite.passwordHint':
    'Se você já tem uma conta Safe Cortex com este e-mail, pode deixar os campos em branco.',
  'invite.join': 'Entrar na equipe',
  'invite.joining': 'Entrando...',
  'invite.invalid': 'Este convite não é válido.',
  'invite.used': 'Este convite já foi aceito.',
  'invite.expired': 'Este convite expirou. Peça um novo para a equipe.',
  'invite.missingToken': 'O link está incompleto. Peça um novo convite para a equipe.',

  'audit.description': 'Quem fez o quê, quando, e de onde.',
  'audit.action': 'Ação',
  'audit.actor': 'Quem',
  'audit.ip': 'Origem',
  'audit.when': 'Quando',
  'audit.viaSupport': 'via sessão de suporte',
  'audit.filterPlaceholder': 'Filtrar por ação (ex.: form.created)',
  'audit.empty': 'Nenhum evento registrado',
  'audit.emptyBody': 'Toda ação relevante no sistema deixa um registro aqui.',
  'audit.actor.USER': 'Pessoa',
  'audit.actor.PLATFORM_ADMIN': 'Safe Cortex',
  'audit.actor.AI_AGENT': 'Agente de IA',
  'audit.actor.AUTOMATION': 'Automação',
  'audit.actor.INTEGRATION': 'Integração',
  'audit.actor.SYSTEM': 'Sistema',

  'billing.description': 'Seu plano, o que ele inclui e até quando vale.',
  'billing.invoices': 'Faturas',
  'billing.noInvoices': 'Nenhuma fatura ainda.',
  'billing.invoiceNumber': 'Fatura',
  'billing.paidAt': 'Pago em',
  'billing.paymentMethod': 'Forma de pagamento',
  'billing.plan': 'Plano',
  'billing.period': 'Período',
  'billing.amount': 'Valor',
  'billing.dueAt': 'Vencimento',
  'billing.late': '· vencida',
  'billing.howToPay':
    'As faturas são conferidas manualmente pela equipe. Envie o comprovante e a confirmação aparece aqui.',
  'billing.invoice.DRAFT': 'Rascunho',
  'billing.invoice.OPEN': 'Em aberto',
  'billing.invoice.PAID': 'Paga',
  'billing.invoice.VOID': 'Cancelada',
  'billing.invoice.UNCOLLECTIBLE': 'Não recebida',
  'billing.limits': 'Limites do plano',
  'billing.features': 'Recursos incluídos',
  'billing.trialUntil': 'Teste até {date}',
  'billing.renewsOn': 'Renova em {date}',

  'billing.changePlan': 'Mudar de plano',
  'billing.changePlanHint':
    'Ao subir de plano você passa a usar os novos recursos na hora e paga só a diferença pelos dias que faltam. Ao descer, o plano atual vale até o fim do período que você já pagou.',
  'billing.currentPlan': 'Plano atual',
  'billing.scheduledPlan': 'Agendado',
  'billing.upgradeTo': 'Mudar para este',
  'billing.downgradeTo': 'Descer para este',
  'billing.confirmChange': 'Confirmar',
  'billing.quoteNow': 'Hoje você paga {value} pelos dias que faltam.',
  'billing.quoteFree': 'Sem cobrança hoje.',
  'billing.quoteAtPeriodEnd': 'Entra em vigor no fim do período atual, sem cobrança agora.',
  'billing.blocker': '{limit}: o plano permite {allowed} e você tem {current}.',
  'billing.scheduled': 'Sua conta muda para {plan} em {date}.',
  'billing.cancelScheduled': 'Cancelar mudança',
  'billing.per.MONTHLY': '/mês',
  'billing.per.YEARLY': '/ano',
  'feature.CRM': 'CRM',
  'feature.TASKS': 'Tarefas e projetos',
  'feature.CALENDAR': 'Agenda e agendamento',
  'feature.FORMS': 'Formulários',
  'feature.NPS': 'Pesquisas e NPS',
  'feature.LANDING_PAGES': 'Páginas de captura',
  'feature.AUTOMATIONS': 'Automações',
  'feature.WHATSAPP': 'WhatsApp',
  'feature.EMAIL_MARKETING': 'E-mail marketing',
  'feature.STORAGE': 'Armazenamento',
  'feature.WEBHOOKS': 'Webhooks',
  'feature.CONTRACTS': 'Propostas e contratos',
  'feature.SOCIAL_SCHEDULER': 'Agendador de redes sociais',
  'feature.WHATSAPP_OFFICIAL': 'WhatsApp oficial (API da Meta)',
  'feature.WHATSAPP_WEB': 'WhatsApp Web (experimental)',
  'feature.SMS': 'SMS',
  'feature.EMAIL': 'E-mail transacional',
  'feature.AI_AGENTS': 'Agentes de IA',
  'feature.ADS_MANAGER': 'Gestor de anúncios',
  'feature.CUSTOM_DOMAIN': 'Domínio próprio',
  'feature.SSO': 'Login único (SSO)',
  'feature.CALENDAR_SYNC': 'Sincronizar com o Google Agenda',
  'feature.API': 'API e chaves de acesso',

  'admin.description': 'Os tenants da plataforma e os planos que eles assinam.',
  'admin.tab.tenants': 'Tenants',
  'admin.tab.plans': 'Planos',
  'admin.tenant': 'Tenant',
  'admin.plan': 'Plano',
  'admin.price': 'Preço',
  'admin.trialDays': 'Dias de teste',
  'admin.trialEnds': 'Teste termina',
  'admin.users': 'Usuários',
  'admin.default': 'Padrão',
  'admin.inactive': 'Inativo',
  'admin.private': 'Não público',
  'admin.filterStatus': 'Filtrar por situação',
  'admin.allStatuses': 'Todas as situações',
  'admin.searchPlaceholder': 'Buscar por nome ou slug',
  'admin.noTenants': 'Nenhum tenant encontrado',
  'admin.noPlans': 'Nenhum plano cadastrado',
  'admin.state.ACTIVE': 'Ativo',
  'admin.state.READ_ONLY': 'Somente leitura',
  'admin.state.SUSPENDED': 'Suspenso',
  'admin.state.DELETED': 'Excluído',
  'admin.period.MONTHLY': 'por mês',
  'admin.period.YEARLY': 'por ano',
  'admin.subscription': 'Assinatura',
  'admin.actions': 'Ações',
  'admin.suspend': 'Suspender',
  'admin.suspendTitle': 'Suspender este tenant?',
  'admin.suspendBody':
    'Ninguém de {name} conseguirá entrar enquanto estiver suspenso. Eles verão a conta bloqueada.',
  'admin.makeReadOnly': 'Deixar só leitura',
  'admin.readOnlyTitle': 'Colocar em somente leitura?',
  'admin.readOnlyBody': 'A equipe continua entrando e vendo tudo, mas não consegue alterar nada.',
  'admin.restore': 'Reativar',
  'admin.restoreTitle': 'Reativar este tenant?',
  'admin.changePlan': 'Mudar de plano',
  'admin.changePlanTitle': 'Mudar o plano deste tenant?',
  'admin.extendTrial': 'Estender teste',
  'admin.extendTrialTitle': 'Estender o período de teste?',
  'admin.days': 'Dias a acrescentar',
  'admin.currentTrialEnds': 'O teste atual termina em {date}.',
  'admin.setSubscription': 'Situação da assinatura',
  'admin.setSubscriptionTitle': 'Alterar a situação da assinatura?',
  'admin.openSupport': 'Abrir sessão de suporte',
  'admin.openSupportTitle': 'Abrir uma sessão de suporte?',
  'admin.openSupportBody':
    'O tenant enxerga esta sessão enquanto ela estiver aberta, e tudo que você fizer fica no registro de auditoria dele.',
  'admin.readOnlySession': 'Somente leitura (recomendado)',
  'admin.durationMinutes': 'Duração em minutos',
  'admin.supportSessions': 'Sessões de suporte',
  'admin.noSupportSessions': 'Nenhuma sessão de suporte neste tenant.',
  'admin.sessionOpen': 'Aberta',
  'admin.sessionEnded': 'Encerrada',
  'admin.readOnly': 'Só leitura',
  'admin.readWrite': 'Leitura e escrita',
  'admin.endSession': 'Encerrar',
  'admin.supportToken': 'Token da sessão de suporte',
  'admin.supportTokenHint':
    'Copie agora: ele aparece uma única vez e dá acesso ao tenant até a sessão expirar.',
  'admin.hideToken': 'Já copiei',
  'admin.overrides': 'Exceções de plano',
  'admin.noOverrides': 'Nenhuma exceção ativa.',
  'admin.invoices': 'Faturas',
  'admin.tab.numbers': 'Números',
  'admin.tab.invoices': 'Faturas',
  'admin.tab.product': 'Produto',
  'admin.tab.feedback': 'Relatos',
  'admin.tab.ai': 'Preços de IA',
  // The rate card (Phase 140).
  'admin.aiRates.title': 'Quanto custa cada modelo',
  'admin.aiRates.description':
    'Digite o preço em dólares, como o provedor publica, e o câmbio do dia. O sistema guarda o valor em reais e a margem separada do custo.',
  'admin.aiRates.provider': 'Provedor',
  'admin.aiRates.model': 'Modelo',
  'admin.aiRates.inputUsd': 'Entrada (US$ por milhão de tokens)',
  'admin.aiRates.outputUsd': 'Saída (US$ por milhão de tokens)',
  'admin.aiRates.fx': 'Câmbio (R$ por US$)',
  'admin.aiRates.markup': 'Margem (%)',
  'admin.aiRates.preview':
    'O cliente vai pagar {in} por milhão de tokens de entrada e {out} de saída.',
  'admin.aiRates.save': 'Salvar preço',
  'admin.aiRates.row': 'Entrada {in} · saída {out} · margem {markup}%',
  'admin.aiRates.at': 'câmbio R$ {fx}',
  'admin.aiRates.edit': 'Editar',
  'admin.aiRates.retire': 'Aposentar',
  'admin.aiRates.retired': 'aposentado',
  'admin.aiRates.unpriced':
    'Modelos já usados que ninguém precificou — cada chamada está sendo cobrada R$ 0,00:',
  'admin.aiRates.unpricedCalls': '{n} chamadas',
  'admin.aiRates.priceIt': 'Precificar',
  'audit.action.platform.ai_rate_saved': 'Preço de modelo de IA alterado',
  'audit.action.platform.ai_rate_retired': 'Preço de modelo de IA aposentado',
  'admin.feedback.title': 'O que os clientes disseram',
  'admin.feedback.description':
    'Problemas e ideias enviados de dentro do produto, de todos os tenants.',
  'admin.feedback.empty': 'Nada por aqui.',
  'admin.feedback.all': 'Todos',
  'admin.feedback.status.NEW': 'Novo',
  'admin.feedback.status.READ': 'Lido',
  'admin.feedback.status.PLANNED': 'Planejado',
  'admin.feedback.status.DONE': 'Feito',
  'admin.feedback.status.DECLINED': 'Não vamos fazer',
  'admin.product.window': 'Janela de {n} dias',
  'admin.product.tenants': 'Tenants',
  'admin.product.activeTenants': '{n} com atividade',
  'admin.product.activated': 'Ativados',
  'admin.product.funnel': 'Ativação',
  'admin.product.funnelHint':
    'Um tenant está ativado quando cumpre três dos cinco primeiros passos. Cada barra é a fatia de todos os tenants.',
  'admin.product.adoption': 'Adoção por área',
  'admin.product.adoptionHint': 'Fatia dos tenants com atividade que usou cada área na janela.',
  'admin.product.cohorts': 'Retenção por mês de entrada',
  'admin.product.cohortsHint':
    'M0 é o mês da entrada; cada célula é a fatia do grupo com alguma atividade naquele mês. Um mês que ainda não chegou fica em branco.',
  'admin.product.noCohorts': 'Nenhum tenant no período.',
  'admin.product.cohort': 'Entrada',
  'admin.product.size': 'Tenants',
  'admin.product.list': 'Todos os tenants, os mais quietos primeiro',
  'admin.product.allBands': 'Todos',
  'admin.product.health': 'Saúde',
  'admin.product.people': 'Pessoas ativas',
  'admin.product.activeDays': 'Dias ativos',
  'admin.product.areas': 'Áreas',
  'admin.product.milestones': 'Primeiros passos',
  'admin.product.lastSeen': 'Última atividade',
  'admin.product.never': 'nunca',
  'admin.product.usage': 'Uso do produto',
  'admin.band.NEW': 'Novo',
  'admin.band.THRIVING': 'Engajado',
  'admin.band.HEALTHY': 'Saudável',
  'admin.band.QUIET': 'Quieto',
  'admin.band.DORMANT': 'Parado',
  'admin.milestone.TEAM': 'Convidou alguém',
  'admin.milestone.CRM': 'Cadastrou um lead ou contato',
  'admin.milestone.PIPELINE': 'Abriu um negócio',
  'admin.milestone.INBOX': 'Conectou um canal ou respondeu',
  'admin.milestone.AUTOMATION': 'Publicou uma automação',
  'nav.usage': 'Uso da conta',
  'usage.title': 'Uso da conta',
  'usage.description':
    'Quem usou o quê nos últimos {n} dias — os mesmos números que a Safe Cortex vê sobre a sua conta.',
  'usage.activePeople': 'Pessoas ativas',
  'usage.activeDays': 'Dias com atividade',
  'usage.health': 'Saúde da conta',
  'usage.healthParts': 'atividade {a} · abrangência {b} · pessoas {c}',
  'usage.byDay': 'Ações por dia',
  'usage.firstSteps': 'Primeiros passos',
  'usage.activated': 'Conta ativada: três dos cinco passos cumpridos.',
  'usage.notActivated': 'Cumpra três destes cinco passos e a conta está ativada.',
  'usage.milestone.TEAM': 'Convidar alguém da equipe',
  'usage.milestone.CRM': 'Cadastrar um lead, contato ou empresa',
  'usage.milestone.PIPELINE': 'Abrir um negócio no funil',
  'usage.milestone.INBOX': 'Conectar um canal ou responder uma conversa',
  'usage.milestone.AUTOMATION': 'Publicar uma automação',
  'usage.areas': 'Áreas usadas',
  'usage.nothingYet': 'Nenhuma atividade registrada ainda.',
  'usage.byPeople': 'por {n} pessoa(s)',
  'usage.people': 'Pessoas',
  'usage.person': 'Pessoa',
  'usage.actions': 'Ações',
  'usage.areasUsed': 'Áreas',
  'usage.lastActive': 'Última atividade',
  'usage.inactive': 'sem atividade na janela',
  'usage.area.account': 'Conta',
  'usage.area.team': 'Equipe',
  'usage.area.crm': 'CRM',
  'usage.area.sales': 'Vendas',
  'usage.area.inbox': 'Atendimento',
  'usage.area.marketing': 'Marketing',
  'usage.area.automation': 'Automações',
  'usage.area.ai': 'Agentes de IA',
  'usage.area.work': 'Trabalho',
  'usage.area.calendar': 'Agenda',
  'usage.area.support': 'Suporte',
  'usage.area.cs': 'Sucesso do cliente',
  'usage.area.files': 'Arquivos',
  'usage.area.social': 'Redes sociais',
  'usage.area.chat': 'Chat interno',
  'admin.lastMonths': '{n} meses',
  'admin.mrr': 'MRR',
  'admin.arr': 'ARR',
  'admin.arpu': 'Receita por conta',
  'admin.ltv': 'Valor do cliente',
  'admin.ltvUnknown': 'Sem churn no período para calcular.',
  'admin.payingAccounts': '{n} conta(s) pagando',
  'admin.retention': 'Retenção',
  'admin.grr': 'Retenção bruta',
  'admin.grrHint': 'Quanto da receita anterior sobreviveu',
  'admin.nrr': 'Retenção líquida',
  'admin.nrrHint': 'Com expansão; acima de 100% cresce sem vender',
  'admin.logoChurn': 'Churn de contas',
  'admin.revenueChurn': 'Churn de receita',
  'admin.retentionBasis':
    'Base: {starting} de {accounts} conta(s) no início · perdeu {churned} de {lost} conta(s)',
  'admin.retentionCaveat':
    'Expansão e contração passam a aparecer depois do primeiro retrato mensal, gravado no início de cada mês.',
  'admin.movement':
    'Desde {month}: {expansion} de expansão · {contraction} de contração · {new} de receita nova',
  'admin.trials': 'Testes',
  'admin.trialConversion': 'Conversão de teste',
  'admin.trialsBasis': '{converted} de {ended} teste(s) encerrado(s)',
  'admin.funnel': 'Assinaturas por situação',
  'admin.subscription.TRIALING': 'Em teste',
  'admin.subscription.ACTIVE': 'Ativa',
  'admin.subscription.PAST_DUE': 'Em atraso',
  'admin.subscription.SUSPENDED': 'Suspensa',
  'admin.subscription.CANCELED': 'Cancelada',
  'admin.subscription.EXPIRED': 'Expirada',
  'admin.byMonth': 'Mês a mês',
  'admin.month': 'Mês',
  'admin.invoiced': 'Faturado',
  'admin.collected': 'Recebido',
  'admin.newTenants': 'Novos',
  'admin.churned': 'Saíram',
  'admin.noHistory': 'Ainda não há histórico.',
  'admin.byPlan': 'Receita por plano',
  'admin.noPaidPlans': 'Nenhuma conta em plano pago.',
  'admin.planAccounts': '{n} conta(s)',
  'admin.platformUsage': 'A plataforma hoje',
  'admin.usage.tenants': 'Contas',
  'admin.usage.active_tenants': 'Contas ativas',
  'admin.usage.users': 'Usuários',
  'admin.usage.open_invoices': 'Faturas em aberto',
  'admin.usage.storage_bytes': 'Armazenamento',
  'admin.usageCaveat':
    'Só o que o plano de controle possui. Registros de CRM não são contados aqui: o painel não lê dados de cliente.',
  'admin.allInvoices': 'Todas',
  'admin.noInvoices': 'Nenhuma fatura.',
  'admin.owed': '{n} em aberto · {total}',
  'admin.confirmPayment': 'Confirmar pagamento',
  'admin.confirmPaymentTitle': 'Confirmar que o pagamento chegou?',
  'admin.confirmPaymentBody':
    'Marca a fatura de {amount} como paga e reativa a conta. Fica registrado quem confirmou.',
  'admin.paymentMethod': 'Como foi pago',
  'admin.paymentReference': 'Referência',
  'admin.paymentReferenceHint': 'ID da transação, end-to-end do PIX, nº do comprovante',
  'admin.method.PIX': 'PIX',
  'admin.method.TRANSFER': 'Transferência',
  'admin.method.BOLETO': 'Boleto',
  'admin.method.CARD': 'Cartão',
  'admin.method.OTHER': 'Outro',
  'admin.voidInvoice': 'Cancelar fatura',
  'admin.voidInvoiceTitle': 'Cancelar esta fatura?',
  'admin.voidInvoiceBody': 'A fatura deixa de ser devida. Não reativa a conta.',
  'admin.newFeatureOverride': 'Liberar recurso',
  'admin.newLimitOverride': 'Ajustar limite',
  'admin.featureOverrideBody':
    'Libera (ou bloqueia) um recurso para este tenant, independentemente do plano.',
  'admin.limitOverrideBody': 'Muda um limite do plano só para este tenant.',
  'admin.featureKey': 'Recurso',
  'admin.featureState': 'Estado',
  'admin.featureOn': 'Liberado',
  'admin.featureOff': 'Bloqueado',
  'admin.limitKey': 'Limite',
  'admin.limitValue': 'Novo valor',
  'admin.limitUnlimited': 'sem limite',
  'admin.limitValueHint': 'Deixe em branco para remover o teto.',
  'admin.grantOverride': 'Aplicar',
  'admin.overrideExpiry': 'Validade',
  'admin.overrideNoExpiry': 'Até ser revogado',
  'admin.dayCount': 'dias',
  'admin.revokeOverride': 'Revogar',
  'admin.revokeOverrideTitle': 'Revogar esta exceção?',
  'admin.history': 'Histórico da assinatura',

  'confirm.reason': 'Motivo',
  'confirm.reasonPlaceholder': 'Por que você está fazendo isso?',
  'confirm.reasonHint': 'O motivo fica no registro de auditoria e o tenant pode lê-lo.',
  'confirm.cancel': 'Cancelar',
  'confirm.working': 'Aplicando...',

  'calendar.description': 'Seus compromissos e o que agendaram com você.',
  'calendar.today': 'Hoje',
  'calendar.more': '+{n}',
  'calendar.newEvent': 'Criar compromisso',
  'calendar.newEventPlaceholder': 'Título do compromisso',
  'calendar.newEventHint': 'Começa na próxima hora cheia e dura uma hora.',
  'calendar.bookings': 'Agendamentos recebidos',
  'calendar.links': 'Links de agendamento',
  'calendar.invitee': 'Quem agendou',
  'calendar.when': 'Quando',
  'calendar.active': 'Ativo',
  'calendar.inactive': 'Inativo',
  'calendar.noBookings': 'Nenhum agendamento ainda',
  'calendar.noBookingsBody': 'Quando alguém marcar por um link público, aparece aqui.',
  'calendar.noLinks': 'Nenhum link de agendamento',
  'calendar.noLinksBody': 'Um link público deixa qualquer pessoa marcar nos seus horários livres.',
  'calendar.booking.CONFIRMED': 'Confirmado',
  'calendar.booking.CANCELED': 'Cancelado',
  'calendar.booking.RESCHEDULED': 'Remarcado',
  'calendar.tab.calendar': 'Agenda',
  'calendar.tab.availability': 'Disponibilidade',
  'calendar.tab.when': 'Quando',
  'calendar.tab.who': 'Quem e onde',
  'calendar.tab.more': 'Detalhes',

  'calendar.create': 'Criar',
  'calendar.previous': 'Anterior',
  'calendar.next': 'Próximo',
  'calendar.view': 'Visualização',
  'calendar.view.DAY': 'Dia',
  'calendar.view.WORK_WEEK': 'Semana útil',
  'calendar.view.WEEK': 'Semana',
  'calendar.view.MONTH': 'Mês',
  'calendar.view.YEAR': 'Ano',
  'calendar.view.SCHEDULE': 'Programação',

  'calendar.myCalendars': 'Minhas agendas',
  'calendar.calendar': 'Agenda',
  'calendar.freeBusyOnly': 'só livre/ocupado',
  'calendar.busy': 'Ocupado',
  'calendar.kind.PERSONAL': 'Pessoal',
  'calendar.kind.TEAM': 'Equipe',
  'calendar.kind.SHARED': 'Compartilhada',
  'calendar.kind.RESOURCE': 'Recurso',

  'calendar.nothing': 'Nada por aqui',
  'calendar.nothingBody': 'Nenhum compromisso neste período.',
  'calendar.titlePlaceholder': 'Ex.: reunião com cliente',
  'calendar.readOnly': 'Você pode ver este compromisso, mas não alterá-lo.',

  'calendar.repeats': 'Repetir',
  'calendar.frequency': 'Frequência',
  'calendar.frequency.DAILY': 'Todos os dias',
  'calendar.frequency.WEEKLY': 'Toda semana',
  'calendar.frequency.MONTHLY': 'Todo mês',
  'calendar.frequency.YEARLY': 'Todo ano',
  'calendar.every': 'A cada',
  'calendar.endsWhen': 'Termina',
  'calendar.ends.NEVER': 'Nunca',
  'calendar.ends.AFTER': 'Depois de',
  'calendar.ends.ON': 'Na data',
  'calendar.occurrences': 'Repetições',
  'calendar.untilDate': 'Até',

  'calendar.seriesScope': 'Aplicar a',
  'calendar.scope.THIS': 'Só este',
  'calendar.scope.FOLLOWING': 'Este e os seguintes',
  'calendar.scope.ALL': 'Toda a série',

  'calendar.conference': 'Link da chamada',
  'calendar.conferencePlaceholder': 'https://…',
  'calendar.visibility': 'Visibilidade',
  'calendar.visibility.DEFAULT': 'Padrão da agenda',
  'calendar.visibility.PUBLIC': 'Pública',
  'calendar.visibility.PRIVATE': 'Particular',
  'calendar.showAsBusy': 'Marcar como ocupado',
  'calendar.showAsBusyHint':
    'Desmarque para o horário continuar livre para quem procura um encaixe.',
  'calendar.guestPermissions': 'Os convidados podem',
  'calendar.guestsCanModify': 'Alterar o compromisso',
  'calendar.guestsCanInvite': 'Convidar outras pessoas',
  'calendar.guestsCanSeeOthers': 'Ver a lista de convidados',
  'calendar.reminders': 'Avisos',
  'calendar.remind.0': 'Na hora',
  'calendar.remind.10': '10 min antes',
  'calendar.remind.30': '30 min antes',
  'calendar.remind.60': '1 h antes',
  'calendar.remind.1440': '1 dia antes',
  'calendar.optional': 'opcional',
  'calendar.inviteColleague': 'Convidar um colega',
  'calendar.yourAnswer': 'Você vai?',

  'calendar.weekday.0': 'dom',
  'calendar.weekday.1': 'seg',
  'calendar.weekday.2': 'ter',
  'calendar.weekday.3': 'qua',
  'calendar.weekday.4': 'qui',
  'calendar.weekday.5': 'sex',
  'calendar.weekday.6': 'sáb',

  'calendar.month.0': 'janeiro',
  'calendar.month.1': 'fevereiro',
  'calendar.month.2': 'março',
  'calendar.month.3': 'abril',
  'calendar.month.4': 'maio',
  'calendar.month.5': 'junho',
  'calendar.month.6': 'julho',
  'calendar.month.7': 'agosto',
  'calendar.month.8': 'setembro',
  'calendar.month.9': 'outubro',
  'calendar.month.10': 'novembro',
  'calendar.month.11': 'dezembro',

  'calendar.color': 'Cor',
  'calendar.colorFromCalendar': 'Usar a cor da agenda',
  'calendar.color.tomato': 'Tomate',
  'calendar.color.tangerine': 'Tangerina',
  'calendar.color.banana': 'Banana',
  'calendar.color.sage': 'Sálvia',
  'calendar.color.basil': 'Manjericão',
  'calendar.color.peacock': 'Pavão',
  'calendar.color.blueberry': 'Mirtilo',
  'calendar.color.lavender': 'Lavanda',
  'calendar.color.grape': 'Uva',
  'calendar.color.flamingo': 'Flamingo',
  'calendar.color.graphite': 'Grafite',

  'calendar.proposed': 'Sugeriu {when}',
  'calendar.acceptProposal': 'Aceitar este horário',

  'calendar.sync': 'Sincronizar com o Google Agenda',
  'calendar.syncDescription':
    'Espelha os compromissos entre a sua agenda do Google e uma agenda daqui. A conexão é sua: ninguém mais na empresa vê ou usa a sua conta.',
  'calendar.syncInto': 'Agenda de destino',
  'calendar.connectGoogle': 'Conectar o Google Agenda',
  'calendar.disconnect': 'Desconectar',
  'calendar.noConnections': 'Nenhuma agenda externa conectada',
  'calendar.noConnectionsBody': 'Conecte a sua agenda do Google para ver tudo em um lugar só.',
  'calendar.lastSynced': 'Última sincronização: {when}',
  'calendar.neverSynced': 'Ainda não sincronizou',
  'calendar.syncPending':
    'A autorização com o Google ainda não está disponível: falta cadastrar o aplicativo. A conexão fica aguardando até lá — nada é sincronizado antes disso.',
  'calendar.provider.google': 'Google Agenda',
  'calendar.syncStatus.PENDING': 'Aguardando autorização',
  'calendar.syncStatus.ACTIVE': 'Sincronizando',
  'calendar.syncStatus.ERROR': 'Com erro',
  'calendar.syncStatus.REVOKED': 'Acesso revogado',
  'calendar.direction.BOTH': 'Nos dois sentidos',
  'calendar.direction.PULL': 'Só trazer de lá',
  'calendar.direction.PUSH': 'Só enviar daqui',
  'calendar.error.calendar.sync.revoked':
    'O Google não aceita mais o nosso acesso. Conecte de novo.',
  'calendar.error.calendar.sync.stale_token': 'A marcação ficou velha; vamos reler tudo.',
  'calendar.error.calendar.sync.failed': 'A última tentativa falhou. Vamos tentar de novo.',
  'calendar.availability': 'Quando você pode ser agendado',
  'calendar.event': 'Compromisso',
  'calendar.eventTitle': 'Título',
  'calendar.starts': 'Começa',
  'calendar.ends': 'Termina',
  'calendar.allDay': 'Dia inteiro',
  'calendar.location': 'Local',
  'calendar.locationPlaceholder': 'Sala, endereço ou link da chamada',
  'calendar.notes': 'Descrição',
  'calendar.deleteEvent': 'Excluir compromisso',
  'calendar.confirmDelete': 'Confirmar exclusão',
  'calendar.attendees': 'Participantes',
  'calendar.noAttendees': 'Ninguém convidado ainda.',
  'calendar.invite': 'Convidar',
  'calendar.invitePlaceholder': 'e-mail do convidado',
  'calendar.status.CONFIRMED': 'Confirmado',
  'calendar.status.TENTATIVE': 'A confirmar',
  'calendar.status.CANCELED': 'Cancelado',
  'calendar.response.NEEDS_ACTION': 'Sem resposta',
  'calendar.response.ACCEPTED': 'Aceito',
  'calendar.response.DECLINED': 'Recusado',
  'calendar.response.TENTATIVE': 'Talvez',
  'calendar.dayOff': 'Indisponível',
  'calendar.bufferBefore': 'Intervalo antes (min)',
  'calendar.bufferAfter': 'Intervalo depois (min)',
  'calendar.minNotice': 'Antecedência mínima (min)',
  'calendar.maxDaysAhead': 'Agendar até (dias)',
  'calendar.saveAvailability': 'Salvar disponibilidade',
  'calendar.blockedDates': 'Datas bloqueadas',
  'calendar.noBlockedDates': 'Nenhuma data bloqueada.',
  'calendar.blockDate': 'Bloquear data',
  'calendar.linkName': 'Nome do link',
  'calendar.linkNamePlaceholder': 'Reunião de diagnóstico',
  'calendar.linkSlug': 'Endereço',
  'calendar.linkDuration': 'Duração',
  'calendar.newLink': 'Criar link',
  'calendar.copyLink': 'Copiar',

  'chat.conversations': 'Conversas internas',
  'chat.thread': 'Conversa',
  'chat.description': 'Onde a equipe conversa sem sair do sistema. Clientes ficam no Atendimento.',
  'chat.rooms': 'Conversas',
  'chat.channels': 'Canais',
  'chat.directs': 'Diretas',
  'chat.newChannel': 'Criar canal',
  'chat.create': 'Criar',
  'chat.newChannelPlaceholder': 'nome-do-canal',
  'chat.today': 'Hoje',
  'chat.yesterday': 'Ontem',
  'chat.someone': 'Alguém',
  'chat.notMember': 'Você está vendo este canal, mas ainda não faz parte dele.',
  'chat.hint': 'Enter envia · Shift+Enter quebra a linha',
  'chat.noMessagesBody': 'As mensagens deste canal aparecem aqui, da mais antiga para a mais nova.',
  'chat.empty': 'Nenhuma conversa ainda',
  'chat.emptyBody': 'Crie um canal para a equipe conversar sem sair do sistema.',
  'chat.pick': 'Escolha uma conversa',
  'chat.pickBody': 'Escolha um canal na lista ao lado para ler e responder.',
  'chat.noMessages': 'Nenhuma mensagem ainda. Comece você.',
  'chat.placeholder': 'Escrever em {room}',
  'chat.join': 'Entrar neste canal',
  'chat.edited': 'editada',
  'chat.live': 'ao vivo',
  'chat.offline': 'reconectando',
  'chat.direct': 'Conversa direta',
  'chat.pinned': 'Fixadas',
  'chat.newDirect': 'Conversar com…',
  'chat.filterPlaceholder': 'Filtrar conversas',
  'chat.pin': 'Fixar no topo',
  'chat.unpin': 'Desafixar',
  'chat.type.CHANNEL': 'Canal',
  'chat.type.DIRECT': 'Conversa direta',

  'crm.entity.LEAD': 'Lead',
  'crm.entity.CONTACT': 'Contato',
  'crm.entity.COMPANY': 'Empresa',
  'crm.entity.OPPORTUNITY': 'Oportunidade',

  'record.fields': 'Dados',
  'record.edit': 'Editar',
  'record.save': 'Salvar alterações',
  'record.saving': 'Salvando…',
  'record.problem.required': 'Obrigatório.',
  'record.problem.email': 'E-mail inválido.',
  'record.problem.url': 'Use o endereço completo, começando com https://',
  'record.problem.number': 'Use apenas números inteiros.',
  'record.customFields': 'Campos personalizados',
  'record.timeline': 'Linha do tempo',
  'record.addNote': 'Registrar',
  'record.notePlaceholder': 'O que aconteceu? Uma ligação, um e-mail, uma observação.',
  'record.noActivity': 'Nada registrado ainda',
  'record.backToList': 'Voltar para a lista',

  'record.field.name': 'Nome',
  'record.field.title': 'Título',
  'record.field.email': 'E-mail',
  'record.field.first_name': 'Nome',
  'record.field.last_name': 'Sobrenome',
  'record.field.phone': 'Telefone',
  'record.field.mobile': 'Celular',
  'record.field.company_name': 'Empresa',
  'record.field.job_title': 'Cargo',
  'record.field.status': 'Situação',
  'record.field.score': 'Score',
  'record.field.source': 'Origem',
  'record.field.website': 'Site',
  'record.field.industry': 'Setor',
  'record.field.employee_count': 'Funcionários',
  'record.field.document': 'CNPJ/CPF',
  'record.field.person_type': 'Tipo de pessoa',
  'record.field.amount_cents': 'Valor',
  'record.field.currency': 'Moeda',
  'record.field.probability': 'Probabilidade',
  'record.field.expected_close_date': 'Fechamento previsto',
  'record.field.pipeline_id': 'Funil',
  'record.field.stage_id': 'Etapa',
  'record.field.company_id': 'Empresa',
  'record.field.contact_id': 'Contato',
  'record.field.referrer': 'Referência',
  'record.field.landing_page': 'Página de entrada',
  'record.field.unqualified_reason': 'Motivo da desqualificação',
  'record.field.converted_at': 'Convertido em',
  'record.field.created_at': 'Criado em',
  'record.field.updated_at': 'Atualizado em',
  'record.field.notes': 'Observações',
  'record.field.address': 'Endereço',
  'record.field.city': 'Cidade',
  'record.field.state': 'Estado',
  'record.field.country': 'País',
  'record.field.postal_code': 'CEP',

  'activity.CREATED': 'Criado',
  'activity.UPDATED': 'Atualizado',
  'activity.NOTE': 'Nota',
  'activity.CALL': 'Ligação',
  'activity.EMAIL': 'E-mail',
  'activity.MEETING': 'Reunião',
  'activity.TASK': 'Tarefa',
  'activity.STATUS_CHANGED': 'Situação alterada',
  'activity.STAGE_CHANGED': 'Etapa alterada',
  'activity.CONVERTED': 'Convertido',
  'activity.WON': 'Ganha',
  'activity.LOST': 'Perdida',
  'activity.MESSAGE': 'Mensagem',

  'search.resultsFor': 'Resultados para “{query}”',
  'search.typeSomething': 'Digite algo para buscar',
  'search.typeSomethingBody': 'A busca procura em leads, contatos, empresas e oportunidades.',
  'search.nothing': 'Nada encontrado',
  'search.nothingBody': 'Tente um trecho menor do nome.',
  'search.inConversations': 'Nas conversas com clientes',
  'search.inChat': 'No chat interno',

  'crm.companies': 'Empresas',
  'crm.companiesDescription': 'Quem compra, e o que já sabemos sobre cada uma.',
  'crm.pipeline': 'Funil de vendas',
  'crm.loading': 'Carregando...',
  'crm.empty': 'Nenhum registro encontrado.',
  'crm.results': '{count} resultado(s)',
  'crm.lastDays': '{n} dias',
  'crm.newLead': 'Novo lead',
  'crm.lifecycle': 'Ciclo de vida',
  'interaction.prompt': 'O que aconteceu?',
  'interaction.NOTE': 'Anotação',
  'interaction.CALL': 'Ligação',
  'interaction.WHATSAPP': 'WhatsApp',
  'interaction.EMAIL': 'E-mail',
  'interaction.SMS': 'SMS',
  'interaction.VISIT': 'Visita',
  'interaction.body.NOTE': 'O que registrar',
  'interaction.body.CALL': 'Como foi a ligação',
  'interaction.body.WHATSAPP': 'O que foi tratado',
  'interaction.body.EMAIL': 'Resumo do e-mail',
  'interaction.body.SMS': 'O que foi enviado',
  'interaction.body.VISIT': 'Como foi a visita',
  'interaction.placeholder.NOTE': 'Quanto pode pagar, o que procura, o que o gerente observou…',
  'interaction.placeholder.CALL': 'O que foi conversado, ou por que não deu certo',
  'interaction.placeholder.WHATSAPP': 'Cole a conversa ou resuma o que ficou combinado',
  'interaction.placeholder.EMAIL': 'Assunto e o que foi respondido',
  'interaction.placeholder.SMS': 'Texto enviado',
  'interaction.placeholder.VISIT': 'O que foi mostrado, o que despertou interesse, objeções',
  'interaction.outcome': 'Situação da ligação',
  'interaction.outcome.ANSWERED': 'Atendeu',
  'interaction.outcome.NO_ANSWER': 'Não atendeu',
  'interaction.outcome.VOICEMAIL': 'Caixa postal',
  'interaction.outcome.BUSY': 'Ocupado',
  'interaction.outcome.CALLBACK_REQUESTED': 'Pediu para ligar depois',
  'interaction.outcome.WRONG_NUMBER': 'Número errado',
  'interaction.scheduleNext': 'Agendar o próximo passo',
  'interaction.scheduleNextHint': 'Vira uma tarefa ligada a este lead, no nome do responsável.',
  'interaction.followUpTitle': 'O que fazer',
  'interaction.when': 'Quando',
  'interaction.in.0': 'Hoje',
  'interaction.in.1': 'Amanhã',
  'interaction.in.2': 'Em 2 dias',
  'interaction.in.3': 'Em 3 dias',
  'interaction.in.7': 'Em 1 semana',
  'interaction.in.15': 'Em 15 dias',
  'interaction.in.30': 'Em 30 dias',
  'interaction.save': 'Registrar',
  'interaction.followUp.NOTE': 'Revisar esta anotação',
  'interaction.followUp.CALL': 'Ligar de novo',
  'interaction.followUp.WHATSAPP': 'Retomar a conversa',
  'interaction.followUp.EMAIL': 'Cobrar resposta do e-mail',
  'interaction.followUp.SMS': 'Confirmar se recebeu',
  'interaction.followUp.VISIT': 'Dar retorno após a visita',

  'lead.knownScore': 'Cadastro completo',
  'lead.missing': 'Falta',
  'lead.field.name': 'nome',
  'lead.field.email': 'e-mail',
  'lead.field.phone': 'telefone',
  'lead.field.company_name': 'empresa',
  'lead.openWhatsapp': 'Abrir no WhatsApp',
  'lead.nextStep': 'Próximo passo',
  'lead.noNextStep': 'Ninguém definiu o próximo passo.',
  'lead.interactions': 'interações',
  'lead.unanswered': 'sem resposta',
  'lead.tryAnotherChannel': 'Já foram várias tentativas sem retorno. Vale tentar outro canal.',
  'lead.lastContact': 'Último contato',
  'lead.dealValue': 'Valor do negócio',
  'lead.probability': 'Possibilidade de venda',
  'lead.probabilityHint': '1 é pouca chance, 5 é chance grande de fechar.',
  'lead.moment': 'Momento do lead',
  'lead.chooseMoment': 'Escolher o momento…',
  'nav.import': 'Importar',
  'import.title': 'Importar planilha',
  'import.description': 'Traga seus contatos de onde eles estiverem, em um arquivo CSV.',
  'import.step.file': 'Arquivo',
  'import.step.map': 'Colunas',
  'import.step.running': 'Importação',
  'import.chooseFile': 'Escolher arquivo',
  'import.reading': 'Lendo o arquivo…',
  'import.what': 'O que tem nessa planilha',
  'import.entity.LEAD': 'Leads',
  'import.entity.COMPANY': 'Empresas',
  'import.entity.CONTACT': 'Contatos',
  'import.fileHint':
    'CSV separado por vírgula ou ponto e vírgula, com a primeira linha sendo os títulos das colunas.',
  'import.mapColumns': 'O que é cada coluna',
  'import.rowsFound': '{n} linha(s)',
  'import.emptyColumn': 'coluna vazia',
  'import.ignore': 'Ignorar esta coluna',
  'import.duplicates': 'Quando já existir',
  'import.duplicates.SKIP': 'Pular',
  'import.duplicates.SKIP.hint': 'Mantém o que já está no CRM e ignora a linha.',
  'import.duplicates.UPDATE': 'Atualizar',
  'import.duplicates.UPDATE.hint':
    'Preenche o que a planilha trouxer. Campo em branco não apaga o que já existe.',
  'import.duplicates.CREATE': 'Criar mesmo assim',
  'import.duplicates.CREATE.hint': 'Cria um segundo registro. Use só se souber o que está fazendo.',
  'import.run': 'Importar {n} linha(s)',
  'import.mapSomething': 'Escolha ao menos uma coluna.',
  'import.progress': 'Importando',
  'import.imported': 'Criados',
  'import.updated': 'Atualizados',
  'import.skipped': 'Pulados',
  'import.failed': 'Com erro',
  'import.whatFailed': 'O que não entrou',
  'import.line': 'Linha',
  'import.another': 'Importar outra planilha',
  'import.history': 'Importações anteriores',
  'import.noHistory': 'Nenhuma importação ainda.',
  'import.summary':
    '{imported} criados · {updated} atualizados · {skipped} pulados · {failed} com erro',
  'import.status.PENDING': 'Na fila',
  'import.status.RUNNING': 'Importando',
  'import.status.DONE': 'Concluída',
  'import.status.FAILED': 'Falhou',
  'import.status.CANCELED': 'Cancelada',
  'record.related': 'Relacionados',
  'record.company': 'Empresa',
  'record.contacts': 'Contatos',
  'record.deals': 'Negócios',
  'record.tasks': 'Tarefas',
  'record.files': 'Arquivos',
  'record.open': 'Abrir',
  'record.noDeals': 'Nenhum negócio.',
  'record.noContacts': 'Nenhum contato.',
  'record.noTasks': 'Nenhuma tarefa.',
  'record.noFiles': 'Nenhum arquivo.',
  'record.tab.all': 'Tudo',
  'record.tab.notes': 'Notas',
  'record.tab.emails': 'E-mails',
  'record.tab.calls': 'Contatos',
  'record.tab.meetings': 'Reuniões',
  'record.tab.tasks': 'Tarefas',
  'record.tab.system': 'Sistema',
  'record.placeholder.NOTE': 'O que você quer registrar?',
  'record.placeholder.CALL': 'O que foi conversado na ligação?',
  'record.placeholder.MEETING': 'O que ficou decidido na reunião?',
  'record.placeholder.EMAIL': 'Resumo do e-mail trocado',
  'record.placeholder.WHATSAPP': 'O que foi tratado no WhatsApp?',
  'record.placeholder.TASK': 'O que precisa ser feito?',
  'crm.stage.LEAD': 'Lead',
  'crm.stage.MQL': 'Qualificado (mkt)',
  'crm.stage.SQL': 'Qualificado (vendas)',
  'crm.stage.OPPORTUNITY': 'Oportunidade',
  'crm.stage.CUSTOMER': 'Cliente',
  'crm.stage.EVANGELIST': 'Promotor',
  'crm.stage.OTHER': 'Fora do funil',
  'crm.bant.title': 'Qualificação (BANT)',
  'crm.bant.answered': '{n} de {total} respondidas',
  'crm.bant.qualified': 'Qualificado',
  'crm.bant.blockedBy': 'Trava',
  'crm.bant.budget': 'Orçamento',
  'crm.bant.budget.hint': 'Existe verba para resolver isso?',
  'crm.bant.authority': 'Autoridade',
  'crm.bant.authority.hint': 'Quem decide está na conversa?',
  'crm.bant.need': 'Necessidade',
  'crm.bant.need.hint': 'O problema é real e reconhecido?',
  'crm.bant.timing': 'Prazo',
  'crm.bant.timing.hint': 'Existe uma data para resolver?',
  'crm.bant.answer.UNKNOWN': 'Não sei',
  'crm.bant.answer.NO': 'Não',
  'crm.bant.answer.PARTIAL': 'Em parte',
  'crm.bant.answer.YES': 'Sim',
  'crm.newLeadHint': 'Só o nome é obrigatório. O resto entra quando você souber.',
  'crm.newLeadNamePlaceholder': 'Nome de quem entrou em contato',
  'crm.saveLead': 'Criar lead',
  'crm.newContact': 'Novo contato',
  'crm.newContactHint': 'Um nome basta. O resto pode vir depois, na ficha.',
  'crm.saveContact': 'Criar contato',
  'crm.newCompany': 'Nova empresa',
  'crm.newCompanyHint': 'Um nome basta. O resto pode vir depois, na ficha.',
  'crm.saveCompany': 'Criar empresa',
  'crm.field.firstName': 'Nome',
  'crm.field.lastName': 'Sobrenome',
  'crm.field.website': 'Site',
  'crm.noCompany': '— sem empresa —',
  'crm.defaultStatus': '— status padrão —',
  'crm.noSource': 'Sem origem',
  'crm.field.company': 'Empresa',
  'crm.field.description': 'Descrição',
  'crm.field.mobile': 'Celular',
  'crm.field.jobTitle': 'Cargo',
  'crm.field.inStageSince': 'Na etapa desde',
  'crm.field.closedAt': 'Fechado em',
  'crm.owner': 'Responsável',
  'crm.unassigned': 'Sem responsável',
  'crm.lastActivity': 'Última interação',
  'crm.noActivity': 'Sem contato',
  'crm.quick.all': 'Todos',
  'crm.quick.mine': 'Meus leads',
  'crm.quick.new': 'Sem contato',
  'crm.quick.qualified': 'Qualificados',
  'crm.quick.converted': 'Convertidos',
  'crm.kpi.untouched': 'Esperando resposta',
  'crm.kpi.untouchedStale': '{n} há mais de um dia',
  'crm.kpi.untouchedClear': 'Ninguém esperando há mais de um dia',
  'crm.kpi.responseTime': 'Tempo até o 1º contato',
  'crm.kpi.responseAverage': 'média {value}',
  'crm.kpi.responseNone': 'Nenhum lead contatado no período',
  'crm.kpi.arrived': 'Leads no período',
  'crm.kpi.inPeriod': 'últimos {n} dias',
  'crm.kpi.qualification': 'Taxa de qualificação',
  'crm.kpi.qualificationHint': 'Quanto do que chega vale trabalhar',
  'crm.kpi.conversion': 'Conversão',
  'crm.kpi.conversionHint': 'Leads que viraram cliente',
  'crm.kpi.close': 'Fechamento',
  'crm.kpi.closeHint': 'Dos qualificados, quantos fecharam',
  'crm.kpi.funnel': 'Funil de leads',
  'crm.kpi.sources': 'Origens',
  'crm.kpi.customers': 'Clientes',
  'crm.kpi.noSources': 'Nenhum lead no período.',
  // Qualifying a contact, and what it produced (Phase 147).
  'crm.kpi.lossReasons': 'Por que não fecharam',
  'crm.kpi.lossReasonsHint': 'Motivos registrados ao desqualificar, no período escolhido.',
  'crm.kpi.noLossReasons': 'Nenhum lead desqualificado no período.',
  'crm.kpi.reason': 'Motivo',
  'crm.kpi.times': 'Vezes',
  'crm.kpi.noReasonGiven': 'sem motivo registrado',
  'crm.kpi.durations':
    'Mediana: {qualify} dias até qualificar, {convert} dias até converter · {fromContacts} vieram de contatos que você já tinha.',
  'crm.qualify': 'Qualificar como lead',
  'crm.qualifyAction': 'Criar o lead',
  'crm.qualifyHint':
    'Cria uma tentativa de venda sobre {name}. A pessoa continua sendo o mesmo contato — o que nasce é a negociação, com status, datas e motivo próprios.',
  'crm.qualifyTitle': 'Do que se trata',
  'crm.qualifySource': 'Origem',
  'crm.qualifyNoSource': 'Sem origem definida',
  'crm.qualifyNoSources': 'Nenhuma origem cadastrada.',
  'crm.qualifyConfigure': 'Cadastrar origens',
  'crm.qualifyValue': 'Valor estimado (R$)',
  'crm.qualifyValueHint': 'Opcional. Serve para priorizar e para medir o que está em jogo.',
  'crm.leadAlreadyOpen':
    'Já existe um lead aberto para este contato. Dois ao mesmo tempo seriam duas pessoas trabalhando a mesma venda sem saber.',
  'crm.openExistingLead': 'Abrir o lead existente',
  'crm.kpi.unknownSource': 'Não informada',
  'crm.kpi.staleBanner': '{n} lead(s) aguardam resposta há mais de 24 horas.',
  'crm.kpi.seePending': 'Ver pendências',
  'crm.kpi.inPeriodCount': '{n} lead(s) no período',

  'crm.period': 'Período',
  'crm.import': 'Importar',
  'crm.export': 'Exportar',
  'crm.exporting': 'Exportando…',
  'crm.exportTruncated':
    'O arquivo traz os primeiros {n} registros. Para levar tudo, use uma exportação em segundo plano.',
  'crm.leadList': 'Lista de leads',
  'crm.showing': 'Exibindo {n} de {total} leads',
  'crm.kpi.sourcesHint': 'Ordenado por clientes gerados, não por volume.',
  'crm.field.name': 'Nome',
  'crm.field.industry': 'Setor',
  'crm.field.employees': 'Funcionários',
  'crm.field.createdAt': 'Criado em',

  'filters.combinator': 'Combinar com',
  'filters.and': 'E (todas as condições)',
  'filters.or': 'OU (qualquer condição)',
  'filters.field': 'Campo',
  'filters.operator': 'Operador',
  'filters.value': 'Valor',
  'filters.add': 'Adicionar condição',
  'filters.addField': 'Campo a adicionar',
  'filters.remove': 'Remover',
  'filters.title': 'Filtros',
  'filters.active': 'Filtros ({n})',
  'filters.apply': 'Aplicar filtros',
  'filters.clear': 'Limpar',
};

const dictionaries: Record<Locale, Dictionary> = {
  'pt-BR': ptBR,
  // Translations are added as the product expands beyond Brazil.
  'en-US': {},
  es: {},
};

let currentLocale: Locale = 'pt-BR';

export function setLocale(locale: Locale): void {
  currentLocale = locale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function t(key: keyof typeof ptBR | string): string {
  return dictionaries[currentLocale][key] ?? dictionaries['pt-BR'][key] ?? key;
}
